<template>
    <website-page class="cms-page-map"
        :class="{ 'assistant-visible': navPath, 'nav-mode': navMode && !navPath, 'navigation-active': navigationActive,'navigation-active-split-view': splitViewActive && navigationActive }"
        container-class="h-100" page-class="h-100" v-style="cmsWebsite.body.page.style">
        <preview-responsive-container>
            <cms-block-builder :blocks="pageBlocks" no-footer>
                <map-container ref="mapContainer" v-if="map && floorSelected && !disableMap" :map="map" :floor="floorSelected"
                    :camera="camera" @reset-camera="resetCameraAndClearPreview"
                    @background-click="onMapBackgroundClick" @dragging="onMapDragging"
                    :show-controls="!isMobileLayoutOrPreview" :show-scale-legend="layers.legend"
                    :show-floor-name="!isMobileLayoutOrPreview && !(wheelchairExtended && isWheelchairMode)">

                    <!-- Debug -->
                    <template v-if="isDebugVisible">
                        <layer-map-debug-item-links v-if="debugSettings.links && !navPath"
                            :floor-links="floorLinks" />
                        <layer-map-debug-pathfinder v-if="debugSettings.pathfinder"
                            :pathfinder="pathfinder" />
                    </template>

                    <layer-map-nav-path v-if="navPath && navMode" :key="floorSelected.id" :nav-path="navPath"
                        :floor="floorSelected" :user-link="markerCurrentLink" :instruction="currentInstruction" :user-marker-position="gpsLocationMarkerItem" :offroute-connector="offRouteConnector" />
                    <layer-map-items @item-selected="selectMapItem" :item-selected="mapItemSelected"
                        :floor="floorSelected" :floor-items="floorItems" :nav-path="navPath" :layers="layers" />

                    <map-location-marker v-if="navPath && currentLocation" variant="primary" :item="currentLocation"
                        :floor="floorSelected" :label="$t('map.youAreHere')" />
                    <map-location-marker v-else-if="startPoint && !currentInstruction" variant="outer-primary"
                        :item="startPoint" :floor="floorSelected" outer-radius />
                    <map-location-marker v-if="endPoint && (!currentInstruction || isKioskDevice)" variant="primary" :item="endPoint"
                        :floor="floorSelected" outer-radius :label="$t('map.destination')" />
                    <map-location-marker v-if="currentLocation" variant="primary" :item="currentLocation"
                        :floor="floorSelected" outer-radius :label="$t('map.youAreHere')" />

                    <!-- Green Arrow for Kiosk -->
                    <map-location-marker
                        v-if="!shouldShowGpsMarker && !navPath && freeNavigation && currentLocationOnPlan && hasPlanCoordinates(currentLocationOnPlan)"
                        :angle="compassHeading" :accuracy="compassAccuracy" shape="arrow" :item="currentLocationMarker"
                        :floor="floorSelected" outer-radius :label="$t('map.yourPosition')" :live-compass="effectiveCompass" />

                    <template v-else-if="shouldShowLiveGpsMarkerGroup">
                        <!-- Live position marker: shows the GPS or pedometer position when a live marker can be rendered on the current floor. -->
                        <map-location-marker
                            v-if="gpsLocationMarkerItem"
                            :variant="displayedGpsMarkerVariant"
                            :angle="navigationMarkerAngle" :accuracy="compassAccuracy" :gps-accuracy="gpsAccuracyDisplay" :shape="displayedGpsMarkerShape"
                            :item="gpsLocationMarkerItem" :no-arrow="!isCurrentLocationOnSite || isNavigationStatic || !navSettings.compass" :floor="floorSelected" :live-compass="effectiveCompass" />

                        <!-- Gray ghost marker: shows the snapped path fallback while the live position is previewed or still outside the active route. -->
                        <map-location-marker v-if="shouldShowGrayPathFallbackMarker" variant="gray" shape="binoculars-tooltip" :item="currentLocationOnPath" :floor="floorSelected"/>
                    </template>

                    <!-- Primary path fallback marker: shows the route position when live GPS rendering is unavailable but a valid path position still exists. -->
                    <map-location-marker
                        v-else-if="shouldShowPrimaryPathFallbackMarker" variant="primary"
                        :angle="navigationMarkerAngle"
                        shape="person" :item="currentLocationOnPath" :floor="floorSelected" :no-arrow="!(isiOSDevice || isAndroidDevice) && (!isCurrentLocationOnSite || isNavigationStatic)" :live-compass="effectiveCompass"/>

                    <!-- Static primary fallback marker: shows the best known navigation position in static mode when no live GPS marker should be displayed. -->
                    <map-location-marker
                        v-else-if="shouldShowStaticPrimaryFallbackMarker"
                        variant="primary" shape="person"
                        :angle="compassHeading" :accuracy="compassAccuracy"
                        :item="staticPrimaryFallbackMarkerItem" :floor="floorSelected"
                        :no-arrow="!shouldEnableStaticPrimaryFallbackMarkerCompass"
                        :live-compass="shouldEnableStaticPrimaryFallbackMarkerCompass ? effectiveCompass : null" />

                    <!-- Navigation fallback marker: shows the on-plan navigation position during an active instruction when every marker branch above is unavailable. -->
                    <map-location-marker
                        v-else-if="navPath && currentLocationOnPlan && currentInstruction"
                        :angle="compassHeading" :accuracy="compassAccuracy" :variant="gpsSignalMarkerVariant" shape="person"  :no-arrow="isNavigationStatic"
                        :item="currentLocationOnPlan" :floor="floorSelected" :live-compass="effectiveCompass" />

                    <template v-if="!mapMobileAssistantDrawerOpened" #controls>
                        <map-floor-selector :map="map" :show-altitude-calibration="currentGPSFloor !== null" :current-gps-floor="currentGPSFloor" :floor-selected="floorSelected" @selected="selectFloor($event);setAutomaticFloorChange();" @calibrate-altitude="showAltitudeCalibrationModal"
                            :nav-path="navPath" :split-view-active="splitViewActive" @show-dev-menu="debugMenuVisibility = true" />
                    </template>
                </map-container>

                <map-mobile-search v-if="!navMode && !navPath && (!mapItemSelected || !isDesktopDevice) && !isKioskDevice"
                    :visible.sync="searchVisible" :map-item-selected="mapItemSelected" :latest-start-point="latestStartPointOption"
                    :latest-destination-point="latestDestinationPointOption" :start-from-current-location="startFromCurrentLocation" :start-from-qr-code="startFromQRCode" :start-from-current-location-loading="startFromCurrentLocationLoading"
                    :start-from-current-location-disabled="startFromCurrentLocationDisabled"
                    @endpoint-selected="onSearchEndpointSelected" @open-qr-scanner="openQrScanner" />

                <map-mobile-trajectory-overlay ref="mapMobileTrajectoryOverlayRef" v-if="!isKioskDevice && navMode && !navigationActive" :map="map"
                                               :current-location="currentLocation" :gpsCurrentLocation="currentLocationOnPlan" :start-point="startPoint" :end-point="endPoint"
                                               :nav-path="navPath" :latest-start-point="latestStartPointOption"
                                               :latest-destination-point="latestDestinationPointOption" :start-from-current-location="startFromCurrentLocation" :start-from-qr-code="startFromQRCode" :start-from-current-location-loading="startFromCurrentLocationLoading"
                                               :start-from-current-location-disabled="startFromCurrentLocationDisabled"
                                               @endpoint-selected="onTrajectoryEndpointSelected" @item-selected="selectMapItem" @swap-endpoints="swapNavigationEndpoints"
                                               @select-from-map="onTrajectorySelectFromMap"
                                               @nav-preferences-changed="navigationPreferenceChanged" @start-navigation="startMobileNavigation"
                                               @close="exitNavModeToLocation" @share-link="shareLink"
                                               @reset-camera="resetCameraAndClearPreview" @open-qr-scanner="openQrScanner('trajectory')" />

                <!-- <template v-if="isDesktopDevice || isKioskDevice">
                    <map-sensors-status @click="switchGpsSignal" v-if="navMode && !isNavigationStatic && sensorsStatus && mapIntegrations.isGpsEnabled() && isCurrentLocationOnSite" :accuracy="sensorsStatus" />
                    <map-sensors-status v-else-if="navigationActive && isNavigationStatic && mapIntegrations.isGpsEnabled()" variant="danger"
                        icon="fas fa-location-arrow-slash" clickable :label="$t('map.staticMode.warning')" @click="showPermissionModalRequest" />
                    <map-sensors-status v-else-if="navigationActive && mapIntegrations.isGpsEnabled() && !isCurrentLocationOnSite" variant="warning" icon="fas fa-location-dot-slash" :label="$t('map.notOnSite')"/>
                </template> -->

                <map-mobile-location-drawer v-if="mapItemSelected && !navMode && !isKioskDevice"
                    :map-item="mapItemSelected" :cms-item="mapItemSelected.cmsItem" :distance="selectedItemDistance"
                    :duration="selectedItemDuration" @directions="switchToNavMode('go-to')"
                    @start-from="switchToNavMode('start-from')" @close="clearSelectedItem"
                    @reset-camera="resetCameraAndClearPreview" @item-selected="onMobileSuggestionSelected" />

                <map-mobile-suggestions v-if="!isKioskDevice && !navMode && !mapItemSelected"
                    :active-tab="mobileSuggestionTab" @tab-changed="mobileSuggestionTab = $event"
                    @endpoint-selected="onMobileSuggestionSelected" @reset-camera="resetCameraAndClearPreview" />

                <map-mobile-navigation-assistant ref="mapMobileAssistant"
                    v-if="!isKioskDevice && navPath && navMode && navigationActive" :nav-path="navPath" :floor-toast-visible="floorToastVisible"
                    :analytics-route="analyticsRoute" :compass-heading="compassHeading" :reroute-state="rerouteState && gpsNavMode && !offRouteConnector" :show-recenter-label="isRecenterVisible"
                    :gps-location-active="shouldShowGpsMarker" :sensor-status-toast="mobileSensorStatusToast"
                    @set-instruction="selectInstruction" @sensor-status-toast-click="onSensorStatusToastClick" :floor-number="floorSelected?.level ?? null" :arrival-modal-visible="arrivalModalVisible"
                    @end-navigation="endNavigation" @reset-navigation="resetNavigation"
                    @back-to-trajectory="backToTrajectory" @ar-mode="disableMap = true" @map-mode="onMapModeActivated"
                    @split-mode="onSplitModeActivated" @reset-camera="resetCameraAndClearPreview"
                    @open-qr-scanner="openQrScanner('navigation')" @share-link="shareLink" @simulate-step="simulateStep()"
                    @reroute="rerouteFromGpsMarker" @drawer-opened-change="mapMobileAssistantDrawerOpened = $event">
                </map-mobile-navigation-assistant>

                <map-mobile-qr-scanner
                    v-if="isQrCodeEnabled"
                    :value="qrScanner.visible"
                    @input="handleQrScannerInput"
                    @decode="onQrScannerDecode" />

                <map-mobile-qr-location-modal
                    v-if="qrScanResult.mapItem"
                    :value="qrScanResult.visible"
                    :map-item="qrScanResult.mapItem"
                    mode="reposition"
                    @input="handleQrScanResultInput"
                    @confirm="confirmQrScanAction" />

                <e-mobile-modal
                    :value="scannedRouteModal.visible"
                    :title="$t('map.assistant.qrCodeScanner.startRouteTitle')"
                    :description="$t('map.assistant.qrCodeScanner.startRouteDescription')"
                    position="center" :close-on-backdrop="false" hide-close
                    @input="handleScannedRouteModalInput">

                    <map-mobile-trajectory-header
                        v-if="scannedRouteModal.target" class="scanned-route-preview"
                        :start-value="scannedRouteModal.target.startItem.getFullName()"
                        :end-value="scannedRouteModal.target.endItem.getFullName()"
                        embedded :interactive="false" />

                    <template #footer>
                        <b-btn variant="light" size="lg" block @click="handleScannedRouteModalInput(false)">
                            <span>{{ $t('map.common.cancel') }}</span>
                        </b-btn>
                        <b-btn variant="accent" size="lg" block @click="confirmScannedRouteNavigation">
                            <span>{{ $t('map.start') }}</span>
                        </b-btn>
                    </template>
                </e-mobile-modal>

                <div class="map-copyright" v-if="isDesktopDevice">
                    ©{{ new Date().getFullYear() }} Eye-In Média <a class="text-underline"
                        href="https://eye-in.com/interactive-wayfinding" target="_blank">https://eye-in.com</a>
                </div>

                <map-assistant ref="mapAssistant" v-if="navPath && navMode && isKioskDevice" :nav-path="navPath"
                    :analytics-route="analyticsRoute" :wheelchair-extended.sync="wheelchairExtended" :base-sharing-url="baseSharingUrl"
                    :hideInstruction="hideInstruction" :class="{ 'wheelchair-extended': wheelchairExtended }"
                    @set-instruction="selectInstruction" @close="leaveNavPath"
                    @close-navigation="closeNavigation" @start-navigation="startNavigation" @focus-path="resetCameraAndClearPreview"
                    @end-navigation="endNavigation" @reset-navigation="resetNavigation" @share="shareLink" />

                <div class="map-overlay" v-if="isWheelchairMode && wheelchairExtended"
                    @click="wheelchairExtended = false" />

                <div class="directory" v-if="isKioskDevice && !navPath"
                    :class="{ 'wheelchair-extended': wheelchairExtended }">
                    <div class="divider py-2 text-white" :class="isWheelchairMode ? 'bg-primary' : 'bg-dark'"
                        v-touch:swipe="directorySwiped">
                        <b-btn class="float-right text-dark mr-3 mt-1" variant="white" v-if="isWheelchairMode"
                            @click="wheelchairExtended = !wheelchairExtended">
                            <template v-if="!wheelchairExtended">
                                <span>{{ $t('map.show') }}</span>
                                <i class="fa-chevron-up ml-1" :class="fontAwesomePrefix" />
                            </template>
                            <template v-else>
                                <span>{{ $t('map.hide') }}</span>
                                <i class="fa-chevron-down ml-1" :class="fontAwesomePrefix" />
                            </template>
                        </b-btn>
                        <h2>{{ $t('map.directory') }}</h2>
                    </div>
                    <template v-if="(!isWheelchairMode || wheelchairExtended)">
                        <div class="this-floor">
                            <div class="floor-items">
                                <div class="floor-item" v-for="item in floorCmsItems" :key="`directory-${item.id}`"
                                    @click="selectMapItem(item.mapItem)">
                                    <i class="mr-1"
                                        :class="[fontAwesomePrefix, item.glyph || item.category?.icon || item.category?.glyph || 'fa-location-dot']" />
                                    {{ item.getName() | tr }}
                                </div>
                            </div>
                        </div>
                        <div class="services-container">
                            <h2 class="divider py-2 bg-dark text-white">{{ $t('map.services') }}</h2>
                            <div class="services my-4">
                                <div class="service" @click="selectMapItemById(`jqh03i`)">
                                    <div class="icon-container">
                                        <i class="fas fa-mug-hot fa-2x" />
                                    </div>
                                    <div class="name h4 f-700">{{ $t('map.coffeeShop') }}</div>
                                </div>
                                <div class="service" @click="selectMapItemById(`mup5qc`)">
                                    <div class="icon-container">
                                        <i class="fas fa-utensils fa-2x" />
                                    </div>
                                    <div class="name h4 f-700">{{ $t('map.restaurant') }}</div>
                                </div>
                                <div class="service" @click="selectService(`atm`)">
                                    <div class="icon-container">
                                        <b-img
                                            src="https://eyeindevstorage.blob.core.windows.net/eye-intelligence/cms/library/hopitallakeshore/66d8bb8dcbfc57ed744a00d0/file.webp"
                                            style="filter: invert(1)" />
                                    </div>
                                    <div class="name h4 f-700">{{ $t('map.atm') }}</div>
                                </div>
                                <div class="service" @click="selectService(`parking-machine`)">
                                    <div class="icon-container">
                                        <b-img
                                            src="https://eyeindevstorage.blob.core.windows.net/eye-intelligence/cms/library/hopitallakeshore/66a444585ec9314775a0e338/file.svg" />
                                    </div>
                                    <div class="name h4 f-700" style="font-size: 13pt">{{ $t('map.parking') }}</div>
                                </div>
                                <div class="service" @click="selectService(`toilet`)">
                                    <div class="icon-container">
                                        <i class="fas fa-restroom-simple fa-2x" />
                                    </div>
                                    <div class="name h4 f-700">{{ $t('map.toilet') }}</div>
                                </div>
                            </div>
                            <template v-if="!isWheelchairMode">
                                <img v-if="mapBlock?.bannerImage.src" :src="mapBlock.bannerImage.src" width="100%" :height="mapBlock.bannerImage.maxHeight || 257"/>
                                <img v-else :src="require(`@/assets/img/ad-banner.jpg`)" width="100%" height="257"/>
                            </template>
                        </div>
                    </template>
                </div>
            </cms-block-builder>
        </preview-responsive-container>

        <b-modal v-if="floorModal.visible" v-model="floorModal.visible" size="lg" modal-class="change-floor-overlay"
            body-class="text-center" hide-footer hide-header>
            <div class="change-floor-content">
                <h3 class="mt-4 text-uppercase">
                    {{ $t('map.floorChange') }}
                </h3>
                <div style="position: relative;">
                    <b-img class="elevator-image" @click="floorModal.visible = false"
                        :src="require(`@/assets/icons/elevator-preview.webp`)" />
                    <div class="floor-direction large" :class="`direction-${floorModal.direction}`">
                        <i class="fas" :class="floorModal.direction === 'up' ? 'fa-up-long' : 'fa-down-long'" />
                    </div>
                </div>
                <h2 class="title mb-3">
                    <template v-if="floorModal.direction === 'up'">{{ $t('map.goUpTo', { floor: floorModal.floorName }) }}</template>
                    <template v-else-if="floorModal.direction === 'down'">{{ $t('map.goDownTo', { floor: floorModal.floorName }) }}</template>
                </h2>
            </div>
        </b-modal>

        <e-mobile-modal v-model="useGPSShowcaseModalVisible" :close-on-backdrop="false" :close-on-escape="false"
                        :hide-close="true" header-no-padding>
            <template #header>
                <div class="showcase-animation">
                    <video autoplay loop muted playsinline preload="auto"
                           :poster="require('@/assets/icons/wayfinding/blueDotAnimation.webp')"
                           :src="require('@/assets/icons/wayfinding/blueDotAnimation.mp4')" />
                </div>
            </template>

            <div class="showcase-content">
                <div class="showcase-body">
                    <div class="showcase-title">
                        {{ $t('map.requestToTurnNavigationMode.title') }}
                    </div>
                    <div class="showcase-description">
                        {{ $t('map.requestToTurnNavigationMode.intro') }}
                    </div>
                </div>
            </div>

            <template #footer>
                <b-btn size="lg" variant="accent" class="rounded-xl" block
                       @click="turnToGPSMode">
                    <i class="fal fa-location-dot mr-1 font-weight-bold" />
                    {{ $t('map.requestToTurnNavigationMode.yesActivateNavigation') }}
                </b-btn>
                <b-btn size="lg" variant="light" class="rounded-xl text-muted mt-0 font-weight-normal" block
                       @click="turnToStepByStepMode">
                    {{ $t('map.requestToTurnNavigationMode.continueWithoutNavigation') }}
                </b-btn>
            </template>
        </e-mobile-modal>

        <e-mobile-modal v-if="map" v-model="altitudeCalibrationVisibility" :close-on-backdrop="false" :close-on-escape="false"
                        :hide-close="true" header-no-padding>

            <div class="showcase-content">
                <div class="showcase-body" style="align-items: stretch">
                    <div class="showcase-title">
                        {{ $t('map.altitudeCalibration.title') }}
                    </div>
                    <div class="showcase-description">
                        {{ $t('map.altitudeCalibration.description') }}
                    </div>
                    <div class="mt-4">
                        <label class="mb-2 d-block text-muted f-12">{{ $t('map.altitudeCalibration.selectFloorLabel') }}</label>
                        <b-select v-model="calibrationFloorSelected">
                            <b-select-option-group :label="$t('map.altitudeCalibration.selectFloorOption')">
                                <b-select-option v-for="floor in map.floors" :key="floor.id" :value="floor">{{ floor.getName() }}</b-select-option>
                            </b-select-option-group>
                        </b-select>
                    </div>
                </div>
            </div>

            <template #footer>
                <b-btn size="lg" variant="accent" class="rounded-xl" block @click="calibrateAltitude">
                    <i class="fal fa-ruler mr-1 font-weight-bold"/>
                    {{ $t('map.altitudeCalibration.calibrate') }}
                </b-btn>
                <b-btn size="lg" variant="light" class="rounded-xl text-muted mt-0 font-weight-normal" block
                       @click="altitudeCalibrationVisibility = false">
                    {{ $t('map.assistant.cancel') }}
                </b-btn>
            </template>
        </e-mobile-modal>

        <e-mobile-modal v-model="permissionShowcaseModalVisible" :close-on-backdrop="false" :close-on-escape="false"
            :hide-close="true" header-no-padding>
            <template #header>
                <div class="showcase-animation">
                    <video autoplay loop muted playsinline preload="auto"
                        :poster="require('@/assets/icons/wayfinding/blueDotAnimation.webp')"
                        :src="require('@/assets/icons/wayfinding/blueDotAnimation.mp4')" />
                </div>
            </template>

            <div class="showcase-content showcase-content-animated">
                <div class="showcase-body">
                    <div class="showcase-label">
                        <i class="fal fa-shield mr-2" />
                        <span>{{ $t('map.sensorsModal.label') }}</span>
                    </div>
                    <div class="showcase-title">
                        {{ permissionShowcaseTitle }}
                    </div>
                    <div class="showcase-description">
                        {{ permissionShowcaseIntro }}
                    </div>
                </div>
            </div>

            <template #footer>
                <b-btn size="lg" variant="accent" class="rounded-xl" block
                    @click="requestPermissionsForMobileNavigation" :disabled="sensors.pending">
                    <i class="fal fa-location-dot mr-1 font-weight-bold" />
                    {{ permissionShowcaseStart }}
                </b-btn>
                <b-btn size="lg" variant="light" class="rounded-xl text-muted mt-0 font-weight-normal" block
                    @click="continueInSimpleMode">
                    {{ $t('map.sensorsModal.skip') }}
                </b-btn>
            </template>
        </e-mobile-modal>

        <e-mobile-modal v-model="geolocationFeedbackModal.visible" :close-on-backdrop="false" :close-on-escape="false" :hide-close="true" header-no-padding>
            <!-- Loading state -->
            <div v-if="geolocationFeedbackModal.type === 'loading'" class="showcase-content">
                <div class="showcase-body d-flex flex-column align-items-center text-center py-4">
                    <i class="fal fa-spinner-third fa-spin fa-3x mb-3" />
                    <div class="showcase-title">
                        {{ $t('map.geolocationFeedbackModal.loading.title') }}
                    </div>
                    <div class="showcase-description text-muted mt-1">
                        {{ $t('map.geolocationFeedbackModal.loading.intro') }}
                    </div>
                </div>
            </div>

            <!-- Denied state (Android) -->
            <div v-if="geolocationFeedbackModal.type === 'denied' && isAndroidDevice" class="showcase-content">
                <div class="showcase-body">
                    <div class="showcase-label text-danger">
                        <i class="fal fa-shield mr-2" />
                        <span>{{ $t('map.sensorsModal.locationDenied.label') }}</span>
                    </div>
                    <div class="showcase-title">
                        {{ $t('map.sensorsModal.locationDenied.title') }}
                    </div>
                    <div class="showcase-description">
                        {{ $t('map.sensorsModal.locationDenied.intro') }}
                    </div>
                    <div class="showcase-body">
                        <div class="showcase-icon-text">
                            <div class="showcase-icon">
                                <i class="fa-light fa-lock"></i>
                            </div>
                            <div class="showcase-text">
                                <p>
                                    <span>1.</span>
                                    {{ $t('map.sensorsModal.locationDenied.step1') }}
                                </p>
                            </div>
                        </div>
                        <div class="showcase-icon-text">
                            <div class="showcase-icon">
                                <i class="fa-light fa-gear"></i>
                            </div>
                            <div class="showcase-text">
                                <p>
                                    <span>2.</span>
                                    {{ $t('map.sensorsModal.locationDenied.step2') }}
                                </p>
                            </div>
                        </div>
                        <div class="showcase-icon-text">
                            <div class="showcase-icon">
                                <i class="fa-light fa-arrow-pointer"></i>
                            </div>
                            <div class="showcase-text">
                                <p>
                                    <span>3.</span>
                                    {{ $t('map.sensorsModal.locationDenied.step3') }}
                                </p>
                            </div>
                        </div>
                        <div class="showcase-icon-text">
                            <div class="showcase-icon">
                                <i class="fa-light fa-circle-check"></i>
                            </div>
                            <div class="showcase-text">
                                <p>
                                    <span>4.</span>
                                    {{ $t('map.sensorsModal.locationDenied.step4') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Denied state (iOS) -->
            <div v-if="geolocationFeedbackModal.type === 'denied' && !isAndroidDevice" class="showcase-content">
                <div class="showcase-body">
                    <div class="showcase-label text-danger">
                        <i class="fal fa-shield mr-2" />
                        <span>{{ $t('map.sensorsModal.locationDenied.label') }}</span>
                    </div>
                    <div class="showcase-title">
                        {{ $t('map.sensorsModal.locationDenied.title') }}
                    </div>
                    <div class="showcase-description">
                        {{ $t('map.sensorsModal.locationDenied.ios.intro') }}
                    </div>
                </div>
            </div>

            <!-- Still not granted state -->
            <div v-if="geolocationFeedbackModal.type === 'stillNotGranted'" class="showcase-content">
                <div class="showcase-body">
                    <div class="showcase-label text-warning">
                        <i class="fal fa-location-dot mr-2" />
                        <span>{{ $t('map.geolocationFeedbackModal.stillNotGranted.label') }}</span>
                    </div>
                    <div class="showcase-title">
                        {{ $t('map.geolocationFeedbackModal.stillNotGranted.title') }}
                    </div>
                    <div class="showcase-description">
                        {{ $t('map.geolocationFeedbackModal.stillNotGranted.intro') }}
                    </div>
                </div>
            </div>

            <!-- Error state -->
            <div v-if="geolocationFeedbackModal.type === 'error'" class="showcase-content">
                <div class="showcase-body">
                    <div class="showcase-label text-danger">
                        <i class="fal fa-location-dot-slash mr-2" />
                        <span>{{ $t('map.geolocationRuntimeErrorModal.label') }}</span>
                    </div>
                    <div class="showcase-title">
                        {{ $t('map.geolocationRuntimeErrorModal.title') }}
                    </div>
                    <div class="showcase-description">
                        {{ $t('map.geolocationRuntimeErrorModal.intro') }}
                    </div>
                </div>
            </div>

            <!-- Out of bounds state -->
            <div v-if="geolocationFeedbackModal.type === 'out-of-bounds'" class="showcase-content">
                <div class="showcase-body">
                    <div class="showcase-label text-accent">
                        <i class="fal fa-location-dot-slash mr-2" />
                        <span>{{ $t('map.geolocationOutOfBoundsModal.label') }}</span>
                    </div>
                    <div class="showcase-title">
                        {{ $t('map.geolocationOutOfBoundsModal.title') }}
                    </div>
                    <div class="showcase-description">
                        {{ $t('map.geolocationOutOfBoundsModal.intro') }}
                    </div>
                </div>
            </div>

            <template #footer>
                <template v-if="geolocationFeedbackModal.type === 'denied'">
                    <b-btn size="lg" variant="accent" class="rounded-xl" block
                        @click="requestReEnableLocation" :disabled="sensors.pending">
                        <i class="fal mr-1 font-weight-bold" :class="{'fa-arrow-rotate-right': !isAndroidDevice, 'fa-location-dot': isAndroidDevice}" />
                        {{ isAndroidDevice ? $t('map.sensorsModal.locationDenied.updated') : $t('map.sensorsModal.locationDenied.reload') }}
                    </b-btn>
                    <b-btn size="lg" variant="light" class="rounded-xl text-muted mt-0 font-weight-normal" block
                        @click="continueInSimpleMode">
                        {{ $t('map.sensorsModal.skip') }}
                    </b-btn>
                </template>

                <template v-else-if="geolocationFeedbackModal.type === 'stillNotGranted'">
                    <b-btn @click="requestPermissionsForMobileNavigation" size="lg" variant="accent" class="rounded-xl" block>
                        <i class="fal fa-arrow-rotate-right mr-1 font-weight-bold" />
                        {{ $t('map.geolocationFeedbackModal.stillNotGranted.cta') }}
                    </b-btn>
                    <b-btn @click="continueInSimpleMode(true)" size="lg" variant="light" class="rounded-xl text-muted mt-0 font-weight-normal" block>
                        {{ $t('map.sensorsModal.skip') }}
                    </b-btn>
                </template>

                <template v-else-if="geolocationFeedbackModal.type === 'error'">
                    <b-btn @click="onTrajectoryEndpointSelected({target: `start`, type: `current-location`})" size="lg" variant="accent" class="rounded-xl" block>
                        <i class="fal fa-arrow-rotate-right mr-1 font-weight-bold" />
                        {{ $t('map.geolocationRuntimeErrorModal.cta') }}
                    </b-btn>
                    <b-btn @click="continueInSimpleMode(true)" size="lg" variant="light" class="rounded-xl text-muted mt-0 font-weight-normal" block>
                        {{ $t('map.sensorsModal.skip') }}
                    </b-btn>
                </template>

                <template v-else-if="geolocationFeedbackModal.type === 'out-of-bounds'">
                    <b-btn @click="closeGeolocationFeedbackModal" size="lg" variant="light" class="rounded-xl text-muted mt-0 font-weight-normal" block>
                        {{ $t('map.common.continue') }}
                    </b-btn>
                </template>
            </template>
        </e-mobile-modal>

        <map-debug-menu
            :sensors-inital-states="sensorsInitalStates"
            :camera-follow-marker="cameraFollowMarker"
            :sensors="sensors"
            :pedometer-config="pedometerConfig"
            :sensitivity-options="sensitivityOptions"
            :pedometer="pedometer"
            :off-route-accuracy-factor="offRouteAccuracyFactor"
            :prev-altitude="prevAltitude"
            :fake-accuracy="fakeAccuracy"
            :instruction-distance-threshold="instructionDistanceThreshold"
            :off-route-threshold-meters="offRouteThresholdMeters"
            :gps-location-marker-item="gpsLocationMarkerItem"
            @load-debug-config="loadDebugConfig"
            @sync-pedometer-config="syncPedometerConfig"
            @save-debug-config="saveDebugConfigIntoLocalStorage"
            @update-pedometer-config="updatePedometerConfig"
            @update:off-route-accuracy-factor="offRouteAccuracyFactor = $event"
            @update:prev-altitude="prevAltitude = $event"
            @update:fake-accuracy="fakeAccuracy = $event"
            @update:instruction-distance-threshold="instructionDistanceThreshold = $event"
            @update:off-route-threshold-meters="offRouteThresholdMeters = $event"
            @emulate-gps-change="clearIsCurrentLocationOnSite"
            @toggle-noisy-signal="toggleNoisySignal"
            @debug-pedometer-without-gps-change="onDebugPedometerWithoutGpsChange"
            @step-joystick-change="onStepJoystickChange"
            @simulate-step-from-joystick="simulateStepFromJoystick"
            @stick-hold-start="onStickHoldStart"
            @stick-hold-end="onStickHoldEnd" />
    </website-page>
</template>

<script>
import CmsBlockBuilder from "@/components/editor/rendering/blocks/cms-block-builder.vue";
import LayerMapItems from "@/components/maps/layers/layer-map-items.vue";
import MapMobileSuggestions from "@/components/maps/mobile/map-mobile-suggestions.vue";
import MapMobileLocationDrawer from "@/components/maps/mobile/map-mobile-location-drawer.vue";
import MapMobileTrajectoryOverlay from "@/components/maps/mobile/map-mobile-trajectory-overlay.vue";
import MapMobileTrajectoryHeader from "@/components/maps/mobile/map-mobile-trajectory-header.vue";
import MapMobileNavigationAssistant from "@/components/maps/mobile/map-mobile-navigation-assistant.vue";
import MapMobileQrScanner from "@/components/maps/mobile/map-mobile-qr-scanner.vue";
import MapMobileQrLocationModal from "@/components/maps/mobile/map-mobile-qr-location-modal.vue";
import MapContainer from "@/components/maps/map-container.vue";
import MapFloorSelector from "@/components/maps/map-floor-selector.vue";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import MapMixin from "@/helpers/maps/map-mixin.js";
import Pathfinder from "@/helpers/maps/Pathfinder.js";
import { swal } from "vue-swal";
import SvgCamera from "@vue-components/helpers/SvgCamera.js";
import PreviewResponsiveContainer from "@/layout/preview-responsive-container.vue";
import MapItem from "@/helpers/maps/MapItem.js";
import MapLink from "@/helpers/maps/MapLink.js";
import LayerMapNavPath from "../components/maps/layers/layer-map-nav-path.vue";
import MapLocationMarker from "../components/maps/layers/map-items/map-location-marker.vue";
import MapAssistant from "../components/maps/map-assistant.vue";
import NavPath from "../helpers/maps/NavPath.js";
import SessionTracker from "../helpers/SessionTracker.js";
import { Compass } from "@/helpers/maps/Compass.js";
import { Motion } from "@/helpers/maps/Motion.js";
import { Pedometer } from "@/helpers/maps/Pedometer.js";
import MapStepNavigationMathHelper from "@/helpers/maps/MapStepNavigationMathHelper.js";
import SensorPermissionHelper from "@/helpers/SensorPermissionHelper.js";
import MapMobileSearch from "@components/maps/mobile/map-mobile-search.vue";
import { getLocale } from "@vue-components/helpers/I18nPluginHelper.js";
import MapSensorsStatus from "@components/maps/map-sensors-status.vue";
import EMobileModal from "@/components/e-mobile-modal.vue";
import Storage from "@vue-components/helpers/Storage.js";

const SENSOR_STATUS_ICONS = {
    noSignal: `fa-solid fa-signal-bars-slash`,
    lowSignal: `fa-solid fa-signal-bars-weak`,
    mediumSignal: `fa-solid fa-signal-bars-fair`,
    goodSignal: `fa-solid fa-signal-bars-good`,
    excellentSignal: `fa-solid fa-signal-bars-strong`,
};

export default {
    name: `cms-page-map`,
    mixins: [MapMixin],
    components: {
        LayerMapDebugPathfinder: () => import("@components/maps/layers/layer-map-debug-pathfinder.vue"),
        LayerMapDebugItemLinks: () => import("@components/maps/layers/layer-map-debug-item-links.vue"),
        MapDebugMenu: () => import("@/components/maps/map-debug-menu.vue"),
        MapSensorsStatus,
        EMobileModal,
        MapMobileSearch,
        MapMobileSuggestions,
        MapMobileLocationDrawer,
        MapMobileTrajectoryHeader,
        MapMobileTrajectoryOverlay,
        MapMobileNavigationAssistant,
        MapMobileQrScanner,
        MapMobileQrLocationModal,
        CmsBlockBuilder,
        PreviewResponsiveContainer,
        MapAssistant,
        LayerMapNavPath,
        MapLocationMarker,
        MapFloorSelector,
        LayerMapItems,
        MapContainer
    },
    provide() {
        return {
            mapCompass: this.compass
        };
    },
    data() {
        return {
            disableMap: false, // for mobile optimization only
            cameraFollowMarker: false,
            noiseConfig: {
                noisyIntervalId: null,
                t0: 0,
                noise: 0,
            },
            gpsMarker: true,
            hideInstruction: false,
            automaticChangeFloor: true,
            calibrationFloorSelected: null,
            currentLocation: null, // for kiosk only
            mapItemSelected: null,
            sensorAccuracy: `noSignal`,
            startPoint: null,
            startFloor: null, // for items with multiple floors
            endPoint: null,
            endFloor: null,
            camera: null,
            lastCameraPosition: null,
            cameraMovedThreshold: 15,
            recenterVisibleFromMapDrag: false,
            currentInstruction: null,
            currentGPSFloor: null,
            gpsNavMode: false,
            stepByStepMode: false,
            floorSelected: null,
            markerEnterInRoute: false,
            pathfinder: null,
            permissionShowcaseModalVisible: false,
            geolocationFeedbackModal: {
                visible: false,
                type: null, // 'loading' | 'denied' | 'stillNotGranted' | 'error' | 'out-of-bounds'
            },
            gpsAccuracyDisplayValue: null,
            gpsAccuracyLastRawValue: null,
            gpsAccuracyStableSampleCount: 0,
            useGPSShowcaseModalVisible: false,
            pendingNavigationStart: false,
            permissionFlowResolved: false,
            permissionShowcasePromiseResolver: null,
            sensorsInitalStates: {
                geolocation: null,
                accelerometer: null
            },
            navPath: null,
            compass: new Compass(),
            motion: new Motion(),
            pedometer: new Pedometer(),
            simulateStepTimer: null,
            sensorListenersInitialized: false,
            joystickHold: false,
            debugInfo: {
                device: null,
                geolocation: null,
                accelerometer: null,
                gyroscope: null,
                magnetometer: null
            },
            accBuffer: [],
            attBuffer: [],
            WINDOW_MS: 2000,
            floorPath: null,
            navMode: false,
            navigationActive: false,
            splitViewActive: false,
            navPreference: {accessible: false},
            navRuleSnapshot: null,
            selectedItemDistance: null,
            selectedItemDuration: null,
            searchVisible: false,
            mobileSuggestionTab: `popular`,
            qrScanner: {
                visible: false,
                source: null
            },
            qrScanResult: {
                visible: false,
                source: null,
                mapItem: null
            },
            scannedRouteModal: {
                visible: false,
                target: null
            },
            floorModal: {
                visible: false,
                direction: `up`,
                floorName: null
            },
            wheelchairExtended: false,
            map: null,
            animationTimer: null,
            analyticsRoute: null,
            geoTransform: null,
            currentLocationOnPlan: null,
            currentLocationOnPath: null,
            zoomToPositionAnimationFrame: null,
            zoomToPositionTimer: null,
            currentLocationMarker: null,
            currentLocationMarkerGPS: null,
            startFromCurrentLocation: false,
            altitudeCalibrationVisibility: false,
            startFromQRCode: false,
            startFromCurrentLocationLoading: false,
            startFromCurrentLocationDisabled: false,
            startFromCurrentLocationTimeoutId: null,
            pendingMapSelectionTarget: null,
            tempDeviceNode: null,
            tempDeviceLinks: [],
            rerouteKeepMarker: false,
            rerouteState: false,
            floorToastVisible: false,
            mapMobileAssistantDrawerOpened: false,
            offRouteThresholdMeters: 7,
            offRouteThresholdMaxMeters: 25,
            offRouteAccuracyFactor: 0.75,
            manualInstructionSelectionActive: false,
            locationUpdateIntervalId: null,
            arrivalModalVisible: false,
            layers: {
                network: true,
                security: true,
                display: false,
                indoor: true,
                location: true,
                navigation: true,
                emergency: true,
                cabling: true,
                services: true,
                zone: true,
                goods: true,
                labels: false,
                legend: false,
                guides: false
            },
            sensors: {
                accelerometer: null,
                gyroscope: null,
                compass: null,
                motionListenerRegistered: false,
                geolocationWatchId: null,
                pending: false,
                geolocation: {
                    altitude: 0,
                    coords: {
                        accuracy: null,
                        altitude: null,
                        latitude: null,
                        longitude: null,
                        speed: null
                    }
                },
                thresholds: {
                    geolocation: {
                        excellent: 5,
                        good: 15,
                        medium: 25,
                        low: 70
                    },
                    compass: {
                        excellent: 15,
                        good: 30,
                        medium: 50,
                        low: 70
                    }
                }
            },
            instructionDistanceThreshold: 5,
            cmsNavLog: null,
            stepCount: 0,
            lastDistanceBetweenUserPositionAndNextPoint: 0,
            prevAltitude: null,
            fakeAccuracy: 10,
            lastRerouteTimestamp: 0,
            pedometerConfig: {},
            junctionRadius: 2.5,
            junctionSwitchCooldownMs: 300,
            lastJunctionSwitchAt: 0,
            lastJunctionEndpointId: null,
            lastJunctionLinkId: null,
            lastPersonMarkerPositionCoords: null,
            stepModeMarkerAnchoredFromGps: false,
        };
    },
    computed: {
        isNavigationStatic: {
            get() {
                return this.navSettings.static;
            },
            set(value) {
                this.navSettings.static = value;
            }
        },
        mapBlock() {
            return this.cmsPage?.blocks.find(b => b.type === `map`);
        },
        pageBlocks() {
            return this.cmsPage?.blocks.filter(b => b.type !== `map`) || [];
        },
        floorItems() {
            if (!this.map || !this.map.items || !this.floorSelected) {
                return null;
            }
            let items = this.map.items.filter((item) =>
                item.isOnFloor(this.floorSelected.id) && item.type !== `wall` && item.type !== `node`
            );
            items.sort((a, b) => {
                return a.zIndex - b.zIndex;
            });
            return items;
        },
        sensorsStatus() {
            const geolocationAccuracy = this.sensors.geolocation?.coords?.accuracy;
            const compassAccuracy = this.compassAccuracy;
            if (!isFinite(geolocationAccuracy)) {
                return `noSignal`;
            }

            const accuracyRank = (accuracy, thresholds) => {
                if (accuracy <= thresholds.excellent) return 0;
                if (accuracy <= thresholds.good) return 1;
                if (accuracy <= thresholds.medium) return 2;
                if (accuracy <= thresholds.low) return 3;
                return 4;
            };

            const gpsRank = accuracyRank(geolocationAccuracy, this.sensors.thresholds.geolocation);
            const compassRank = accuracyRank(compassAccuracy, this.sensors.thresholds.compass);
            const worstRank = Math.max(gpsRank, compassRank);
            return [`excellentSignal`, `goodSignal`, `mediumSignal`, `lowSignal`, `noSignal`][worstRank];
        },
        mobileSensorStatusToast() {
            if (!this.navigationActive || !this.navMode || !this.navPath || !this.mapIntegrations.isGpsEnabled()) {
                return null;
            }

            if (this.isNavigationStatic) {
                return {
                    variant: `danger`,
                    icon: `fas fa-location-arrow-slash`,
                    label: this.$t(`map.staticMode.warning`),
                    clickable: true,
                    action: `showPermissionModalRequest`
                };
            }

            if (!this.isCurrentLocationOnSite) {
                return {
                    variant: `warning`,
                    icon: `fas fa-location-dot-slash`,
                    label: this.$t(`map.notOnSite`)
                };
            }

            if (!this.sensorsStatus || this.sensorsStatus === `goodSignal` || this.sensorsStatus === `excellentSignal`) {
                return null;
            }

            return {
                variant: this.sensorsStatus === `mediumSignal` ? `info` : this.sensorsStatus === `lowSignal` ? `warning` : `danger`,
                icon: SENSOR_STATUS_ICONS[this.sensorsStatus],
                label: this.$t(`map.sensorsStatus.${this.sensorsStatus}`),
                clickable: !this.isLive(),
                action: `switchGpsSignal`
            };
        },
        geolocationTimestamp() {
           return this.emulateGPS ? Date.now() : this.sensors.geolocation.timestamp;
        },
        geolocationAccuracy() {
            return this.emulateGPS ? this.fakeAccuracy : this.sensors.geolocation?.coords?.accuracy;
        },
        geolocationAltitude() {
          return this.emulateGPS ? this.currentFloor.altitude : this.sensors.geolocation?.coords?.altitude;
        },
        geolocationSignalIsGood() {
          return this.emulateGPS ? (this.geolocationAccuracy <= this.sensors.thresholds.geolocation.good)
              : (this.geolocationAccuracy <= this.sensors.thresholds.geolocation.good) && ((Date.now() - this.geolocationTimestamp) <= 3000);
        },
        cameraMoved() {
            if (!this.camera) {
                return false;
            }
            const p = this.getCameraPosition();
            if (!p) {
                return false;
            }
            if (!this.lastCameraPosition) {
                return true;
            }

            const dx = p.x - this.lastCameraPosition.x;
            const dy = p.y - this.lastCameraPosition.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const threshold = Number(this.cameraMovedThreshold);
            const safeThreshold = isFinite(threshold) && threshold > 0 ? threshold : 2;
            return distance >= safeThreshold;
        },
        gpsSignalMarkerVariant() {
            if (!this.isCurrentLocationOnSite) {
                return null;
            }
            if (this.geolocationSignalIsGood) {
                return this.isLive() ? null : `gps`;
            }
            return null;
        },
        shouldShowGpsMarker() {
            return this.gpsMarker && this.isCurrentLocationOnSite && (this.hasPlanCoordinates(this.currentLocationOnPlan) || this.hasPlanCoordinates(this.currentLocationOnPath));
        },
        shouldShowLiveGpsMarkerGroup() {
            if (!this.shouldShowGpsMarker) {
                return false;
            }

            if (this.geolocationAltitude == null) {
                return false;
            }

            const navigationContextActive = this.navigationActive && this.navMode && this.navPath && this.currentLocationOnPlan;
            if (!navigationContextActive) {
                return true;
            }

            return !this.stepByStepMode;
        },
        hasPathFallbackMarkerPosition() {
            return this.hasPlanCoordinates(this.currentLocationOnPath);
        },
        shouldShowGrayPathFallbackMarker() {
            if (!this.shouldShowLiveGpsMarkerGroup || !this.hasPathFallbackMarkerPosition) {
                return false;
            }

            if (this.manualInstructionSelectionActive) {
                return true;
            }

            return (this.mapIntegrations.isPedometerEnabled() && this.mapIntegrations.isGpsEnabled())
                && !this.stepByStepMode
                && !this.markerEnterInRoute
                && !this.isGpsMarkerOnActivePath;
        },
        shouldShowPrimaryPathFallbackMarker() {
            return !this.isNavigationStatic && !this.shouldShowLiveGpsMarkerGroup && this.hasPathFallbackMarkerPosition;
        },
        staticPrimaryFallbackMarkerItem() {
            if (!this.navMode || !this.navPath) {
                return null;
            }

            if (this.hasPlanCoordinates(this.currentLocationOnPath)) {
                return this.currentLocationOnPath;
            }

            if (this.hasPlanCoordinates(this.currentInstruction?.currentItem)) {
                return this.currentInstruction.currentItem;
            }

            if (this.hasPlanCoordinates(this.currentLocationOnPlan)) {
                return this.currentLocationOnPlan;
            }

            return null;
        },
        shouldShowStaticPrimaryFallbackMarker() {
            return !!this.isNavigationStatic
                && !!this.staticPrimaryFallbackMarkerItem
                && !this.shouldShowLiveGpsMarkerGroup
                && !this.shouldShowPrimaryPathFallbackMarker;
        },
        shouldEnableStaticPrimaryFallbackMarkerCompass() {
            return this.isNavigationStatic && this.navSettings.compass;
        },
        currentPositionFloorMismatch() {
            if (!this.navigationActive || !this.navMode || !this.navPath) {
                return false;
            }

            const currentPositionFloorId = this.currentGPSFloor?.id || this.currentLocationOnPlan?.floorId || null;
            const selectedFloorId = this.floorSelected?.id || null;
            return !!currentPositionFloorId && !!selectedFloorId && currentPositionFloorId !== selectedFloorId;
        },
        displayedGpsMarkerShape() {
            if (!this.navigationActive || !this.navMode || !this.navPath) {
                return `dot`;
            }

            if (this.isNavigationStatic || !this.isCurrentLocationOnSite) {
                return `person`;
            }

            if (!this.isGpsMarkerOnActivePath) {
                return this.markerEnterInRoute ? `dot` : `person`;
            }

            return `person`;
        },
        isRecenterVisible() {
            return !!this.navigationActive
                && !!this.navMode
                && !!this.navPath
                && (this.manualInstructionSelectionActive || this.currentPositionFloorMismatch || !this.cameraFollowMarker || this.recenterVisibleFromMapDrag);
        },
        isGpsMarkerOnActivePath() {
            if (!this.isCurrentLocationOnSite) {
                return false;
            }
            if (!this.shouldShowGpsMarker || this.freeNavigation) {
                return false;
            }
            if (!(this.navMode && this.navigationActive && this.navPath && this.currentInstruction)) {
                return false;
            }

            const floorId = this.currentLocationOnPlan?.floorId || this.floorSelected?.id || null;
            const links = this.getNavigationLinksForFloor(floorId, { fallbackToAllPathLinksWhenEmpty: true });
            if (!Array.isArray(links) || links.length === 0) {
                return false;
            }

            return !this.isGpsMarkerOffRoute(links, this.effectiveOffRouteThresholdMeters);
        },
        gpsLocationMarkerItem() {
            const marker = this.currentLocationOnPlan;
            if (!marker) {
                console.log(`No current location on plan available for marker rendering`);
                return null;
            }

            if (this.isGpsMarkerOnActivePath) {
                return marker;
            }

            const markerX = typeof marker.rawX === `number` ? marker.rawX : marker.x;
            const markerY = typeof marker.rawY === `number` ? marker.rawY : marker.y;
            const floorId = marker.floorId || this.floorSelected?.id || null;
            const floor = floorId && this.map?.getFloorById ? this.map.getFloorById(floorId) : null;
            const rawInsideFloor =
                !!floor &&
                typeof markerX === `number` &&
                isFinite(markerX) &&
                typeof markerY === `number` &&
                isFinite(markerY) &&
                markerX >= 0 &&
                markerX <= floor.realWidth &&
                markerY >= 0 &&
                markerY <= floor.realHeight;

            return {
                ...marker,
                x: rawInsideFloor ? markerX : marker.x,
                y: rawInsideFloor ? markerY : marker.y,
                floorId,
            };
        },
        markerCurrentLink() {
            if (!this.isGpsMarkerOnActivePath) {
                return;
            }
            const instructionIndex = Number(this.currentInstruction?.index);
            if (Number.isFinite(instructionIndex) && instructionIndex <= 0) {
                return null;
            }
            const marker = this.gpsLocationMarkerItem || this.currentLocationOnPath || this.currentLocationOnPlan;
            if (!marker) {
                return null;
            }

            const markerX = typeof marker.rawX === `number` ? marker.rawX : marker.x;
            const markerY = typeof marker.rawY === `number` ? marker.rawY : marker.y;
            if (!isFinite(markerX) || !isFinite(markerY)) {
                return null;
            }

            const floorId = marker.floorId || this.floorSelected?.id || this.currentLocationOnPlan?.floorId || null;
            if (!floorId) {
                return null;
            }

            const links = this.freeNavigation
                ? this.getAllLinksForFloor(floorId)
                : this.getNavigationLinksForFloor(floorId, { fallbackToAllPathLinksWhenEmpty: true });
            if (!Array.isArray(links) || links.length === 0) {
                return null;
            }

            const projection = MapStepNavigationMathHelper.findBestProjection(markerX, markerY, links);
            return projection?.link || null;
        },
        displayedGpsMarkerVariant() {
            if (!this.isCurrentLocationOnSite || this.isNavigationStatic) {
                return `primary`;
            }

            if (this.isGpsMarkerOnActivePath) {
                return this.gpsSignalMarkerVariant || `primary`;
            }

            return `primary`;
        },
        offRouteConnector() {
            if (!this.navMode || this.markerEnterInRoute || !this.navigationActive || !this.navPath || this.freeNavigation || !this.pathfinder) {
                return null;
            }
            if (!this.isCurrentLocationOnSite || !this.shouldShowGpsMarker || !this.currentLocationOnPlan) {
                return null;
            }

            const marker = this.gpsLocationMarkerItem || this.currentLocationOnPlan;
            const markerX = typeof marker?.x === `number` ? marker.x : null;
            const markerY = typeof marker?.y === `number` ? marker.y : null;
            const floorId = marker?.floorId || this.currentLocationOnPlan?.floorId || this.floorSelected?.id || null;
            if (!isFinite(markerX) || !isFinite(markerY) || !floorId) {
                return null;
            }

            const targetFloorId = this.resolveOffRouteTargetFloorId(floorId);
            const isDifferentFloorFromTarget = !!targetFloorId && targetFloorId !== floorId;

            const floorLinks = this.getNavigationLinksForFloor(floorId, { fallbackToAllPathLinksWhenEmpty: false });
            const links = (Array.isArray(floorLinks) && floorLinks.length > 0)
                ? floorLinks
                : (isDifferentFloorFromTarget
                    ? []
                    : this.getNavigationLinksForFloor(floorId, { fallbackToAllPathLinksWhenEmpty: true }));

            if (!isDifferentFloorFromTarget && (!Array.isArray(links) || links.length === 0)) {
                return null;
            }

            if (!isDifferentFloorFromTarget && !this.isGpsMarkerOffRoute(links, this.effectiveOffRouteThresholdMeters)) {
                return null;
            }

            let startCandidate = this.findClosestMapItemToPlanCoord(markerX, markerY, floorId);
            if (!startCandidate) {
                startCandidate = this.findClosestMapItemToPlanCoord(markerX, markerY);
            }
            if (!startCandidate) {
                return null;
            }

            if (isDifferentFloorFromTarget) {
                const transitionTargets = this.getFloorConnectorsBetweenFloors(floorId, targetFloorId, {
                    anchorX: markerX,
                    anchorY: markerY
                });
                if (!transitionTargets.length) {
                    return null;
                }

                if (transitionTargets.some((target) => this.isSameMapItem(target, startCandidate))) {
                    return null;
                }

                let connectorPath = null;
                const maxTransitionCandidates = Math.min(transitionTargets.length, 12);
                for (let i = 0; i < maxTransitionCandidates; i++) {
                    const target = transitionTargets[i];
                    if (!target || this.isSameMapItem(target, startCandidate)) {
                        continue;
                    }
                    const candidatePath = this.pathfinder.findClosestPath(startCandidate, target);
                    if (!candidatePath || !Array.isArray(candidatePath.links) || candidatePath.links.length === 0) {
                        continue;
                    }
                    if (!connectorPath || (candidatePath.totalDistance || Number.POSITIVE_INFINITY) < (connectorPath.totalDistance || Number.POSITIVE_INFINITY)) {
                        connectorPath = candidatePath;
                    }
                }

                if (!connectorPath || !Array.isArray(connectorPath.links) || connectorPath.links.length === 0) {
                    return null;
                }

                return {
                    floorId,
                    links: connectorPath.links
                };
            }

            const navItems = (Array.isArray(this.navPath?.items) ? this.navPath.items : [])
                .filter((item) => item && isFinite(item.x) && isFinite(item.y) && Array.isArray(item.links) && item.links.length > 0);
            const sameFloorNavItems = navItems.filter((item) => item.floorId === floorId);
            const pathTargets = sameFloorNavItems.length > 0 ? sameFloorNavItems : navItems;

            if (!pathTargets.length) {
                return null;
            }

            const sortedTargets = [...pathTargets].sort((a, b) => {
                const da = Math.pow(a.x - markerX, 2) + Math.pow(a.y - markerY, 2);
                const db = Math.pow(b.x - markerX, 2) + Math.pow(b.y - markerY, 2);
                return da - db;
            });

            const candidateTargets = [];
            if (this.currentInstruction?.currentItem) {
                candidateTargets.push(this.currentInstruction.currentItem);
            }
            for (let i = 0; i < Math.min(sortedTargets.length, 8); i++) {
                const candidate = sortedTargets[i];
                if (!candidateTargets.includes(candidate)) {
                    candidateTargets.push(candidate);
                }
            }

            let connectorPath = null;
            for (const target of candidateTargets) {
                if (!target || target === startCandidate || (target.id && startCandidate.id && target.id === startCandidate.id)) {
                    continue;
                }
                const candidatePath = this.pathfinder.findClosestPath(startCandidate, target);
                if (!candidatePath || !Array.isArray(candidatePath.links) || candidatePath.links.length === 0) {
                    continue;
                }
                if (!connectorPath || (candidatePath.totalDistance || Number.POSITIVE_INFINITY) < (connectorPath.totalDistance || Number.POSITIVE_INFINITY)) {
                    connectorPath = candidatePath;
                }
            }

            if (!connectorPath || !Array.isArray(connectorPath.links) || connectorPath.links.length === 0) {
                return null;
            }

            return {
                floorId,
                links: connectorPath.links
            };
        },
        effectiveOffRouteThresholdMeters() {
            const baseThreshold = Number(this.offRouteThresholdMeters) || 8;
            const maxThreshold = Number(this.offRouteThresholdMaxMeters) || 25;
            const accuracy = Number(this.sensors?.geolocation?.coords?.accuracy);
            const accuracyFactor = Number(this.offRouteAccuracyFactor) || 0.75;

            if (!isFinite(accuracy) || accuracy <= 0) {
                return baseThreshold;
            }

            const adaptiveThreshold = accuracy * accuracyFactor;
            return Math.max(baseThreshold, Math.min(maxThreshold, adaptiveThreshold));
        },
        effectiveCompass() {
            if (this.debugOrientation.enabled) {
                return null;
            }
            return this.compass;
        },
        compassHeading() {
            if (this.debugOrientation.enabled) {
                const heading = this.debugOrientation.alpha;
                return heading !== null && heading !== undefined && isFinite(heading) ? heading : 0;
            }
            if (this.currentInstruction && this.disableOrientation) {
                return this.currentInstruction.toAngle;
            }
            const compassData = this.sensors.compass;
            const heading = compassData?.trueHeading ?? compassData?.heading;
            return heading !== null && heading !== undefined && isFinite(heading) ? heading : 0;
        },
        navigationMarkerAngle() {
            if (this.debugOrientation.enabled) {
                return this.compassHeading;
            }
            if (!this.currentInstruction) {
                return this.compassHeading;
            }
            if (this.isMobileLayoutOrPreview) {
                return this.compassHeading;
            }

            return this.currentInstruction.toAngle;
        },
        compassAccuracy() {
            // Accuracy in degrees (lower = more accurate, higher = less accurate)
            // Default to 45° if not available
            if (this.debugOrientation.enabled) {
                return 8;
            }
            const compassData = this.sensors.compass;
            const accuracy = compassData?.accuracy;
            return accuracy !== null && accuracy !== undefined && isFinite(accuracy) ? accuracy : 45;
        },
        rawGpsAccuracy() {
            return this.normalizeGpsAccuracyValue(this.geolocationAccuracy);
        },
        gpsAccuracy() {
            return this.rawGpsAccuracy ?? 100;
        },
        gpsAccuracyDisplay() {
            return this.gpsAccuracyDisplayValue ?? this.rawGpsAccuracy ?? 100;
        },
        permissionShowcaseVariant() {
            if (!this.mapIntegrations.isGpsEnabled() && this.mapIntegrations.isPedometerEnabled()) {
                return `noGpsPedometer`;
            }
            if (!this.mapIntegrations.isGpsEnabled() && !this.mapIntegrations.isPedometerEnabled() && this.mapIntegrations.isCompassEnabled()) {
                return `noGpsCompass`;
            }
            return `default`;
        },
        permissionShowcaseTitle() {
            if (this.permissionShowcaseVariant === `noGpsPedometer`) {
                return this.$t(`map.sensorsModal.noGpsPedometer.title`);
            }
            if (this.permissionShowcaseVariant === `noGpsCompass`) {
                return this.$t(`map.sensorsModal.noGpsCompass.title`);
            }
            return this.$t(`map.sensorsModal.title`);
        },
        permissionShowcaseIntro() {
            if (this.permissionShowcaseVariant === `noGpsPedometer`) {
                return this.$t(`map.sensorsModal.noGpsPedometer.intro`);
            }
            if (this.permissionShowcaseVariant === `noGpsCompass`) {
                return this.$t(`map.sensorsModal.noGpsCompass.intro`);
            }
            return this.$t(`map.sensorsModal.intro`);
        },
        permissionShowcaseStart() {
            if (this.permissionShowcaseVariant === `noGpsPedometer`) {
                return this.$t(`map.sensorsModal.noGpsPedometer.start`);
            }
            if (this.permissionShowcaseVariant === `noGpsCompass`) {
                return this.$t(`map.sensorsModal.noGpsCompass.start`);
            }
            return this.$t(`map.sensorsModal.start`);
        },
        compassDeclination() {
            const compassData = this.sensors.compass;
            return compassData?.declination ?? 0;
        },
        sensorsState() {
            const defaultState = {
                motionOrientation: false,
                gps: false
            };
            if (!this.map) return defaultState;
            return this.map.sensors || defaultState;
        },
        latestStartPointOption() {
            const cmsItemId = this.$store.state.mapEditor.latestStartId;
            if (!cmsItemId || !this.map || !this.pathfinder) {
                return null;
            }
            return this.findClosestItemByCmsItemId(this.endPoint || this.startPoint, cmsItemId);
        },
        latestDestinationPointOption() {
            const cmsItemId = this.$store.state.mapEditor.latestEndId;
            if (!cmsItemId || !this.map || !this.pathfinder) return null;
            return this.findClosestItemByCmsItemId(this.startPoint || this.endPoint, cmsItemId);
        },
        floorCmsItems() {
            if (!this.floorItems) {
                return null;
            }
            let items = [];
            for (let mapItem of this.floorItems) {
                if (mapItem.cmsItem && (mapItem.cmsItem.type === `department` || mapItem.cmsItem.type === `store` || mapItem.cmsItem.type === `location` || mapItem.cmsItem.type === `service`)
                    && items.indexOf(mapItem.cmsItem) === -1) {
                    items.push(mapItem.cmsItem);
                }
            }
            items.sort((a, b) => this.$tr(a.getName()).localeCompare(this.$tr(b.getName())));
            return items;
        },
        floorLinks() {
            if (!this.map.items || !this.floorSelected) {
                return null;
            }
            return this.map.links.filter(link => link.floorId === this.floorSelected.id && link.type !== `wall`);
        },
        startPointComputed() {
            if (this.isKioskDevice && this.currentLocation) {
                return this.currentLocation;
            }
            return this.startPoint;
        },
        distanceBetweenUserPositionAndNextPoint() {
            if (!(this.currentInstruction && this.currentInstruction.nextInstruction) || !this.currentLocationOnPlan) {
                return 0;
            }

            const currentLocation = this.currentLocationOnPlan;
            const nextPoint = this.currentInstruction.nextInstruction.currentItem ? this.currentInstruction.nextInstruction.currentItem : 0;

            return Math.sqrt(Math.pow(currentLocation.x - nextPoint.x, 2) + Math.pow(currentLocation.y - nextPoint.y, 2));
        },
        wrongDirection() {
            if (!this.currentInstruction || !this.currentLocationOnPlan) {
                return false;
            }

            return this.distanceBetweenUserPositionAndNextPoint > this.lastDistanceBetweenUserPositionAndNextPoint;
        },
        sensitivityOptions() {
            return [
                { value: `low`, text: `Low (less sensitive)` },
                { value: `medium`, text: `Medium` },
                { value: `high`, text: `High (more sensitive)` }
            ];
        },
        baseSharingUrl() {
            if (!this.navPath) return ``;
            const baseUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
            const startQuery = this.$tr(`start|depart`);
            const endQuery = this.$tr(`end|arrivee`);
            return `${baseUrl}?${startQuery}=${this.navPath.start.getPermalink()}&${endQuery}=${this.navPath.end.getPermalink()}`;
        }
    },
    activated() {
        if (!this.cmsPage) {
            this.init();
        } else {
            this.initMap();
        }
    },
    async mounted() {
        if (!this.cmsPage) {
            this.init();
        } else {
            this.initMap();
        }

        const { states } = await SensorPermissionHelper.checkNavigationPermissions();

        this.sensorsInitalStates = {
            geolocation: states.geolocation,
            accelerometer: states.accelerometer
        };
    },
    unmounted() {
        this.cancelZoomToPositionAnimation();
        this.stopLocationInterval();
        this.hideRerouteToast();
        this.stopJoystickHeadingSmoothing();

        clearInterval(this._compassSyncInterval);
        SensorPermissionHelper.clearGeolocationWatch(this.sensors.geolocationWatchId);
        this.compass.dispose();
        this.motion.dispose();
        this.pedometer.dispose();
    },
    watch: {
        rawGpsAccuracy: {
            immediate: true,
            handler(value) {
                this.updateDisplayedGpsAccuracy(value);
            }
        }
    },
    methods: {
        init() {
            this.loading = true;
            this.showLoading();
            NetworkV2.get(`/api/page/${this.$route.params.pageId}`)
                .then(resp => {
                    this.$store.commit(`setPage`, { page: resp.data, route: this.$route });
                    this.initMap();
                    this.hideLoading();
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                })
        },
        async initMap() {
            this.map = this.currentMap;

            this.$store.commit(`mapEditor/setMap`, this.map);

            this.navRuleSnapshot = new Map();
            if (this.map?.items?.length) {
                this.map.items.forEach((item) => this.navRuleSnapshot.set(item.id, item.navRule));
            }

            this.pathfinder = new Pathfinder(this.map, this.navSettings.preferences);
            this.permissionFlowResolved = false;

            this.navSettings.static = true;
            this.navSettings.compass = false;
            this.navSettings.gps = false;
            this.navSettings.image360 = this.mapIntegrations.isImages360Enabled();
            this.navSettings.pedometer = false;

            this.syncEndpointIdsToStore();

            let floor = null;
            let initialCameraItem = null;
            let initialCameraDelay = 0;
            let shouldLogEndCameraMove = false;

            if (this.isKioskDevice) {
                if (this.$store.state.playerId) {
                    this.currentLocation = this.map.items.find(i => i.integrations.signage.playerId === this.$store.state.playerId);
                } else {
                    this.currentLocation = this.map.items.find(i => i.integrations.signage?.playerId);
                }
            } else if (localStorage.getItem(`qrCode`)) {
                this.currentLocation = this.map.getItemByQRCode(localStorage.getItem(`qrCode`));

                if (this.currentLocation && this.currentLocation.x && this.currentLocation.y) {
                    this.currentLocationOnPlan = {
                        x: this.currentLocation.x,
                        y: this.currentLocation.y,
                    };
                    this.currentLocationMarker = {
                        x: this.currentLocation.x,
                        y: this.currentLocation.y,
                        floorId: this.currentLocation.floorId,
                        angle: this.currentLocationMarker?.angle || 0,
                    };
                    if (!this.gpsNavMode) {
                        this.currentLocationOnPath = {
                            x: this.currentLocationOnPlan.x,
                            y: this.currentLocationOnPlan.y,
                            floorId: this.currentLocationOnPlan.floorId,
                            angle: 0
                        }
                    }
                }
            } else {
                this.currentLocation = null;
            }

            if (this.currentLocation) {
                floor = this.map.getFloorById(this.currentLocation.floorId);
            }

            const pointSelected = this.$route.query.point;
            if (pointSelected) {
                this.selectMapItem(this.findClosestItemByPermalink(pointSelected));
                if (this.mapItemSelected) {
                    floor = this.map.getFloorById(this.mapItemSelected.floorId);
                    initialCameraItem = this.mapItemSelected;
                }
            }

            if (this.$route.query.search !== undefined) {
                this.searchVisible = true;
            }

            if (this.$route.query[`qr-code-scanner`] !== undefined) {
                this.$nextTick(() => {
                    this.openQrScanner(`suggestions`);
                });
            }

            const start = this.$route.query.start || this.$route.query.depart;
            if (start) {
                if (this.currentLocation) {
                    this.currentLocation = null;
                    localStorage.removeItem(`qrCode`);
                }
                this.navMode = true;
                this.selectMapItem(this.findClosestItemByPermalink(start), `start`);
                if (this.startPoint) {
                    floor = this.map.getFloorById(this.startPoint.floorId);
                    this.startFloor = floor;
                    initialCameraItem = this.startPoint;
                }
            } else if (this.currentLocation && !this.isKioskDevice) {
                // to select QR code
                this.selectMapItem(this.currentLocation, `start`);
            }

            const end = this.$route.query.end || this.$route.query.arrivee;
            if (end) {
                const item = this.findClosestItemByPermalink(end);
                this.selectMapItem(item, `end`);
                if (this.endPoint) {
                    initialCameraItem = this.endPoint;
                    shouldLogEndCameraMove = true;
                }
                if (this.endPoint && !this.startPoint) {
                    floor = this.map.getFloorById(this.endPoint.floorId);
                    this.endFloor = floor;
                }
            }

            const itemType = this.$route.query.closest || this.$route.query.autour;
            if (itemType) {
                this.findClosestItemByType(itemType);
            }

            // the nav path select floor automatically
            if (!this.navPath) {
                if (!floor) {
                    floor = this.map.getMainFloor();
                }
                this.selectFloor(floor);
            }

            this.camera = new SvgCamera(this.floorSelected.realWidth, this.floorSelected.realHeight, this.getDefaultZoom());
            if (this.isMobileLayout) {
                this.camera.padding = 10;
                this.camera.minZoom = 100;
            }

            if (this.isKioskDevice) {
                this.camera.maxZoom = this.map.display.devices?.kiosk.zoom.max || 1000;
                this.camera.minZoom = this.map.display.devices?.kiosk.zoom.min || 50;
            } else if (this.isMobileLayout) {
                this.camera.maxZoom = this.map.display.devices?.mobile.zoom.max || 1000;
                this.camera.minZoom = this.map.display.devices?.mobile.zoom.min || 100;
            } else {
                this.camera.maxZoom = this.map.display.devices?.desktop.zoom.max || 1000;
                this.camera.minZoom = this.map.display.devices?.desktop.zoom.min || 50;
            }

            if (initialCameraItem) {
                this.camera.center.x = initialCameraItem.x;
                this.camera.center.y = initialCameraItem.y;
                this.camera.zoomPercent = this.getDefaultZoom();

                setTimeout(() => {
                    this.zoomToPosition(initialCameraItem, {
                        delay: 0,
                        zoomFactor: 2,
                        duration: 500
                    });
                    if (shouldLogEndCameraMove) {
                        console.log(`Camera moved to end point after initialization`);
                    }
                }, initialCameraDelay);
            } else {
                this.resetCamera(false);
            }

            setTimeout(() => {
                this.fitCameraToNavPath();
            }, 500);
        },
        onDebugPedometerWithoutGpsChange(enabled) {
            if (!enabled) {
                this.turnToGPSMode();
                return;
            }
            this.turnToStepByStepMode();
        },
        normalizeGpsAccuracyValue(value) {
            const accuracy = Number(value);
            if (!Number.isFinite(accuracy) || accuracy <= 0) {
                return null;
            }
            return Math.max(3, Math.min(120, accuracy)) * 0.75;
        },
        updateDisplayedGpsAccuracy(rawAccuracy) {
            const normalizedAccuracy = this.normalizeGpsAccuracyValue(rawAccuracy);

            if (normalizedAccuracy == null) {
                this.gpsAccuracyDisplayValue = null;
                this.gpsAccuracyLastRawValue = null;
                this.gpsAccuracyStableSampleCount = 0;
                return;
            }

            if (!Number.isFinite(this.gpsAccuracyDisplayValue)) {
                this.gpsAccuracyDisplayValue = normalizedAccuracy;
                this.gpsAccuracyLastRawValue = normalizedAccuracy;
                this.gpsAccuracyStableSampleCount = 1;
                return;
            }

            const deltaFromDisplay = Math.abs(normalizedAccuracy - this.gpsAccuracyDisplayValue);
            const deltaFromPrevious = Number.isFinite(this.gpsAccuracyLastRawValue)
                ? Math.abs(normalizedAccuracy - this.gpsAccuracyLastRawValue)
                : deltaFromDisplay;
            const isStableSample = deltaFromDisplay <= 4 || deltaFromPrevious <= 4;

            this.gpsAccuracyStableSampleCount = isStableSample
                ? Math.min(this.gpsAccuracyStableSampleCount + 1, 6)
                : 0;

            let smoothingFactor = 0.2;
            if (deltaFromDisplay < 8) {
                smoothingFactor = 0.45;
            } else if (deltaFromDisplay < 16) {
                smoothingFactor = 0.32;
            }
            if (this.gpsAccuracyStableSampleCount >= 3) {
                smoothingFactor = Math.max(smoothingFactor, 0.58);
            }
            if (normalizedAccuracy > this.gpsAccuracyDisplayValue && deltaFromDisplay >= 20) {
                smoothingFactor *= 0.8;
            }

            const nextDisplayedAccuracy = this.gpsAccuracyDisplayValue + ((normalizedAccuracy - this.gpsAccuracyDisplayValue) * smoothingFactor);
            this.gpsAccuracyDisplayValue = Number((this.normalizeGpsAccuracyValue(nextDisplayedAccuracy) ?? normalizedAccuracy).toFixed(2));
            this.gpsAccuracyLastRawValue = normalizedAccuracy;
        },
        hasAnyOrientationCapabilityGranted() {
            return this.navSettings.compass || this.navSettings.gps || this.navSettings.pedometer;
        },
        turnToGPSMode() {
            if (!this.gpsNavMode && this.isCurrentLocationOnSite) {
                this.gpsNavMode = true;
                this.stepByStepMode = false;
                this.stepModeMarkerAnchoredFromGps = false;
                this.manualInstructionSelectionActive = false;
                this.currentLocationOnPath = null;
                console.log(`Switched to GPS navigation mode`);
            }
        },
        turnToStepByStepMode() {
            if (!this.stepByStepMode) {
                this.stepByStepMode = true;
                this.gpsNavMode = false;
                console.log(`Switched to Step-By-Step navigation mode`);
            }
        },
        anchorStepModeMarkerToLastGpsPosition() {
            if (this.stepModeMarkerAnchoredFromGps) {
                return;
            }

            const marker = this.currentLocationOnPlan || this.currentLocationOnPath;
            const lastGps = this.lastPersonMarkerPositionCoords;
            const source = (isFinite(marker?.rawX) && isFinite(marker?.rawY))
                ? marker
                : ((isFinite(lastGps?.rawX) && isFinite(lastGps?.rawY)) || (isFinite(lastGps?.x) && isFinite(lastGps?.y)) ? lastGps : null);
            if (!marker && !source) {
                return;
            }

            if (!this.currentLocationOnPlan && source) {
                this.currentLocationOnPlan = { ...source };
            }

            const markerToAnchor = this.currentLocationOnPlan;
            const rawX = typeof source?.rawX === `number` ? source.rawX : (typeof source?.x === `number` ? source.x : null);
            const rawY = typeof source?.rawY === `number` ? source.rawY : (typeof source?.y === `number` ? source.y : null);
            if (!isFinite(rawX) || !isFinite(rawY)) {
                return;
            }

            markerToAnchor.x = rawX;
            markerToAnchor.y = rawY;
            if (!markerToAnchor.floorId && this.floorSelected?.id) {
                markerToAnchor.floorId = this.floorSelected.id;
            }

            this.syncStepModeMarkerState(markerToAnchor);
            this.stepModeMarkerAnchoredFromGps = true;
        },
        syncStepModeMarkerState(marker = this.currentLocationOnPlan) {
            if (!marker || typeof marker.x !== `number` || typeof marker.y !== `number`) {
                return;
            }

            if (!marker.floorId && this.floorSelected?.id) {
                marker.floorId = this.floorSelected.id;
            }

            marker.rawX = marker.x;
            marker.rawY = marker.y;

            if (this.freeNavigation) {
                marker.distanceToLink = 0;
            } else {
                const floorId = marker.floorId || this.floorSelected?.id || null;
                const links = this.getNavigationLinksForFloor(floorId, { fallbackToAllPathLinksWhenEmpty: true });
                const snapped = this.snapPositionToPath(marker.x, marker.y, links, this.offRouteThresholdMeters);
                marker.distanceToLink = snapped && typeof snapped.dist === `number`
                    ? snapped.dist
                    : Number.POSITIVE_INFINITY;
            }

            if (!this.gpsNavMode) {
                this.currentLocationOnPath = {
                    x: marker.x,
                    y: marker.y,
                    floorId: marker.floorId || this.floorSelected?.id || null
                };
            }
        },
        async continueInSimpleMode(resetStartPoint = false) {
            this.stopWaitingForCurrentLocation();
            this.closeGeolocationFeedbackModal();
            this.permissionShowcaseModalVisible = false;
            this.permissionFlowResolved = false;
            if (this.permissionShowcasePromiseResolver) {
                this.permissionShowcasePromiseResolver(false);
                this.permissionShowcasePromiseResolver = null;
            }
            if (resetStartPoint) {
                this.setEndpoint(`start`, null);
            }

            let compassEnabled = false;
            if (this.isAndroidDevice && this.mapIntegrations.isCompassEnabled()) {
                this.initSensorListeners();
                const response = await SensorPermissionHelper.startCompass(this.compass, this.map.world.northAngle);
                compassEnabled = response.success;
            }

            this.navSettings.static = true;
            this.navSettings.compass = compassEnabled;
            this.navSettings.gps = false;
            this.navSettings.image360 = this.mapIntegrations.isImages360Enabled();
            this.navSettings.pedometer = false;

            this.isNavigationStatic = true;
            if (this.pendingNavigationStart) {
                this.activateMobileNavigation();
            }
        },
        requestReEnableLocation() {
            window.location.reload();
        },
        async loadDebugConfig() {
            try {
                const config = JSON.parse(await Storage.get(`debugConfig`));

                this.sensors = config.sensors;
                this.offRouteAccuracyFactor = config.offRouteAccuracyFactor;
                this.prevAltitude = config.prevAltitude;
                this.fakeAccuracy = config.fakeAccuracy;
                this.instructionDistanceThreshold = config.instructionDistanceThreshold;
                this.offRouteThresholdMeters = config.offRouteThresholdMeters;
            }
            catch (error) {
                console.error(`Failed to load debug config from local storage:`, error);
            }
        },
        async saveDebugConfigIntoLocalStorage() {
            const config = {
                sensors: this.sensors,
                offRouteAccuracyFactor: this.offRouteAccuracyFactor,
                prevAltitude: this.prevAltitude,
                fakeAccuracy: this.fakeAccuracy,
                instructionDistanceThreshold: this.instructionDistanceThreshold,
                offRouteThresholdMeters: this.offRouteThresholdMeters,
            }

            await Storage.set(`debugConfig`, JSON.stringify(config));
        },
        clearIsCurrentLocationOnSite() {
            this.$store.commit(`mapEditor/setIsCurrentLocationOnSite`, null);
        },
        toggleNoisySignal() {
            console.log(`Toggling noisy GPS signal. Noisy: ${this.noisyGPS}`);
            if (!this.noisyGPS) {
                if (this.noiseConfig.noisyIntervalId) {
                    clearInterval(this.noiseConfig.noisyIntervalId);
                    this.noiseConfig.noisyIntervalId = null;
                }
                this.fakeAccuracy = 10;
                return;
            }

            if (this.noiseConfig.noisyIntervalId) return;

            this.t0 = Date.now();

            const MIN = 5;
            const MAX = 70;
            const BASE = 9;
            const DRIFT_SPEED = 0.003;
            const NOISE_ALPHA = 0.85;
            const GUST_CHANCE = 0.08;
            const GUST_MIN = 5;
            const GUST_MAX = 18;

            this.noiseConfig.noisyIntervalId = setInterval(() => {
                const t = (Date.now() - this.t0) / 1000;

                const osc =
                    2.2 * Math.sin(t * 2 * Math.PI * 0.18) +  // ~5-6s
                    1.4 * Math.sin(t * 2 * Math.PI * 0.07) +  // ~14s
                    0.6 * Math.sin(t * 2 * Math.PI * 0.33);   // ~3s

                const drift = 3.0 * Math.sin(t * 2 * Math.PI * DRIFT_SPEED);

                this.noiseConfig.noise = NOISE_ALPHA * this.noiseConfig.noise + (1 - NOISE_ALPHA) * (Math.random() * 2 - 1);

                const gust = (Math.random() < GUST_CHANCE)
                    ? (GUST_MIN + Math.random() * (GUST_MAX - GUST_MIN))
                    : 0;

                let accuracy = BASE + osc + drift + (this.noiseConfig.noise * 4) + gust;

                if (accuracy < MIN) accuracy = MIN;
                if (accuracy > MAX) accuracy = MAX;

                if (typeof this.fakeAccuracy !== `number`) this.fakeAccuracy = BASE;
                this.fakeAccuracy = this.fakeAccuracy * 0.75 + accuracy * 0.25;
            }, 1000);
        },
        activateMobileNavigation() {
            this.pendingNavigationStart = false;
            this.navigationActive = true;
            this.startNavigation();
        },
        resolveOffRouteTargetFloorId(markerFloorId = null) {
            const fromFloorId = this.currentInstruction?.fromFloor?.id || null;
            const toFloorId = this.currentInstruction?.toFloor?.id || null;
            const itemFloorId = this.currentInstruction?.currentItem?.floorId || null;
            const fallbackFloorId = itemFloorId || this.navPath?.startFloor?.id || this.floorSelected?.id || null;

            if (!markerFloorId) {
                return fromFloorId || toFloorId || fallbackFloorId;
            }

            if (fromFloorId && fromFloorId !== markerFloorId) {
                return fromFloorId;
            }
            if (toFloorId && toFloorId !== markerFloorId) {
                return toFloorId;
            }
            return fromFloorId || toFloorId || fallbackFloorId;
        },
        getFloorConnectorsBetweenFloors(fromFloorId, toFloorId, { anchorX = null, anchorY = null } = {}) {
            if (!this.map?.items?.length || !fromFloorId || !toFloorId || fromFloorId === toFloorId) {
                return [];
            }

            const targets = this.map.items.filter((item) => {
                if (!item || !Array.isArray(item.links) || item.links.length === 0) {
                    return false;
                }
                if (item.type !== `elevator` && item.type !== `stairs`) {
                    return false;
                }
                return item.isOnFloor?.(fromFloorId) && item.isOnFloor?.(toFloorId);
            });

            if (!isFinite(anchorX) || !isFinite(anchorY)) {
                return targets;
            }

            return [...targets].sort((a, b) => {
                const da = Math.pow((a?.x ?? 0) - anchorX, 2) + Math.pow((a?.y ?? 0) - anchorY, 2);
                const db = Math.pow((b?.x ?? 0) - anchorX, 2) + Math.pow((b?.y ?? 0) - anchorY, 2);
                return da - db;
            });
        },
        syncEndpointIdsToStore() {
            this.$store.commit(`mapEditor/setStartId`, this.startPoint?.cmsItem?.id || null);
            this.$store.commit(`mapEditor/setEndId`, this.endPoint?.cmsItem?.id || null);
        },
        persistLatestEndpoints() {
            if (this.startPoint?.cmsItem?.id) {
                this.$store.commit(`mapEditor/setLatestStartId`, this.startPoint.cmsItem.id);
            }
            if (this.endPoint?.cmsItem?.id) {
                this.$store.commit(`mapEditor/setLatestEndId`, this.endPoint.cmsItem.id);
            }
        },
        resolveCurrentLocationStartPoint() {
            if (this.currentLocationOnPlan && this.floorSelected) {
                const candidateOnFloor = this.findClosestMapItemToPlanCoord(this.currentLocationOnPlan.x, this.currentLocationOnPlan.y, this.floorSelected.id);
                if (candidateOnFloor) {
                    return candidateOnFloor;
                }
            }
            if (this.currentLocation) {
                return this.currentLocation;
            }
            return null;
        },
        hasReusableGrantedGeolocation(maxAgeMs = 5000) {
            const position = this.sensors.geolocation;
            const latitude = Number(position?.coords?.latitude);
            const longitude = Number(position?.coords?.longitude);
            const timestamp = Number(position?.timestamp);

            if (!isFinite(latitude) || !isFinite(longitude)) {
                return false;
            }

            if (isFinite(timestamp) && (Date.now() - timestamp) > maxAgeMs) {
                return false;
            }

            return this.isPositionInMapBounds(latitude, longitude);
        },
        onSearchEndpointSelected(payload) {
            let cmsItemId = payload.cmsItemId;
            if (payload.type === `latest-start`) {
                cmsItemId = this.$store.state.mapEditor.latestStartId;
            } else if (payload.type === `latest-destination`) {
                cmsItemId = this.$store.state.mapEditor.latestEndId;
            }

            this.selectMapItem(this.findClosestItemByCmsItemId(null, cmsItemId));
            if (this.mapItemSelected) {
                if (!this.mapItemSelected.isOnFloor(this.currentMap.id)) {
                    this.selectFloorById(this.mapItemSelected.floorId);
                }
            }
        },
        async resolveCurrentLocationFromGrantedPermission() {
            const result = await SensorPermissionHelper.getCurrentPosition();
            if (!result.success) {
                if (result.error === `Geolocation permission denied`) {
                    await this.PermissionNotGranted();
                }
                return false;
            }

            this.handleGeolocation(result.data);
            return this.hasPlanCoordinates(this.currentLocationOnPlan);
        },
        setEndpoint(target, mapItem, { fromCurrentLocation = false, fromQRCode = false } = {}) {
            if (target === `start`) {
                this.startPoint = mapItem || null;
                this.startFromCurrentLocation = !!fromCurrentLocation;
                this.startFromQRCode = !!fromQRCode;
                if (!fromCurrentLocation) {
                    this.cleanupVirtualDeviceNodes();
                }
            } else {
                this.endPoint = mapItem || null;
            }
            this.syncEndpointIdsToStore();
        },
        assignEndpointByCmsItemId(target, cmsItemId) {
            if (!cmsItemId) {
                this.setEndpoint(target, null);
                return;
            }
            const currentPoint = target === `start` ? this.startPoint : this.endPoint;
            if (currentPoint?.cmsItem?.id === cmsItemId && this.map?.getItemById(currentPoint.id)) {
                this.setEndpoint(target, currentPoint);
                return;
            }
            const startPoint = target === `start` ? this.endPoint : this.startPoint;
            const mapItem = this.findClosestItemByCmsItemId(startPoint, cmsItemId);
            this.setEndpoint(target, mapItem);
        },
        async onTrajectoryEndpointSelected(payload) {
            this.closeGeolocationFeedbackModal();

            if (!payload || !payload.target) {
                return;
            }

            const target = payload.target === `start` ? `start` : `end`;
            const anchorPoint = target === `start` ? this.endPoint : this.startPoint;
            if (payload.type === `current-location`) {
                if (target !== `start`) {
                    return;
                }
                if (this.isNavigationStatic) {
                    this.isNavigationStatic = false;
                }
                this.setEndpoint(`start`, this.resolveCurrentLocationStartPoint(), { fromCurrentLocation: true });

                if (!this.hasPlanCoordinates(this.currentLocationOnPlan) && this.hasReusableGrantedGeolocation()) {
                    this.handleGeolocation(this.sensors.geolocation);
                }

                if (this.geolocationFeedbackModal.visible && this.geolocationFeedbackModal.type === `out-of-bounds`) {
                    return;
                }

                if (!this.hasPlanCoordinates(this.currentLocationOnPlan)) {
                    this.startWaitingForCurrentLocation();
                    const resolvedCurrentLocation = await this.getCurrentGeolocationPosition();

                    if (this.geolocationFeedbackModal.visible && this.geolocationFeedbackModal.type !== `loading`) {
                        this.stopWaitingForCurrentLocation();
                        return;
                    }

                    if (!resolvedCurrentLocation || !this.hasPlanCoordinates(this.currentLocationOnPlan)) {
                        return;
                    }

                    this.stopWaitingForCurrentLocation({ closeModal: true });
                    const mapItem = this.resolveCurrentLocationStartPoint();
                    this.setEndpoint(`start`, mapItem, { fromCurrentLocation: true });
                    if (this.startPoint && this.endPoint?.cmsItem?.hasMultipleMapItems()) {
                        this.setEndpoint(`end`, this.findClosestItemByCmsItemId(this.startPoint, this.endPoint.cmsItem.id));
                    }
                    const rerouteApplied = this.rerouteFromGpsMarker({ ignoreCooldown: true });
                    if (!rerouteApplied) {
                        this.navPointUpdated();
                    }
                    this.gpsNavMode = true;
                    await Storage.set(`lastStartGPSLocation`, mapItem.id);
                    return;
                }
            }
            this.resetNavigationMode();
            if (payload.type === `map-item`) {
                this.setEndpoint(target, this.map.getItemById(payload.mapItemId));
                this.turnToStepByStepMode();
            } else if (payload.type === `cms-item`) {
                this.assignEndpointByCmsItemId(target, payload.cmsItemId);
                this.turnToStepByStepMode();
            } else if (payload.type === `latest-start`) {
                const cmsItemId = this.$store.state.mapEditor.latestStartId;
                const mapItem = cmsItemId ? this.findClosestItemByCmsItemId(anchorPoint, cmsItemId) : null;
                this.setEndpoint(target, mapItem);
            } else if (payload.type === `latest-destination`) {
                const cmsItemId = this.$store.state.mapEditor.latestEndId;
                const mapItem = cmsItemId ? this.findClosestItemByCmsItemId(anchorPoint, cmsItemId) : null;
                this.setEndpoint(target, mapItem);
            }
            if (target === `start` && this.startPoint && this.endPoint?.cmsItem?.hasMultipleMapItems()) {
                this.setEndpoint(`end`, this.findClosestItemByCmsItemId(this.startPoint, this.endPoint.cmsItem.id));
            }
            if (target === `end` && this.endPoint && this.startPoint?.cmsItem?.hasMultipleMapItems()) {
                this.setEndpoint(`start`, this.findClosestItemByCmsItemId(this.endPoint, this.startPoint.cmsItem.id));
            }
            if (target === `end` && this.startFromCurrentLocation && this.endPoint && this.hasPlanCoordinates(this.currentLocationOnPlan)) {
                const virtualPath = this.createVirtualDeviceRoute(this.endPoint, this.currentLocationOnPlan, false);
                if (virtualPath) {
                    return;
                }
            }
            this.navPointUpdated();
            if (target === `end` && this.endPoint && !this.navPath) {
                this.selectFloorById(this.endPoint.floorId);
                this.zoomToMapItem(this.endPoint);
            }
        },
        onTrajectorySelectFromMap(target) {
            const selectionTarget = target === `end` ? `end` : `start`;
            if (selectionTarget === `start`) {
                this.startFromCurrentLocation = false;
                this.startFromQRCode = false;
            }
            this.pendingMapSelectionTarget = selectionTarget;
            if (this.navPath) {
                this.navPath = null;
                this.currentInstruction = null;
                this.hideRerouteToast();
            }
        },
        swapNavigationEndpoints() {
            if (this.startFromCurrentLocation || this.startFromQRCode) {
                return;
            }
            const oldStart = this.startPoint;
            const oldEnd = this.endPoint;
            this.startPoint = oldEnd;
            this.endPoint = oldStart;
            this.startFromCurrentLocation = false;
            this.startFromQRCode = false;
            this.syncEndpointIdsToStore();
            this.navPointUpdated();
        },
        resetCamera(animated = true) {
            // not working yet since camera center is not updated by viewbox

            // default values for compatibility with Lakeshore, to remove soon
            let center = { x: this.floorSelected.realWidth / 2, y: this.floorSelected.realHeight / 2 };

            if (!this.map.display.center) {
                center.x = 130;
                center.y = 100;
            } else if (this.map.display.center.customize) {
                center.x = this.map.display.center.x;
                center.y = this.map.display.center.y;
            }

            if (animated) {
                this.camera.moveTo(center.x, center.y, 500);
            } else {
                this.camera.center.x = center.x;
                this.camera.center.y = center.y;
            }

            this.camera.zoomPercent = this.getDefaultZoom();
        },
        initGeoTransform() {
            if (!this.floorSelected) return;

            const imageWidth = this.floorSelected.realWidth;
            const imageHeight = this.floorSelected.realHeight;

            this.geoTransform = this.createImageTransformConfig(
                this.map.world,
                imageWidth,
                imageHeight
            );
        },
        onMapDragging() {
            if (this.cameraFollowMarker && this.navPath && this.navMode && this.navigationActive) {
                this.cameraFollowMarker = false;
                this.recenterVisibleFromMapDrag = true;
            }
        },
        onMapBackgroundClick(point) {
            if (!this.emulateGPS || !point) {
                console.log(`Emulate GPS cancelled because emulateGPS or point is null`, !this.emulateGPS || !point);
                return;
            }
            if (!this.floorSelected || !this.map?.world) {
                console.log(`Emulate GPS cancelled because floorSelected or map.world is null`, !this.floorSelected || !this.map?.world);
                return;
            }
            if (!this.geoTransform) {
                console.log(`Emulate GPS cancelled because geoTransform is null`, !this.geoTransform);
                this.initGeoTransform();
            }
            if (!this.geoTransform) {
                console.log(`Emulate GPS cancelled because geoTransform is null 2`, !this.geoTransform);
                return;
            }

            const gps = this.imageXYToLatLng(point.x, point.y, this.geoTransform);
            if (!gps || !isFinite(gps.lat) || !isFinite(gps.lng)) {
                return;
            }

            const accuracy = this.fakeAccuracy || 10;
            const altitude = this.floorSelected.altitude;
            const position = {
                coords: {
                    latitude: gps.lat,
                    longitude: gps.lng,
                    accuracy,
                    altitude,
                    altitudeAccuracy: 1,
                    heading: null,
                    speed: null
                }
            };

            this.handleGeolocation(position);
        },
        stopLocationInterval() {
            if (this.locationUpdateIntervalId) {
                clearInterval(this.locationUpdateIntervalId);
                this.locationUpdateIntervalId = null;
            }
        },
        normalizeHeading(value) {
            const heading = Number(value) || 0;
            return ((heading % 360) + 360) % 360;
        },
        clampValue(value, min, max) {
            return Math.min(max, Math.max(min, value));
        },
        getJoystickAxisValue(value, deadZone = 0.06) {
            const numeric = Number(value) || 0;
            const magnitude = Math.abs(numeric);
            if (magnitude <= deadZone) {
                return 0;
            }
            const normalized = (magnitude - deadZone) / (1 - deadZone);
            return Math.sign(numeric) * this.clampValue(normalized, 0, 1);
        },
        initializeDebugOrientationFromLiveCompass() {
            if (this.debugOrientation.enabled) {
                return;
            }
            const source = this.sensors.compass || this.compass.data || {};
            const initialHeading = source.trueHeading ?? source.heading ?? 0;
            this.debugOrientation.alpha = this.normalizeHeading(initialHeading);
            this.debugOrientation.targetAlpha = this.debugOrientation.alpha;
            this.debugOrientation.enabled = true;
        },
        onStickHoldStart() {
            this.initializeDebugOrientationFromLiveCompass();
            this.debugOrientation.isHolding = true;
            this.startJoystickHeadingSmoothing();
        },
        onStickHoldEnd() {
            this.debugOrientation.isHolding = false;
            this.debugOrientation.targetAlpha = this.debugOrientation.alpha;
            this.stopJoystickHeadingSmoothing();
        },
        shortestAngleDelta(current, target) {
            return ((target - current + 540) % 360) - 180;
        },
        startJoystickHeadingSmoothing() {
            if (this.debugOrientation.smoothingRaf) {
                return;
            }

            let lastTimestamp = performance.now();
            const tick = (timestamp) => {
                const dt = this.clampValue((timestamp - lastTimestamp) / 1000, 0, 0.08);
                lastTimestamp = timestamp;

                const target = this.debugOrientation.targetAlpha;
                if (target !== null && target !== undefined) {
                    const current = this.debugOrientation.alpha;
                    const delta = this.shortestAngleDelta(current, target);
                    const maxStep = this.debugOrientation.maxTurnSpeedDegPerSec * dt;
                    const appliedDelta = this.clampValue(delta, -maxStep, maxStep);
                    this.debugOrientation.alpha = this.normalizeHeading(current + appliedDelta);
                }

                if (!this.debugOrientation.isHolding) {
                    this.debugOrientation.smoothingRaf = null;
                    return;
                }
                this.debugOrientation.smoothingRaf = requestAnimationFrame(tick);
            };

            this.debugOrientation.smoothingRaf = requestAnimationFrame(tick);
        },
        stopJoystickHeadingSmoothing() {
            if (this.debugOrientation.smoothingRaf) {
                cancelAnimationFrame(this.debugOrientation.smoothingRaf);
                this.debugOrientation.smoothingRaf = null;
            }
        },
        onStepJoystickChange(payload = {}) {
            this.initializeDebugOrientationFromLiveCompass();

            const x = Number(payload.x) || 0;
            const y = Number(payload.y) || 0;
            const magnitude = Math.hypot(x, y);
            if (magnitude < this.debugOrientation.angleDeadZone) {
                return;
            }

            // 360 mapping from joystick direction:
            // up=0, right=90, down=180, left=270
            const heading = this.normalizeHeading((Math.atan2(x, -y) * 180) / Math.PI);
            this.debugOrientation.targetAlpha = heading;
            if (this.debugOrientation.isHolding) {
                this.startJoystickHeadingSmoothing();
            } else {
                this.debugOrientation.alpha = heading;
            }
        },
        simulateStep() {
            this.pedometer.simulateStep();
        },
        getHeadingFromJoystickPayload(payload = {}) {
            const x = Number(payload.x);
            const y = Number(payload.y);
            if (!isFinite(x) || !isFinite(y)) {
                return null;
            }

            if (Math.hypot(x, y) < this.debugOrientation.angleDeadZone) {
                return null;
            }

            return this.normalizeHeading((Math.atan2(x, -y) * 180) / Math.PI);
        },
        buildSimulatedGpsStepPosition(payload = {}) {
            if (!this.gpsNavMode || !this.mapIntegrations.isGpsEnabled()) {
                return null;
            }

            const marker = this.hasPlanCoordinates(this.currentLocationOnPlan)
                ? this.currentLocationOnPlan
                : (this.hasPlanCoordinates(this.currentLocationOnPath) ? this.currentLocationOnPath : null);
            if (!marker || !this.map?.world) {
                return null;
            }

            const floorId = marker.floorId || this.floorSelected?.id || null;
            const floor = floorId && this.map?.getFloorById ? this.map.getFloorById(floorId) : this.floorSelected;
            const targetFloor = floor || this.floorSelected;
            if (!targetFloor?.realWidth || !targetFloor?.realHeight) {
                return null;
            }

            const headingFromPayload = this.getHeadingFromJoystickPayload(payload);
            const heading = headingFromPayload != null ? headingFromPayload : this.compassHeading;
            const headingRadian = (heading || 0) * Math.PI / 180;
            const stepDistance = Number(this.pedometer?.strideLength) || 0;

            const nextX = marker.x + (stepDistance * Math.sin(headingRadian));
            const nextY = marker.y - (stepDistance * Math.cos(headingRadian));

            let transform = null;
            try {
                if (this.geoTransform && this.floorSelected?.id === targetFloor.id) {
                    transform = this.geoTransform;
                } else {
                    transform = this.createImageTransformConfig(
                        this.map.world,
                        targetFloor.realWidth,
                        targetFloor.realHeight
                    );
                }
            } catch (err) {
                console.warn(`Unable to build simulated GPS transform`, err);
                return null;
            }

            const gps = this.imageXYToLatLng(nextX, nextY, transform);
            if (!gps || !isFinite(gps.lat) || !isFinite(gps.lng)) {
                return null;
            }

            const goodAccuracyThreshold = Number(this.sensors?.thresholds?.geolocation?.good);
            const currentAccuracy = Number(this.sensors?.geolocation?.coords?.accuracy);
            const fallbackAccuracy = Number(this.fakeAccuracy) || (isFinite(goodAccuracyThreshold) ? goodAccuracyThreshold : 10);
            let accuracy = isFinite(currentAccuracy) ? currentAccuracy : fallbackAccuracy;
            if (isFinite(goodAccuracyThreshold)) {
                accuracy = Math.min(accuracy, goodAccuracyThreshold);
            }

            const altitude = this.geolocationAltitude ?? targetFloor.altitude ?? 0;
            const currentAltitudeAccuracy = Number(this.sensors?.geolocation?.coords?.altitudeAccuracy);

            return {
                coords: {
                    latitude: gps.lat,
                    longitude: gps.lng,
                    accuracy,
                    altitude,
                    altitudeAccuracy: isFinite(currentAltitudeAccuracy) ? currentAltitudeAccuracy : 1,
                    heading,
                    speed: null
                },
                timestamp: Date.now()
            };
        },
        simulateStepFromJoystick(payload = {}) {
            const simulatedGpsPosition = this.buildSimulatedGpsStepPosition(payload);
            if (simulatedGpsPosition && this.geolocationSignalIsGood && this.mapIntegrations.isGpsEnabled() && this.isCurrentLocationOnSite) {
                this.handleGeolocation(simulatedGpsPosition, { disablePathSnap: true });
                return;
            }

            let originalTimestamp = null;
            const shouldForceStepByStep = this.geolocationSignalIsGood
                && this.mapIntegrations.isGpsEnabled()
                && this.isCurrentLocationOnSite
                && this.sensors?.geolocation;

            if (shouldForceStepByStep) {
                originalTimestamp = this.sensors.geolocation.timestamp;
                // Force onStepDetected to run step-by-step branch for joystick simulation
                this.sensors.geolocation.timestamp = 0;
            }

            this.pedometer.simulateStep();

            if (shouldForceStepByStep) {
                this.sensors.geolocation.timestamp = originalTimestamp;
            }
        },
        updatePedometerConfig() {
            this.pedometer.configure({
                sex: this.pedometerConfig.sex,
                height: this.pedometerConfig.height,
                sensitivity: this.pedometerConfig.sensitivity,
                strideLength: this.pedometerConfig.strideLength,
                peakThreshold: this.pedometerConfig.peakThreshold,
                minStepIntervalMs: this.pedometerConfig.minStepIntervalMs
            });
        },
        hasPlanCoordinates(point) {
            return !!point
                && typeof point.x === `number`
                && isFinite(point.x)
                && typeof point.y === `number`
                && isFinite(point.y);
        },
        isPositionInMapBounds(lat, lng) {
            const bounds = this.map?.world?.bounds;
            if (!bounds) return true; // no bounds defined — allow all

            const corners = [
                bounds.bottomLeftCorner,
                bounds.topLeftCorner,
                bounds.topRightCorner,
                bounds.bottomRightCorner,
            ];

            // Validate that all corners exist
            if (corners.some(c => !c || !isFinite(c.lat) || !isFinite(c.lng))) {
                return true;
            }

            // Ray casting algorithm for point-in-polygon
            let inside = false;
            for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
                const xi = corners[i].lng;
                const yi = corners[i].lat;
                const xj = corners[j].lng;
                const yj = corners[j].lat;
                const intersect = ((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);

                if (intersect) {
                    inside = !inside;
                }
            }
            return inside;
        },
        isSameMapItem(a, b) {
            if (!a || !b) return false;
            return a === b || (a.id && b.id && a.id === b.id);
        },
        canSuggestManualReroute() {
            return this.navMode
                && this.navigationActive
                && !!this.navPath
                && !!this.currentInstruction
                && !!this.endPoint
                && this.hasPlanCoordinates(this.currentLocationOnPlan);
        },
        markGpsRouteEntry(navLinks = null) {
            if (this.markerEnterInRoute || !this.navMode || !this.navigationActive || !this.navPath || this.freeNavigation) {
                return false;
            }
            if (!this.isCurrentLocationOnSite || !this.hasPlanCoordinates(this.currentLocationOnPlan)) {
                return false;
            }

            const floorId = this.currentLocationOnPlan?.floorId || this.floorSelected?.id || null;
            const links = (Array.isArray(navLinks) && navLinks.length)
                ? navLinks
                : this.getNavigationLinksForFloor(floorId, { fallbackToAllPathLinksWhenEmpty: true });
            if (!Array.isArray(links) || links.length === 0) {
                return false;
            }

            if (this.isGpsMarkerOffRoute(links, this.effectiveOffRouteThresholdMeters)) {
                return false;
            }

            this.markerEnterInRoute = true;
            return true;
        },
        showRerouteToast() {
            if (this.rerouteState || !this.canSuggestManualReroute() || !this.isCurrentLocationOnSite) {
                return;
            }
            this.rerouteState = true;
        },
        showAltitudeCalibrationModal () {
          this.altitudeCalibrationVisibility = true;
          this.calibrationFloorSelected = this.floorSelected;
        },
        hideRerouteToast() {
            this.rerouteState = false;
        },
        handleOffRouteReroutePrompt(navLinks = null) {
            if (!this.canSuggestManualReroute()) {
                this.hideRerouteToast();
                return false;
            }

            const floorId = this.currentLocationOnPlan?.floorId || this.floorSelected?.id || null;
            const links = (Array.isArray(navLinks) && navLinks.length)
                ? navLinks
                : this.getNavigationLinksForFloor(floorId, { fallbackToAllPathLinksWhenEmpty: true });
            if (!Array.isArray(links) || links.length === 0) {
                this.hideRerouteToast();
                return false;
            }

            const isOffRoute = this.isGpsMarkerOffRoute(links, this.effectiveOffRouteThresholdMeters);
            if (isOffRoute) {
                this.showRerouteToast();
            } else {
                this.markGpsRouteEntry(links);
                this.hideRerouteToast();
            }
            return isOffRoute;
        },
        syncPedometerConfig() {
            this.pedometerConfig.sensitivity = this.pedometer.sensitivity;
            this.pedometerConfig.strideLength = this.pedometer.strideLength;
            this.pedometerConfig.peakThreshold = this.pedometer.peakThreshold;
            this.pedometerConfig.minStepIntervalMs = this.pedometer.minStepIntervalMs;
        },
        getNavigationLinksForFloor(floorId = null, { fallbackToAllPathLinksWhenEmpty = false } = {}) {
            let links;
            if (this.freeNavigation) {
                links = this.map?.links || [];
            } else {
                links = this.navPath?.path?.links || this.map?.links || [];
            }

            const targetFloorId = floorId || this.floorSelected?.id || this.currentLocationOnPlan?.floorId || null;
            if (!targetFloorId) {
                return links;
            }

            const floorLinks = links.filter((link) => link && link.floorId === targetFloorId);
            if (floorId) {
                if (fallbackToAllPathLinksWhenEmpty && floorLinks.length === 0) {
                    return links;
                }
                return floorLinks;
            }
            return floorLinks.length ? floorLinks : links;
        },
        getAllLinksForFloor(floorId = null) {
            const links = this.map?.links || [];
            const targetFloorId = floorId || this.floorSelected?.id || this.currentLocationOnPlan?.floorId || null;
            if (!targetFloorId) {
                return links;
            }

            const floorLinks = links.filter((link) => link && link.floorId === targetFloorId);
            if (floorId) {
                return floorLinks;
            }
            return floorLinks.length ? floorLinks : links;
        },
        isLinkInCurrentNavPath(link) {
            if (!link) return false;
            const navLinks = this.navPath?.path?.links || [];
            return navLinks.some((navLink) => navLink === link || (navLink?.id && link?.id && navLink.id === link.id));
        },
        isGpsMarkerOffRoute(links, threshold = 8) {
            const marker = this.currentLocationOnPlan;
            if (!marker) return false;

            const distanceToLink = typeof marker.distanceToLink === `number` ? marker.distanceToLink : null;
            if (distanceToLink !== null && isFinite(distanceToLink)) {
                return distanceToLink >= threshold;
            }

            const markerX = typeof marker.rawX === `number` ? marker.rawX : marker.x;
            const markerY = typeof marker.rawY === `number` ? marker.rawY : marker.y;
            if (typeof markerX !== `number` || typeof markerY !== `number`) {
                return false;
            }

            const floorId = marker.floorId || this.floorSelected?.id || null;
            const linksToCheck = (Array.isArray(links) && links.length)
                ? links
                : this.getNavigationLinksForFloor(floorId, { fallbackToAllPathLinksWhenEmpty: true });
            const snapped = this.snapPositionToPath(markerX, markerY, linksToCheck, this.offRouteThresholdMeters);
            if (!snapped) {
                return true;
            }
            return snapped.dist >= threshold;
        },
        rerouteFromGpsMarker({ ignoreCooldown = false, skipRecenter = false } = {}) {
            if (!this.endPoint || !this.currentLocationOnPlan) {
                return false;
            }

            const now = Date.now();
            const cooldownMs = 3000;
            if (!ignoreCooldown && this.lastRerouteTimestamp && (now - this.lastRerouteTimestamp) < cooldownMs) {
                return false;
            }

            const marker = this.currentLocationOnPlan;
            const markerX = typeof marker.rawX === `number` ? marker.rawX : marker.x;
            const markerY = typeof marker.rawY === `number` ? marker.rawY : marker.y;
            if (typeof markerX !== `number` || typeof markerY !== `number`) {
                return false;
            }

            const floorId = marker.floorId || this.floorSelected?.id || this.currentLocationOnPlan?.floorId || null;
            let startCandidate = this.findClosestMapItemToPlanCoord(markerX, markerY, floorId);
            if (!startCandidate) {
                startCandidate = this.findClosestMapItemToPlanCoord(markerX, markerY);
            }
            if (!startCandidate || startCandidate === this.endPoint) {
                return false;
            }

            const originStart = this.startPointComputed || this.navPath?.start || this.startPoint || this.currentLocation;
            const hasProgressedPastStart = Number.isFinite(Number(this.currentInstruction?.index)) && Number(this.currentInstruction?.index) > 0;
            const keepOriginStartInReroute = !!originStart && (hasProgressedPastStart || this.isSameMapItem(originStart, startCandidate));

            let reroutePath = null;
            let historyStart = startCandidate;
            console.log(`Rerouting from GPS marker. Candidate: ${startCandidate?.id}, Keep origin start: ${keepOriginStartInReroute}`);
            if (keepOriginStartInReroute) {
                reroutePath = this.buildPathWithWaypoint(originStart, startCandidate, this.endPoint);
                historyStart = originStart;
            } else if (this.pathfinder) {
                reroutePath = this.pathfinder.findClosestPath(startCandidate, this.endPoint);
            }

            if (!reroutePath) {
                return false;
            }

            this.lastRerouteTimestamp = now;
            this.rerouteKeepMarker = true;
            this.hideRerouteToast();
            const rerouteApplied = this.applyNavigationPath(reroutePath, {
                historyStart,
                historyEnd: this.endPoint,
                focusFloorId: startCandidate.floorId || this.floorSelected?.id || null,
                skipRecenter
            });
            if (!rerouteApplied) {
                return false;
            }

            this.setEndpoint(`start`, historyStart, { fromCurrentLocation: this.startFromCurrentLocation });
            this.alignNavigationInstructionToMapItem(startCandidate, { skipRecenter });
            return true;
        },
        syncAssistantInstructionIndex(instruction, source = `auto`) {
            if (!instruction || !this.navPath?.instructions?.length) {
                return;
            }

            let targetIndex = this.navPath.instructions.findIndex((navInstruction) => {
                if (!navInstruction || !instruction) {
                    return false;
                }
                if (navInstruction === instruction) {
                    return true;
                }
                const navItemId = navInstruction.currentItem?.id;
                const targetItemId = instruction.currentItem?.id;
                return !!navItemId && !!targetItemId && navItemId === targetItemId;
            });

            if (targetIndex < 0) {
                if (Number.isFinite(instruction.index)) {
                    targetIndex = Math.max(0, Math.min(instruction.index, this.navPath.instructions.length - 1));
                } else {
                    return;
                }
            }

            const mapAssistant = this.$refs.mapAssistant;
            const mapMobileAssistant = this.$refs.mapMobileAssistant;

            if (mapAssistant?.index !== targetIndex) {
                mapAssistant?.setIndex(targetIndex, false, source);
            }

            if (mapMobileAssistant?.index !== targetIndex) {
                mapMobileAssistant?.setIndex(targetIndex, false, false, source);
            }
        },
        getCurrentInstructionIndex() {
            if (!this.currentInstruction || !this.navPath?.instructions?.length) {
                return -1;
            }
            return this.navPath.instructions.findIndex((instruction) => {
                if (instruction === this.currentInstruction) {
                    return true;
                }
                return instruction?.currentItem?.id
                    && this.currentInstruction?.currentItem?.id
                    && instruction.currentItem.id === this.currentInstruction.currentItem.id;
            });
        },
        findClosestInstructionAtMarker(marker, maxDistance = 1.2, { includeCurrent = true } = {}) {
            if (!marker || !this.currentInstruction || !this.navPath?.instructions?.length) {
                return null;
            }

            if (!this.hasPlanCoordinates(marker)) {
                return null;
            }

            const currentIndex = this.getCurrentInstructionIndex();
            if (currentIndex < 0) {
                return null;
            }

            const markerFloorId = marker.floorId || this.floorSelected?.id || null;
            const startIndex = includeCurrent ? currentIndex : currentIndex + 1;

            let bestMatch = null;
            for (let i = startIndex; i < this.navPath.instructions.length; i++) {
                const instruction = this.navPath.instructions[i];
                const item = instruction?.currentItem;
                if (!item) continue;
                if (markerFloorId && item.floorId !== markerFloorId) continue;

                const dx = item.x - marker.x;
                const dy = item.y - marker.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist > maxDistance) continue;

                if (!bestMatch || dist < bestMatch.dist || (dist === bestMatch.dist && i > bestMatch.index)) {
                    bestMatch = { instruction, index: i, dist };
                }
            }

            return bestMatch || null;
        },
        findForwardInstructionAtMarker(marker, maxDistance = 1.2) {
            const match = this.findClosestInstructionAtMarker(marker, maxDistance, { includeCurrent: false });
            return match?.instruction || null;
        },
        findAllInstructionsAtMarker(marker, maxDistance = 1.2) {
            if (!marker || !this.navPath?.instructions?.length || !this.hasPlanCoordinates(marker)) {
                return [];
            }

            const instructions = this.navPath.instructions;
            const markerFloorId = marker.floorId || this.floorSelected?.id || null;
            const distanceLimit = Number(maxDistance);
            const safeMaxDistance = Number.isFinite(distanceLimit) && distanceLimit > 0 ? distanceLimit : 1.2;
            const matches = [];

            for (let i = 0; i < instructions.length; i++) {
                const instruction = instructions[i];
                const item = instruction?.currentItem;
                if (!item || !isFinite(item.x) || !isFinite(item.y)) {
                    continue;
                }
                if (markerFloorId && item.floorId !== markerFloorId) {
                    continue;
                }

                const dx = item.x - marker.x;
                const dy = item.y - marker.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (dist > safeMaxDistance) {
                    continue;
                }

                matches.push({ instruction, index: i, dist });
            }

            return matches.sort((a, b) => {
                if (a.dist === b.dist) {
                    return b.index - a.index;
                }
                return a.dist - b.dist;
            });
        },
        findAnyInstructionAtMarker(marker, maxDistance = 1.2) {
            const matches = this.findAllInstructionsAtMarker(marker, maxDistance);
            return matches.length > 0 ? matches[0].instruction : null;
        },
        advanceNavigationFromMarkerPosition(source = `step`) {
            if (!this.gpsNavMode && source !== `step`) {
                return;
            }
            if (source === `gps` && this.manualInstructionSelectionActive) {
                return false;
            }

            if (!this.navMode || !this.navigationActive || !this.navPath || !this.currentInstruction || (!this.currentLocationOnPlan && !this.currentLocationOnPath)) {
                console.log(`Cannot advance navigation from marker position: missing required state.`, source);
                return false;
            }

            const marker = this.currentLocationOnPlan || this.currentLocationOnPath;

            /* GPS */
            if (source === `gps`) {
                const targetLinks = this.getNavigationLinksForFloor(marker.floorId || this.floorSelected?.id);
                const markerX = typeof marker.rawX === `number` ? marker.rawX : marker.x;
                const markerY = typeof marker.rawY === `number` ? marker.rawY : marker.y;
                const snappedPosition = this.snapPositionToPath(markerX, markerY, targetLinks, this.offRouteThresholdMeters);

                if (!snappedPosition) {
                    return;
                }

                // console.log(`Nearby link ${snappedPosition?.navLink.index} ${snappedPosition.dist}m`);
                let instruction = this.navPath.getInstructionByNavLink(snappedPosition.navLink);
                if (!instruction) {
                    return;
                }

                // if we are still close from previous instruction, use it instead to make experience smoother with 360 pictures
                if (this.mapIntegrations.isImages360Enabled()) {
                    const instructionDistance = instruction.getMarkerDistance(snappedPosition);
                    const instructionProgress = instruction.getWalkedProgress(snappedPosition);

                    // if walked less than 3m or 40% of instruction distance, and maximum 8m, keep previous instruction
                    if (instructionDistance < 3 || (instructionProgress < 0.40 && instructionDistance < 8)) {
                        instruction = this.navPath.getPreviousInstruction(instruction);
                    }
                }

                this.selectInstruction(instruction, source);
                this.syncAssistantInstructionIndex(instruction);

                const gpsMarkerPosition = {
                    x: snappedPosition.x,
                    y: snappedPosition.y,
                    floorId: instruction.fromFloor.id,
                    angle: 0
                }
                this.currentLocationOnPlan = gpsMarkerPosition;
                return;
            }

            /* PEDOMETER */
            let hasAdvanced = false;

            const instruction = this.findAnyInstructionAtMarker(marker, this.instructionDistanceThreshold);
            if (!instruction || !marker) {
                return;
            }

            const currentInstructionId = this.currentInstruction?.currentItem?.id || null;
            const closestInstructionId = instruction.currentItem?.id || null;
            if (closestInstructionId && closestInstructionId !== currentInstructionId) {
                this.selectInstruction(instruction, source);
                this.syncAssistantInstructionIndex(instruction);
                hasAdvanced = true;
            }

            if (instruction.currentItem) {
                const dxFloor = instruction.currentItem.x - marker.x;
                const dyFloor = instruction.currentItem.y - marker.y;
                const nearFloorSwitchPoint = Math.sqrt(dxFloor * dxFloor + dyFloor * dyFloor) < 3;

                const prevAltitude = Number(this.prevAltitude);
                const currentAltitude = Number(this.geolocationAltitude);
                const altitudeDiff = (isFinite(prevAltitude) && isFinite(currentAltitude))
                    ? Math.abs(prevAltitude - currentAltitude)
                    : 0;
                const altitudeSupportsSwitch = altitudeDiff >= 3;
                const nextFloor = this.findClosestFloorByAltitude(currentAltitude, 3);
                const shouldSwitchFloor = nearFloorSwitchPoint && altitudeSupportsSwitch;

                if (altitudeSupportsSwitch && nextFloor) {
                    if (this.floorSelected?.id !== nextFloor.id) {
                        console.log(`Automatically switching to floor ${nextFloor.name} based on marker position and altitude change.`);
                    }

                    const targetLinks = this.getNavigationLinksForFloor(nextFloor.id);
                    const markerX = typeof marker.rawX === `number` ? marker.rawX : marker.x;
                    const markerY = typeof marker.rawY === `number` ? marker.rawY : marker.y;
                    const snappedOnTargetFloor = this.snapPositionToPath(markerX, markerY, targetLinks, this.offRouteThresholdMeters);

                    const nextMarkerState = {
                        x: snappedOnTargetFloor?.x ?? marker.x,
                        y: snappedOnTargetFloor?.y ?? marker.y,
                        floorId: nextFloor.id,
                        distanceToLink: (snappedOnTargetFloor && typeof snappedOnTargetFloor.dist === `number`)
                            ? snappedOnTargetFloor.dist
                            : this.currentLocationOnPlan?.distanceToLink
                    };

                    this.currentLocationOnPlan = {
                        ...this.currentLocationOnPlan,
                        ...nextMarkerState
                    };

                    if (!this.gpsNavMode) {
                        this.currentLocationOnPath = {
                            x: this.currentLocationOnPlan.x,
                            y: this.currentLocationOnPlan.y,
                            floorId: this.currentLocationOnPlan.floorId,
                            angle: 0
                        }
                    }

                    hasAdvanced = true;
                }
            }

            const nextInstruction = instruction.nextInstruction;
            const nextItem = nextInstruction?.currentItem;

            if (!nextInstruction || !nextItem) {
                const inst = this.navPath.instructions[this.navPath.instructions.length - 1];
                this.selectInstruction(inst, source);
                this.syncAssistantInstructionIndex(inst);
                return hasAdvanced;
            }

            this.lastDistanceBetweenUserPositionAndNextPoint = this.distanceBetweenUserPositionAndNextPoint;
            const currentMarker = this.currentLocationOnPlan || this.currentLocationOnPath;
            if (!currentMarker) {
                return hasAdvanced;
            }

            const dx = nextItem.x - currentMarker.x;
            const dy = nextItem.y - currentMarker.y;
            const nearby = Math.sqrt(dx * dx + dy * dy) < this.instructionDistanceThreshold;
            if (nearby) {
                const activeInstructionId = this.currentInstruction?.currentItem?.id || null;
                const nextInstructionId = nextInstruction.currentItem?.id || null;
                if (nextInstructionId && nextInstructionId !== activeInstructionId) {
                    this.selectInstruction(nextInstruction, source);
                    this.syncAssistantInstructionIndex(nextInstruction);
                    hasAdvanced = true;
                }
                return hasAdvanced;
            }

            const directInstruction = this.findForwardInstructionAtMarker(currentMarker, this.instructionDistanceThreshold);
            if (directInstruction) {
                const activeInstructionId = this.currentInstruction?.currentItem?.id || null;
                const directInstructionId = directInstruction.currentItem?.id || null;
                if (directInstructionId && directInstructionId !== activeInstructionId) {
                    this.selectInstruction(directInstruction, source);
                    this.syncAssistantInstructionIndex(directInstruction);
                    hasAdvanced = true;
                }
            }

            return hasAdvanced;
        },
        buildPathWithWaypoint(start, waypoint, end) {
            if (!start || !waypoint || !end || !this.pathfinder) {
                return null;
            }

            const sameStartAndWaypoint = start === waypoint || (start.id && waypoint.id && start.id === waypoint.id);
            const firstLeg = sameStartAndWaypoint ? null : this.pathfinder.findClosestPath(start, waypoint);
            const secondLeg = this.pathfinder.findClosestPath(waypoint, end);

            if (!secondLeg) {
                return null;
            }

            if (!sameStartAndWaypoint && !firstLeg) {
                return null;
            }

            if (sameStartAndWaypoint) {
                return {
                    ...secondLeg,
                    startPoint: start,
                    endPoint: end
                };
            }

            const firstItems = Array.isArray(firstLeg.items) ? firstLeg.items : [];
            const secondItems = Array.isArray(secondLeg.items) ? secondLeg.items : [];
            const items = [...firstItems];
            if (items.length && secondItems.length && items[items.length - 1]?.id === secondItems[0]?.id) {
                items.push(...secondItems.slice(1));
            } else {
                items.push(...secondItems);
            }

            const links = [
                ...(Array.isArray(firstLeg.links) ? firstLeg.links : []),
                ...(Array.isArray(secondLeg.links) ? secondLeg.links : []),
            ];

            return {
                startPoint: start,
                endPoint: end,
                startNode: firstLeg.startNode || secondLeg.startNode,
                endNode: secondLeg.endNode,
                parents: {},
                distances: {},
                totalDistance: (firstLeg.totalDistance || 0) + (secondLeg.totalDistance || 0),
                items,
                links,
            };
        },
        alignNavigationInstructionToMapItem(mapItem, { skipRecenter = false } = {}) {
            if (!this.navPath || !mapItem) {
                return;
            }

            const targetIndex = this.navPath.instructions.findIndex((instruction) => {
                const current = instruction?.currentItem;
                return !!current && (current === mapItem || (current.id && mapItem.id && current.id === mapItem.id));
            });

            if (targetIndex < 0) {
                return;
            }

            this.$nextTick(() => {
                const mapAssistant = this.$refs.mapAssistant;
                const mapMobileAssistant = this.$refs.mapMobileAssistant;

                if (mapAssistant?.setIndex) {
                    mapAssistant.setIndex(targetIndex, false, `auto`);
                }

                if (mapMobileAssistant?.setIndex) {
                    mapMobileAssistant.setIndex(targetIndex, true, false,`auto`);
                }

                const instruction = this.navPath?.instructions?.[targetIndex];
                if (instruction) {
                    this.selectInstruction(instruction, `auto`, { skipRecenter });
                    this.syncAssistantInstructionIndex(instruction);
                }
            });
        },
        applyNavigationPath(path, { historyStart = null, historyEnd = null, focusFloorId = null, skipRecenter = null } = {}) {
            if (!path) {
                this.navPath = null;
                window.history.pushState({}, ``, `${this.$route.path}`);
                return false;
            }

            this.navPath = new NavPath(path);
            this.navMode = true;
            this.cameraFollowMarker = true;

            const startForHistory = historyStart || this.startPointComputed || this.navPath.start;
            const endForHistory = historyEnd || this.endPoint || this.navPath.end;

            if (this.navPath) {
                if (!this.isKioskDevice) {
                    if (startForHistory && endForHistory) {
                        window.history.pushState({}, ``, `${this.$route.path}?${this.$tr(`start|depart`)}=${startForHistory.getPermalink()}&${this.$tr(`end|arrivee`)}=${endForHistory.getPermalink()}`);
                    } else {
                        window.history.pushState({}, ``, `${this.$route.path}`);
                    }

                } else if (endForHistory) {
                    window.history.pushState({}, ``, `${this.$route.path}?${this.$tr(`end|arrivee`)}=${endForHistory.getPermalink()}`);
                } else {
                    window.history.pushState({}, ``, `${this.$route.path}`);
                }

                this.analyticsRoute = {
                    sessionId: SessionTracker.session.id,
                    websiteId: this.cmsWebsite.id,
                    dealerid: this.dealerid,
                    duration: this.navPath.duration,
                    distance: this.navPath.distance
                };

                const analyticsStart = startForHistory || this.startPointComputed || this.navPath.start;
                const analyticsEnd = endForHistory || this.endPoint || this.navPath.end;

                if (analyticsStart && this.navPath.startFloor) {
                    this.analyticsRoute.start = {
                        id: analyticsStart.id,
                        name: analyticsStart.name,
                        type: analyticsStart.type,
                        floor: {
                            id: this.navPath.startFloor.id,
                            name: this.navPath.startFloor.name
                        }
                    };
                    if (analyticsStart.cmsItem) {
                        this.analyticsRoute.start.cmsItem = {
                            id: analyticsStart.cmsItem.id,
                            name: analyticsStart.cmsItem.name,
                            type: analyticsStart.cmsItem.type
                        };
                    }
                }

                if (analyticsEnd && this.navPath.endFloor) {
                    this.analyticsRoute.destination = {
                        id: analyticsEnd.id,
                        name: analyticsEnd.name,
                        type: analyticsEnd.type,
                        floor: {
                            id: this.navPath.endFloor.id,
                            name: this.navPath.endFloor.name
                        }
                    };
                    if (analyticsEnd.cmsItem) {
                        this.analyticsRoute.destination.cmsItem = {
                            id: analyticsEnd.cmsItem.id,
                            name: analyticsEnd.cmsItem.name,
                            type: analyticsEnd.cmsItem.type
                        };
                    }
                }

                this.analyticsRoute.navPoints = this.navPath.items.map((i) => {
                    return { id: i.id, type: i.type };
                });

                NetworkV2.post(`/api/public/analytics/routes/add`, this.analyticsRoute)
                    .then(resp => {
                        this.analyticsRoute.id = resp.data.id;
                    });
            } else {
                window.history.pushState({}, ``, `${this.$route.path}`);
            }

            this.selectFloorById(focusFloorId, !skipRecenter);

            this.nextFloorPath(!skipRecenter);
            return true;
        },
        onStepDetected() {
            if (this.isNavigationStatic) {
                return;
            }
            if (!this.mapIntegrations.isPedometerEnabled()) {
                return;
            }
            if (this.geolocationSignalIsGood && this.mapIntegrations.isGpsEnabled() && this.isCurrentLocationOnSite ) {
                this.turnToGPSMode();
                return;
            }

            this.turnToStepByStepMode()
            this.anchorStepModeMarkerToLastGpsPosition();
            let currentMarker = this.currentLocationOnPlan;

            try {
                let navLinks = this.getNavigationLinksForFloor(currentMarker?.floorId || this.floorSelected?.id);
                let movementLinks = this.freeNavigation ? navLinks : this.getAllLinksForFloor(currentMarker?.floorId || this.floorSelected?.id);
                this.stepCount++;

                if (this.navMode) {
                    if (!this.currentInstruction) {
                        return;
                    }
                    this.advanceNavigationFromMarkerPosition(`step`);
                    navLinks = this.getNavigationLinksForFloor(currentMarker?.floorId || this.floorSelected?.id);
                    movementLinks = this.freeNavigation ? navLinks : this.getAllLinksForFloor(currentMarker?.floorId || this.floorSelected?.id);
                }
                // Math.max(5, this.compassAccuracy * 1.5)
                if (this.navMode && this.navPath && this.isGpsMarkerOffRoute(navLinks, this.effectiveOffRouteThresholdMeters) && this.mapIntegrations.isGpsEnabled()) {
                    this.handleOffRouteReroutePrompt(navLinks);
                } else {
                    this.hideRerouteToast();
                }

                let distance = this.pedometer.strideLength;
                const radian = (this.compassHeading || 0) * Math.PI / 180;
                let mvx = distance * Math.sin(radian);
                let mvy = - distance * Math.cos(radian);
                if (!currentMarker) return;

                if (this.freeNavigation) {
                    currentMarker.x += mvx;
                    currentMarker.y += mvy;
                    if (!currentMarker.floorId && this.floorSelected?.id) {
                        currentMarker.floorId = this.floorSelected.id;
                    }
                    return;
                }

                let px = currentMarker.x;
                let py = currentMarker.y;

                if (this.navMode && this.navPath && !this.freeNavigation) {
                    const nextX = px + mvx;
                    const nextY = py + mvy;
                    const magneticSnapThreshold = Math.max(2, Math.min(8, this.effectiveOffRouteThresholdMeters * 0.8));
                    const currentSnap = this.snapPositionToPath(px, py, navLinks, this.offRouteThresholdMeters);
                    const projectionOnRoute = MapStepNavigationMathHelper.findBestProjection(nextX, nextY, navLinks);
                    const currentDistToRoute = currentSnap?.dist;
                    const nextDistToRoute = isFinite(projectionOnRoute?.dist2) ? Math.sqrt(projectionOnRoute.dist2) : null;
                    const isApproachingRoute =
                        isFinite(currentDistToRoute) && isFinite(nextDistToRoute)
                            ? nextDistToRoute < (currentDistToRoute - 0.05)
                            : false;
                    const isVeryNearRoute = isFinite(nextDistToRoute) && nextDistToRoute <= Math.max(0.8, magneticSnapThreshold * 0.35);
                    let isMovingAlongRoute = false;
                    if (projectionOnRoute && isFinite(projectionOnRoute.abx) && isFinite(projectionOnRoute.aby)) {
                        const movementNorm = MapStepNavigationMathHelper.normalize(mvx, mvy);
                        const linkNorm = MapStepNavigationMathHelper.normalize(projectionOnRoute.abx, projectionOnRoute.aby);
                        const alignment = Math.abs((movementNorm.x * linkNorm.x) + (movementNorm.y * linkNorm.y));
                        isMovingAlongRoute = alignment >= 0.75;
                    }

                    currentMarker.x = nextX;
                    currentMarker.y = nextY;

                    if (projectionOnRoute
                        && isFinite(nextDistToRoute)
                        && nextDistToRoute <= magneticSnapThreshold
                        && (isApproachingRoute || (isVeryNearRoute && isMovingAlongRoute))) {
                        currentMarker.x = projectionOnRoute.qx;
                        currentMarker.y = projectionOnRoute.qy;
                    }

                    this.moveCameraToMarker(currentMarker);

                    this.lastPersonMarkerPositionCoords = currentMarker
                        ? { ...currentMarker }
                        : null;
                    return;
                }

                if (!movementLinks || !Array.isArray(movementLinks) || movementLinks.length === 0) {
                    currentMarker.x = px + mvx;
                    currentMarker.y = py + mvy;
                    return;
                }

                const mvNorm = MapStepNavigationMathHelper.normalize(mvx, mvy);
                let remaining = MapStepNavigationMathHelper.len(mvx, mvy);

                let proj = MapStepNavigationMathHelper.findBestProjection(px, py, movementLinks);
                if (this.navMode && !this.freeNavigation && this.navPath && proj && !this.isLinkInCurrentNavPath(proj.link) && this.mapIntegrations.isGpsEnabled()) {
                    this.handleOffRouteReroutePrompt(navLinks);
                }
                if (!proj) {
                    currentMarker.x = px + mvx;
                    currentMarker.y = py + mvy;
                    return;
                }
                this.createLinkBetweenMarkerAndItem(currentMarker, proj.link);

                let current = proj;

                let safety = 0;
                while (remaining > 1e-6 && current && safety < 10) {
                    safety++;
                    const junction = MapStepNavigationMathHelper.detectJunction(px, py, current.link, this.junctionRadius, movementLinks);
                    if (junction) {
                        const now = Date.now();
                        const canSwitch = !(this.lastJunctionEndpointId === junction.endpointId &&
                            (now - this.lastJunctionSwitchAt) < this.junctionSwitchCooldownMs);
                        const best = MapStepNavigationMathHelper.getBestLinkFromJunction(junction.endpointId, mvNorm, movementLinks);
                        if (canSwitch && best && best.link && best.link !== current.link) {
                            const projected = MapStepNavigationMathHelper.projectPointToLink(px, py, best.link);
                            if (projected) {
                                this.lastJunctionSwitchAt = now;
                                this.lastJunctionEndpointId = junction.endpointId;
                                this.lastJunctionLinkId = best.link?.id || null;
                                current = projected;
                                px = projected.qx;
                                py = projected.qy;
                                currentMarker.x = px;
                                currentMarker.y = py;
                                this.lastPersonMarkerPositionCoords = currentMarker
                                    ? { ...currentMarker }
                                    : null;
                            }
                        }
                    }

                    const { ax, ay, abx, aby, ab2 } = current;
                    const segLen = Math.sqrt(ab2) || 1;
                    const ux = abx / segLen;
                    const uy = aby / segLen;

                    let tcur = current.t;

                    const signed = mvNorm.x * ux + mvNorm.y * uy;
                    const moveAlong = remaining * signed;

                    if (Math.abs(moveAlong) < 1e-7) {
                        break;
                    }

                    if (moveAlong > 0) {
                        const distToEnd = (1 - tcur) * segLen;

                        if (moveAlong <= distToEnd + 1e-9) {
                            const newT = tcur + moveAlong / segLen;
                            const newX = ax + newT * abx;
                            const newY = ay + newT * aby;
                            currentMarker.x = newX;
                            currentMarker.y = newY;
                            this.lastPersonMarkerPositionCoords = currentMarker
                                ? { ...currentMarker }
                                : null;
                            return;
                        } else {
                            remaining = remaining - distToEnd / Math.abs(signed);
                            px = ax + 1 * abx;
                            py = ay + 1 * aby;
                            const endpointId = current.link.slaveItem?.id || null;
                            const next = MapStepNavigationMathHelper.chooseNextLink(endpointId, px, py, mvNorm, current.link, movementLinks);
                            if (next) {
                                current = MapStepNavigationMathHelper.findBestProjection(px, py, movementLinks);
                                if (!current) {
                                    currentMarker.x = px;
                                    currentMarker.y = py;
                                    this.lastPersonMarkerPositionCoords = currentMarker
                                        ? { ...currentMarker }
                                        : null;
                                    return;
                                }
                                continue;
                            } else {
                                currentMarker.x = px;
                                currentMarker.y = py;
                                this.lastPersonMarkerPositionCoords = currentMarker
                                    ? { ...currentMarker }
                                    : null;
                                return;
                            }

                        }
                    } else {
                        const distToStart = tcur * segLen;
                        if (Math.abs(moveAlong) <= distToStart + 1e-9) {
                            const newT = tcur + moveAlong / segLen;
                            const newX = ax + newT * abx;
                            const newY = ay + newT * aby;
                            currentMarker.x = newX;
                            currentMarker.y = newY;
                            this.lastPersonMarkerPositionCoords = currentMarker
                                ? { ...currentMarker }
                                : null;
                            return;
                        } else {
                            remaining = remaining - distToStart / Math.abs(signed);
                            px = ax + 0 * abx;
                            py = ay + 0 * aby;
                            const endpointId = current.link.masterItem?.id || null;
                            const next = MapStepNavigationMathHelper.chooseNextLink(endpointId, px, py, mvNorm, current.link, movementLinks);
                            if (next) {
                                current = MapStepNavigationMathHelper.findBestProjection(px, py, movementLinks);
                                if (!current) {
                                    currentMarker.x = px;
                                    currentMarker.y = py;
                                    this.lastPersonMarkerPositionCoords = currentMarker
                                        ? { ...currentMarker }
                                        : null;
                                    return;
                                }
                                continue;
                            } else {
                                currentMarker.x = px;
                                currentMarker.y = py;
                                this.lastPersonMarkerPositionCoords = currentMarker
                                    ? { ...currentMarker }
                                    : null;
                                return;
                            }
                        }
                    }
                }

                currentMarker.x = px + mvx;
                currentMarker.y = py + mvy;
                this.lastPersonMarkerPositionCoords = this.currentLocationOnPath
                    ? { ...this.currentLocationOnPath }
                    : null;

            } catch (err) {
                console.error(`onStepDetected error`, err);
                // fallback
                if (currentMarker) {
                    const rad = (this.compassHeading || 0) * Math.PI / 180;
                    const d = this.pedometer.strideLength;
                    const dx = d * Math.sin(rad);
                    const dy = - d * Math.cos(rad);
                    currentMarker.x += dx;
                    currentMarker.y += dy;
                }
            } finally {
                if (this.stepByStepMode && currentMarker) {
                    this.syncStepModeMarkerState(currentMarker);
                }
            }
        },
        createLinkBetweenMarkerAndItem(marker, item) {
            return {
                id: `virtual-link-${marker.floorId}-${item.id}`,
                masterItem: marker,
                slaveItem: item,
                floorId: marker.floorId,
                type: `navigation`
            };
        },
        getDefaultZoom() {
            if (this.isKioskDevice) {
                return this.map.display.devices?.kiosk.zoom.default || 200;
            } else if (this.isMobileLayout) {
                return this.map.display.devices?.mobile.zoom.default || 300;
            } else {
                return this.map.display.devices?.desktop.zoom.default || 150;
            }
        },
        setAutomaticFloorChange() {
          const selectedFloorId = this.floorSelected?.id || null;
          const estimatedAltitude = this.geolocationAltitude;
          const realFloor = this.currentGPSFloor || this.findClosestFloorByAltitude(estimatedAltitude, 3) || null;
          const realFloorId = realFloor?.id || this.currentLocationOnPlan?.floorId || null;

          if (!selectedFloorId || !realFloorId) {
              this.automaticChangeFloor = true;
              console.log(`Automatic floor change enabled (insufficient floor context to lock manually).`);
              return;
          }

          this.automaticChangeFloor = selectedFloorId === realFloorId;
          if (this.automaticChangeFloor) {
              console.log(`Automatic floor change enabled (selected floor matches real floor).`);
          } else {
              console.log(`Automatic floor change paused (selected floor differs from real floor).`);
          }
        },
        selectFloor(floor, fitCameraToPath = true) {
            if (this.animationTimer) {
                clearTimeout(this.animationTimer);
                this.animationTimer = null;
            }
            this.$store.commit(`mapEditor/setFloor`, floor);
            this.floorSelected = floor;
            if (floor && this.navPath) {
                this.floorPath = this.navPath.getFloorPath(floor.id);
                if (fitCameraToPath) {
                    this.fitCameraToNavPath();
                }
            } else {
                this.floorPath = null;
            }
        },
        selectFloorById(floorId, fitCameraToPath = true) {
            this.selectFloor(this.map.getFloorById(floorId), fitCameraToPath);
        },
        switchGpsSignal() {
            if (!this.isLive()){
                if (this.fakeAccuracy <= 10) {
                    this.fakeAccuracy = 100;
                    console.log(`Switched to no GPS signal (fake accuracy: ${this.fakeAccuracy}m)`);
                } else {
                    this.fakeAccuracy = 10;
                    console.log(`Switched to good GPS signal (fake accuracy: ${this.fakeAccuracy}m)`);
                }
            }
        },
        onSensorStatusToastClick(toast) {
            if (!toast || !toast.action) {
                return;
            }

            if (toast.action === `switchGpsSignal`) {
                this.switchGpsSignal();
                return;
            }

            if (toast.action === `showPermissionModalRequest`) {
                this.showPermissionModalRequest();
            }
        },
        async showPermissionModalRequest() {
          const { geolocation } = await SensorPermissionHelper.queryPermissionStates();
          this.permissionFlowResolved = false;
          if (geolocation === `denied`) {
              this.isNavigationStatic = true;
              this.showGeolocationFeedbackModal(`denied`);
              return false;
          }
          this.permissionShowcaseModalVisible = true;
          return await new Promise((resolve) => {
              this.permissionShowcasePromiseResolver = resolve;
          });
        },
        switchToNavMode(target = `go-to`) {
            if (!this.mapItemSelected) {
                return;
            }

            const useCurrentLocationAsStart = target === `go-to` && this.hasPlanCoordinates(this.currentLocationOnPlan);
            if (target === `start-from`) {
                this.setEndpoint(`start`, this.mapItemSelected);
            } else {
                this.setEndpoint(`end`, this.mapItemSelected);
                if (useCurrentLocationAsStart) {
                    this.setEndpoint(`start`, this.resolveCurrentLocationStartPoint(), { fromCurrentLocation: true });
                    if (this.startPoint && this.endPoint?.cmsItem?.hasMultipleMapItems()) {
                        this.setEndpoint(`end`, this.findClosestItemByCmsItemId(this.startPoint, this.endPoint.cmsItem.id));
                    }
                }
            }
            this.mapItemSelected = null;
            this.selectedItemDistance = null;
            this.selectedItemDuration = null;

            this.navMode = true;
            this.navigationActive = false;
            this.navPointUpdated();

            if (!this.isKioskDevice && !useCurrentLocationAsStart) {
                const selectTarget = target === `start-from` ? `end` : `start`;
                this.$nextTick(() => {
                    this.$refs.mapMobileTrajectoryOverlayRef?.openSelect?.(selectTarget);
                });
            }
        },
        showSearch() {
            this.$nextTick(() => {
                this.$refs.mapMobileTrajectoryOverlayRef?.openSelect?.(selectTarget);
            });
        },
        showFloorToastChange() {
          this.floorToastVisible = true;
          setTimeout(() => {
                this.floorToastVisible = false;
          }, 2000);
        },
        exitNavModeToLocation() {
            this.closeNavigation();

            // Keep the endpoint as the selected item and exit nav mode
            const selectedItem = this.endPoint;
            this.startPoint = null;
            this.endPoint = null;
            this.startFromCurrentLocation = false;
            this.startFromQRCode = false;
            this.pendingMapSelectionTarget = null;
            this.$store.commit(`mapEditor/clearEndpoints`);
            this.navPath = null;
            this.currentInstruction = null;
            this.navMode = false;
            this.navigationActive = false;
            this.pendingNavigationStart = false;
            this.permissionShowcaseModalVisible = false;
            this.cleanupVirtualDeviceNodes();
            window.history.pushState({}, ``, `${this.$route.path}`);

            // Restore the selected item to show the location drawer
            if (selectedItem) {
                this.mapItemSelected = selectedItem;
            }
        },
        leaveNavPath() {
            this.closeNavigation();

            if (this.animationTimer) {
                clearTimeout(this.animationTimer);
            }
            if (this.isKioskDevice) {
                this.endPoint = null;
                this.navMode = false;
            } else {
                this.startPoint = null;
                this.endPoint = null;
                if (this.isMobileLayout) {
                    this.navMode = false;
                }
            }
            this.startFromCurrentLocation = false;
            this.startFromQRCode = false;
            this.pendingMapSelectionTarget = null;
            this.navigationActive = false;
            this.navPath = null;
            this.$store.commit(`mapEditor/clearEndpoints`);
            this.pendingNavigationStart = false;
            this.permissionShowcaseModalVisible = false;
            this.cleanupVirtualDeviceNodes();
            window.history.pushState({}, ``, `${this.$route.path}`);
        },
        initSensorListeners() {
            if (this.sensorListenersInitialized) {
                return;
            }
            this.sensorListenersInitialized = true;

            this.motion.addEventListener(`accelerometer`, (e) => { this.sensors.accelerometer = e.detail; });
            this.motion.addEventListener(`gyroscope`, (e) => { this.sensors.gyroscope = e.detail; });

            this._compassSyncInterval = setInterval(() => {
                const data = this.compass.data;
                if (data) {
                    this.sensors.compass = { ...data };
                }
            }, 500);
            this.pedometer.addEventListener(`step`, () => this.onStepDetected());
        },
        showGeolocationFeedbackModal(type) {
            this.geolocationFeedbackModal.type = type;
            this.geolocationFeedbackModal.visible = true;
        },
        startWaitingForCurrentLocation() {
            this.startFromCurrentLocationLoading = true;
            this.showGeolocationFeedbackModal(`loading`);

            if (this.startFromCurrentLocationTimeoutId) {
                clearTimeout(this.startFromCurrentLocationTimeoutId);
            }

            this.startFromCurrentLocationTimeoutId = setTimeout(() => {
                this.startFromCurrentLocationTimeoutId = null;

                if (!this.startFromCurrentLocation || !this.startFromCurrentLocationLoading || this.hasPlanCoordinates(this.currentLocationOnPlan)) {
                    return;
                }

                if (!(this.geolocationFeedbackModal.visible && this.geolocationFeedbackModal.type === `loading`)) {
                    return;
                }

                this.startFromCurrentLocationLoading = false;
                this.showGeolocationFeedbackModal(`error`);
            }, 12000);
        },
        stopWaitingForCurrentLocation({ closeModal = false } = {}) {
            if (this.startFromCurrentLocationTimeoutId) {
                clearTimeout(this.startFromCurrentLocationTimeoutId);
                this.startFromCurrentLocationTimeoutId = null;
            }

            this.startFromCurrentLocationLoading = false;

            if (closeModal) {
                this.closeGeolocationFeedbackModal();
            }
        },
        handleGeolocationRuntimeError(err) {
            if (err?.code === 3) {
                return;
            }

            const waitingForCurrentLocation = this.startFromCurrentLocation
                && this.startFromCurrentLocationLoading
                && !this.hasPlanCoordinates(this.currentLocationOnPlan);

            if (!waitingForCurrentLocation) {
                console.warn(`[Navigation] Geolocation runtime error.`, err);
                return;
            }

            this.stopWaitingForCurrentLocation();
            this.showGeolocationFeedbackModal(`error`);
        },
        async getGeolocationPermissionFeedbackType() {
            try {
                const { geolocation } = await SensorPermissionHelper.queryPermissionStates();

                if (geolocation === `denied`) {
                    return `denied`;
                }

                if (geolocation === `prompt`) {
                    return `stillNotGranted`;
                }
            } catch (error) {
                console.warn(`[Navigation] Failed to re-check geolocation permission state.`, error);
            }

            return `error`;
        },
        async handleGeolocationPermissionNotGranted() {
            const feedbackType = await this.getGeolocationPermissionFeedbackType();
            this.isNavigationStatic = true;
            this.stopWaitingForCurrentLocation();
            this.showGeolocationFeedbackModal(feedbackType);
            this.showGpsUnavailableFeedback();
        },
        closeGeolocationFeedbackModal() {
            this.geolocationFeedbackModal.visible = false;
            this.geolocationFeedbackModal.type = null;
        },
        showGpsUnavailableFeedback() {
            console.error(`[Navigation] GPS not available - feedback suppressed (handled by permission modal).`);
            this.stopWaitingForCurrentLocation();
            this.setEndpoint(`start`, null);
        },
        showPedometerUnavailableFeedback() {
            this.$bvToast.toast(`Pedometer not available`, {
                title: this.$t(`map.sensorsModal.accelerometer.title`),
                variant: `warning`,
                solid: true,
                toaster: `b-toaster-bottom-right`
            });
        },
        async getCurrentGeolocationPosition() {
            if (this.hasReusableGrantedGeolocation()) {
                this.handleGeolocation(this.sensors.geolocation);
                return this.hasPlanCoordinates(this.currentLocationOnPlan);
            }

            let geolocationGranted = await SensorPermissionHelper.isGeolocationGranted();
            if (!geolocationGranted) {
                await this.requestNavigationSensorPermissions();
                geolocationGranted = this.permissionFlowResolved;
            }

            if (!geolocationGranted) {
                return false;
            }

            if (this.hasReusableGrantedGeolocation()) {
                this.handleGeolocation(this.sensors.geolocation);
                return this.hasPlanCoordinates(this.currentLocationOnPlan);
            }

            const resolvedFromGrantedPermission = await this.resolveCurrentLocationFromGrantedPermission();
            if (resolvedFromGrantedPermission) {
                if (this.sensors.geolocationWatchId == null) {
                    const onGeoError = (err) => {
                        if (err?.code === 1) {
                            void this.handleGeolocationPermissionNotGranted();
                        }
                    }

                    const { success, data } = await SensorPermissionHelper.startGeolocationWatch(this.onGeolocationWatch, onGeoError, { skipInitialPosition: true });
                    if (success) {
                        this.sensors.geolocationWatchId = data.watchId;
                    }
                }
                return true;
            }

            if (this.sensors.geolocationWatchId != null) {
                return this.hasPlanCoordinates(this.currentLocationOnPlan);
            }

            const onGeoError = (err) => {
                if (err?.code === 1) {
                    void this.handleGeolocationPermissionNotGranted();
                }
            }

            const { success, data } = await SensorPermissionHelper.startGeolocationWatch(this.onGeolocationWatch, onGeoError);
            if (!success) {
                this.showGpsUnavailableFeedback();
                return false;
            }

            this.sensors.geolocationWatchId = data.watchId;
            if (data.initialPosition) {
                this.handleGeolocation(data.initialPosition);
            }
            return this.hasPlanCoordinates(this.currentLocationOnPlan);
        },
        calibrateAltitude() {
            this.altitudeCalibrationVisibility = false;

            const currentAltitude = this.geolocationAltitude;
            const currentFloorIndex = this.map.floors.indexOf(this.calibrationFloorSelected);
            if (currentFloorIndex === -1) {
                return 0;
            }

            const offset = currentAltitude - this.map.floors[currentFloorIndex].altitude;
            for (const floor of this.map.floors) {
                const floorIndex = this.map.floors.indexOf(floor);
                const floorAltitude = floor.altitude;
                const nextFloor = this.map.floors[floorIndex - 1];
                const prevFloor = this.map.floors[floorIndex + 1];

                if (nextFloor !== undefined) {
                    floor.delta = nextFloor.altitude - floorAltitude;
                } else if (prevFloor !== undefined) {
                    floor.delta = floorAltitude - prevFloor.altitude;
                }

                floor.altitude = floor.altitude + offset;
            }
            const actualFloor = this.findClosestFloorByAltitude(currentAltitude, 3);
            if (!actualFloor) {
                return;
            }

            this.currentGPSFloor = actualFloor;
            this.currentLocationOnPlan.floorId = actualFloor.id;
            this.selectFloor(actualFloor);
        },
        onGeolocationWatch(position) {
            if (this.emulateGPS)
                return;

            this.handleGeolocation(position);
        },
        async requestPermissionsForMobileNavigation({ skipInitialPosition = !this.startFromCurrentLocation } = {}) {
            this.initSensorListeners();
            this.sensors.pending = true;
            this.permissionShowcaseModalVisible = false;

            const requestMotion = this.mapIntegrations.isCompassEnabled() || this.mapIntegrations.isPedometerEnabled();
            const requestGeolocation = this.mapIntegrations.isGpsEnabled();
            const waitingForCurrentLocation = requestGeolocation
                && this.startFromCurrentLocation
                && !this.hasPlanCoordinates(this.currentLocationOnPlan);

            if (waitingForCurrentLocation) {
                this.startWaitingForCurrentLocation();
            }

            let success = false;
            try {
                const response = await SensorPermissionHelper.startNavigation({
                    motion: this.motion,
                    pedometer: this.pedometer,
                    compass: this.compass,
                    northAngle: this.map?.world?.northAngle,
                    requestMotion,
                    requestGeolocation,
                    skipInitialPosition,
                    onPosition: this.onGeolocationWatch,
                    onGeoError: (err) => {
                        if (err?.code === 1) {
                            void this.handleGeolocationPermissionNotGranted();
                            return;
                        }

                        this.handleGeolocationRuntimeError(err);
                    },
                });
                success = response?.success === true;
                const data = response?.data || {};
                const { motionStarted, compassStarted, geoStarted, watchId, initialPosition } = data;

                if (motionStarted) {
                    this.sensors.motionListenerRegistered = true;
                    this.updatePedometerConfig();
                } else if (requestMotion) {
                    this.showPedometerUnavailableFeedback();
                }

                if (geoStarted) {
                    this.sensors.geolocationWatchId = watchId;
                    if (initialPosition) {
                        this.handleGeolocation(initialPosition);
                    }
                    if (waitingForCurrentLocation && !this.hasPlanCoordinates(this.currentLocationOnPlan)) {
                        this.startWaitingForCurrentLocation();
                    }
                } else if (requestGeolocation) {
                    console.log(`Geolocation permission not granted or unavailable.`, requestGeolocation);

                    if (waitingForCurrentLocation) {
                        const hasTerminalGeoFeedback = this.geolocationFeedbackModal.visible
                            && this.geolocationFeedbackModal.type
                            && this.geolocationFeedbackModal.type !== `loading`;

                        this.stopWaitingForCurrentLocation();
                        if (!hasTerminalGeoFeedback) {
                            this.showGeolocationFeedbackModal(`error`);
                        }
                    }

                    this.showGpsUnavailableFeedback();
                }

                this.navSettings.static = true;
                this.navSettings.compass = !!compassStarted;
                this.navSettings.gps = !!geoStarted;
                this.navSettings.image360 = this.mapIntegrations.isImages360Enabled();
                this.navSettings.pedometer = !!motionStarted;
            } catch (e) {
                console.warn(`[Navigation] Permission request failed, falling back to simple mode`, e);
                if (waitingForCurrentLocation) {
                    const hasTerminalGeoFeedback = this.geolocationFeedbackModal.visible
                        && this.geolocationFeedbackModal.type
                        && this.geolocationFeedbackModal.type !== `loading`;

                    this.stopWaitingForCurrentLocation();
                    if (!hasTerminalGeoFeedback) {
                        this.showGeolocationFeedbackModal(`error`);
                    }
                }

                this.navSettings.static = true;
                this.navSettings.compass = false;
                this.navSettings.gps = false;
                this.navSettings.image360 = this.mapIntegrations.isImages360Enabled();
                this.navSettings.pedometer = false;

            } finally {
                this.isNavigationStatic = !this.hasAnyOrientationCapabilityGranted();
                // Only mark resolved if all required permissions were actually granted
                this.permissionFlowResolved = success;
                if (this.permissionShowcasePromiseResolver) {
                    this.permissionShowcasePromiseResolver(success);
                    this.permissionShowcasePromiseResolver = null;
                }
                this.sensors.pending = false;
                if (this.pendingNavigationStart) {
                    this.activateMobileNavigation();
                }
            }
            return success;
        },
        calculHeading(event) {
            let compass = -(event.alpha + event.beta * event.gamma / 90);
            compass -= Math.floor(compass / 360) * 360;
            return compass;
        },
        snapPositionToPath(x, y, navLinks, maxSnapDistance = 5) {
            if (!navLinks || !Array.isArray(navLinks) || navLinks.length === 0) {
                return null;
            }

            let bestResult = null;

            for (const navLink of navLinks) {
                try {
                    if (navLink.navRule && navLink.navRule === `unavailable`) {
                        continue;
                    }

                    if (!navLink.masterItem || !navLink.slaveItem) {
                        continue;
                    }

                    const ax = navLink.masterItem.x;
                    const ay = navLink.masterItem.y;
                    const bx = navLink.slaveItem.x;
                    const by = navLink.slaveItem.y;

                    if (typeof ax !== `number` || typeof ay !== `number` || typeof bx !== `number` || typeof by !== `number`) {
                        continue;
                    }

                    const abx = bx - ax;
                    const aby = by - ay;
                    const ab2 = abx * abx + aby * aby;
                    if (ab2 === 0) continue;

                    const apx = x - ax;
                    const apy = y - ay;
                    const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2));
                    const qx = ax + t * abx;
                    const qy = ay + t * aby;
                    const dx = x - qx;
                    const dy = y - qy;
                    const currentDistance = Math.sqrt(dx * dx + dy * dy);

                    if (currentDistance <= maxSnapDistance && (!bestResult || currentDistance < bestResult.dist)) {
                        bestResult = { navLink: navLink, x: qx, y: qy, dist: currentDistance };
                    }
                } catch (e) {
                    continue;
                }
            }

            return bestResult;
        },
        findClosestFloorByAltitude(altitude, maxDiff = 3) {
            const targetAltitude = Number(altitude);
            if (!isFinite(targetAltitude)) {
                return null;
            }

            const floors = [];
            const seenIds = new Set();
            const addFloor = (floor) => {
                if (!floor || floor.id == null || seenIds.has(floor.id)) {
                    return;
                }
                seenIds.add(floor.id);
                floors.push(floor);
            };

            if (Array.isArray(this.map?.floors)) {
                for (const floor of this.map.floors) {
                    addFloor(floor);
                }
            }

            let closestFloor = null;
            let closestDiff = Infinity;
            for (const floor of floors) {
                const floorAltitude = Number(floor?.altitude);
                if (!isFinite(floorAltitude)) {
                    continue;
                }

                const diff = Math.abs(floorAltitude - targetAltitude);
                if (diff <= maxDiff && diff < closestDiff) {
                    closestDiff = diff;
                    closestFloor = floor;
                }
            }

            return closestFloor;
        },
        handleGeolocation(position, options = {}) {
            if (!position) {
                console.log(`Geolocation Cancelled because position is null:`, position, options);
                return;
            }
            const disablePathSnap = !!options.disablePathSnap;
            const previousGeolocation = this.sensors.geolocation;
            const previousMarker = this.currentLocationOnPlan
                ? {
                    x: this.currentLocationOnPlan.x,
                    y: this.currentLocationOnPlan.y,
                    floorId: this.currentLocationOnPlan.floorId
                }
                : null;
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;
            const positionIsInBounds = this.isPositionInMapBounds(latitude, longitude);
            const previousOnSiteState = this.isCurrentLocationOnSite;

            if (previousOnSiteState !== positionIsInBounds) {
                this.$store.commit(`mapEditor/setIsCurrentLocationOnSite`, positionIsInBounds);
            }

            if (!positionIsInBounds) {
                const shouldShowOutOfBoundsFeedback = this.startFromCurrentLocation && !this.navigationActive;

                this.stopWaitingForCurrentLocation();
                if (shouldShowOutOfBoundsFeedback) {
                    this.setEndpoint(`start`, null);
                    this.navPointUpdated();
                    this.showGeolocationFeedbackModal(`out-of-bounds`);
                }

                if (this.gpsNavMode) {
                    this.gpsNavMode = false;
                }
                return;
            }

            this.prevAltitude = this.geolocationAltitude;
            this.sensors.geolocation = position;

            const altitude = position.coords.altitude;
            const geolocationUnchanged = previousGeolocation
                && previousGeolocation.coords.latitude === latitude
                && previousGeolocation.coords.longitude === longitude
                && previousGeolocation.coords.altitude === altitude
                && previousGeolocation.coords.accuracy === position.coords.accuracy
                && previousGeolocation.coords.heading === position.coords.heading
                && previousGeolocation.coords.speed === position.coords.speed;

            const actualFloor = this.findClosestFloorByAltitude(altitude, 3);
            if (!actualFloor) {
                return;
            }

            this.currentGPSFloor = actualFloor;
            this.startFromCurrentLocationDisabled = false;

            if (actualFloor && this.floorSelected?.id !== actualFloor?.id && this.automaticChangeFloor && !this.manualInstructionSelectionActive) {
                this.selectFloor(actualFloor, !this.cameraFollowMarker);
                this.showFloorToastChange();
            } else if (!this.automaticChangeFloor && this.floorSelected?.id === actualFloor.id) {
                this.automaticChangeFloor = true;
            }

            // if (this.compass && latitude !== null && longitude !== null) {
            //     const altitudeKm = altitude ? altitude / 1000 : 0;
            //     this.compass.setLocation(latitude, longitude, altitudeKm);
            // }

            if (this.compass && latitude !== null && longitude !== null) {
                this.compass.setLocation(latitude, longitude, altitude ? altitude / 1000 : 0);
            }

            if (geolocationUnchanged && !this.navMode && !this.startFromCurrentLocationLoading) {
                return;
            }

            if (!this.geoTransform) {
                this.initGeoTransform();
            }

            if (this.geoTransform) {
                const pos = this.latLngToImageXY(latitude, longitude, this.geoTransform);
                const liveFloorId = actualFloor?.id || this.floorSelected?.id;
                const links = this.freeNavigation ? [] : this.getNavigationLinksForFloor(liveFloorId);
                const snappedPos = this.freeNavigation ? null : this.snapPositionToPath(pos.x, pos.y, links);
                const distanceToLink = this.freeNavigation ? 0 : (snappedPos && typeof snappedPos.dist === `number` ? snappedPos.dist : Number.POSITIVE_INFINITY);
                const offRouteThreshold = Number(this.offRouteThresholdMeters);
                const snapThresholdMeters = (isFinite(offRouteThreshold) && offRouteThreshold >= 0) ? offRouteThreshold : 8;
                const shouldSnapToPath = !disablePathSnap && !!snappedPos && distanceToLink < snapThresholdMeters;

                // Always update the user's location on the plan regardless of accuracy,
                // so the marker is visible even with unstable GPS readings.
                if (actualFloor) {
                    if (shouldSnapToPath) {
                        this.currentLocationOnPlan = {
                            x: snappedPos.x,
                            y: snappedPos.y,
                            floorId: actualFloor.id,
                            angle: this.currentLocationOnPlan?.angle || 0,
                            rawX: pos.x,
                            rawY: pos.y,
                            distanceToLink,
                        };
                    } else {
                        this.currentLocationOnPlan = {
                            x: pos.x,
                            y: pos.y,
                            floorId: actualFloor.id,
                            angle: this.currentLocationOnPlan?.angle || 0,
                            rawX: pos.x,
                            rawY: pos.y,
                            distanceToLink,
                        };
                    }

                    // Only activate GPS navigation mode when accuracy is good enough
                    if (position.coords.accuracy <= this.sensors.thresholds.geolocation.good) {
                        if (this.isGpsMarkerOnActivePath) {
                            this.turnToGPSMode();
                        }
                        this.markGpsRouteEntry(links);
                    }
                } else {
                    if (this.gpsNavMode) {
                        this.gpsNavMode = false;
                    }
                }

                if (this.navMode && this.navigationActive) {
                    this.advanceNavigationFromMarkerPosition(`gps`);
                }
                this.lastPersonMarkerPositionCoords = this.currentLocationOnPlan
                    ? { ...this.currentLocationOnPlan }
                    : null;

                this.handleOffRouteReroutePrompt(links);

                const markerChanged = !previousMarker
                    || previousMarker.x !== this.currentLocationOnPlan.x
                    || previousMarker.y !== this.currentLocationOnPlan.y
                    || previousMarker.floorId !== this.currentLocationOnPlan.floorId;

                const shouldSkipStartFromCurrentLocationRecenter = this.startFromCurrentLocation && !this.navigationActive;
                if (!shouldSkipStartFromCurrentLocationRecenter && !this.manualInstructionSelectionActive && this.currentLocationOnPlan && markerChanged) {
                    this.moveCameraToMarker(this.currentLocationOnPlan);
                }

            }

            if (this.hasPlanCoordinates(this.currentLocationOnPlan)) {
                const feedbackType = this.geolocationFeedbackModal.type;
                if (this.geolocationFeedbackModal.visible && [`loading`, `error`, `out-of-bounds`].includes(feedbackType)) {
                    this.stopWaitingForCurrentLocation({ closeModal: true });
                    if (feedbackType === `loading`) {
                        this.gpsNavMode = true;
                    }
                } else if (this.startFromCurrentLocationLoading) {
                    this.stopWaitingForCurrentLocation();
                }
            }

            if (this.startFromCurrentLocation && !this.navigationActive) {
                this.rerouteFromGpsMarker({ skipRecenter: true });
            }
        },
        moveCameraToMarker(marker, duration = 1000) {
            if (!this.cameraFollowMarker || !marker) {
                return;
            }
            this.recenterVisibleFromMapDrag = false;
            this.zoomToPosition(marker, { delay: 0, zoomFactor: 3, duration });
        },
        navPointUpdated() {
            if (this.startPoint && this.endPoint) {
                this.findClosestPath(this.startPoint, this.endPoint);
            } else {
                if (this.startPoint || this.endPoint) {
                    this.navMode = true;
                }
                if (this.navPath) {
                    this.navPath = null;
                    this.currentInstruction = null;
                }
            }

            if (this.isMobileLayoutOrPreview) {
                this.navigationActive = false;
                this.currentInstruction = null;
            }

            if (this.navPath) {
                this.handleOffRouteReroutePrompt();
            } else {
                this.hideRerouteToast();
            }
        },
        onMobileSuggestionSelected(payload) {
            if (payload?.type) {
                this.onSearchEndpointSelected(payload);
                return;
            }

            if (payload) {
                this.onSearchEndpointSelected({
                    target: `end`,
                    type: `cms-item`,
                    cmsItemId: payload.cmsItem?.id || null
                });
            }
        },
        openQrScanner(source) {
            if (!this.isQrCodeEnabled) {
                return;
            }
            this.qrScanner.source = source;
            this.qrScanner.visible = true;
        },
        handleQrScannerInput(value) {
            if (value) {
                this.qrScanner.visible = true;
                return;
            }
            this.closeQrScanner();
        },
        closeQrScanner() {
            this.qrScanner.visible = false;
            this.qrScanner.source = null;
        },
        handleQrScanResultInput(value) {
            this.qrScanResult.visible = value;
            if (!value) {
                this.qrScanResult.source = null;
                this.qrScanResult.mapItem = null;
            }
        },
        // Open the mobile confirmation modal for a scanned route.
        openScannedRouteModal(qrTarget) {
            this.scannedRouteModal.target = qrTarget;
            this.scannedRouteModal.visible = true;
        },
        // Update the mobile confirmation modal state.
        handleScannedRouteModalInput(value) {
            this.scannedRouteModal.visible = value;
            if (!value) {
                this.scannedRouteModal.target = null;
            }
        },
        // Confirm the scanned route and replace the current navigation.
        confirmScannedRouteNavigation() {
            const qrTarget = this.scannedRouteModal.target;
            this.handleScannedRouteModalInput(false);
            if (!qrTarget) {
                return;
            }

            this.applyScannedRoute(qrTarget);
        },
        // Replace the current map state with the scanned route items.
        applyScannedRoute(qrTarget) {
            this.navigationActive = false;
            this.pendingNavigationStart = false;
            this.navPath = null;
            this.currentInstruction = null;
            this.currentLocation = null;
            this.currentLocationOnPlan = null;
            this.currentLocationOnPath = null;

            this.setEndpoint(`start`, null);
            this.setEndpoint(`end`, null);

            this.selectMapItem(qrTarget.startItem, `start`);
            if (!this.startPoint) {
                return false;
            }

            this.selectMapItem(qrTarget.endItem, `end`);
            if (!this.endPoint) {
                return false;
            }

            this.startFromCurrentLocation = false;
            this.startFromQRCode = true;

            this.selectFloorById(this.startPoint.floorId);

            this.clearSelectedItem();
            return true;
        },
        // Apply the resolved QR target to the current map UI state.
        async onQrScannerDecode(value, resumeScanner, rejectQrScan) {
            const source = this.qrScanner.source;
            const qrTarget = this.map.getQrScanTarget(value);
            if (!qrTarget) {
                rejectQrScan();
                return;
            }

            if (!qrTarget.endItem) {
                const mapItem = qrTarget.startItem;
                this.closeQrScanner();
                if (source === `navigation`) {
                    this.applyQrNavigationReposition(mapItem);
                    return;
                }

                this.applyScannedStartPoint(mapItem, source);
                if (source === `trajectory`) {
                    this.closeTrajectorySelectModal();
                }
                return;
            }

            if (source === `navigation` && this.navigationActive) {
                this.closeQrScanner();
                this.openScannedRouteModal(qrTarget);
                return;
            }

            const routeApplied = this.applyScannedRoute(qrTarget);
            if (!routeApplied) {
                rejectQrScan();
                return;
            }

            this.closeQrScanner();
            if (source === `trajectory`) {
                this.closeTrajectorySelectModal();
            }
        },
        confirmQrScanAction() {
            const mapItem = this.qrScanResult.mapItem;
            this.handleQrScanResultInput(false);

            if (!mapItem) {
                return;
            }

            this.applyQrNavigationReposition(mapItem);
        },
        closeTrajectorySelectModal() {
            this.$refs.mapMobileTrajectoryOverlayRef.selectModal.visible = false;
        },
        applyScannedStartPoint(mapItem, source = `suggestions`) {
            if (!mapItem) {
                return;
            }

            let destination = null;
            if (source === `location`) {
                destination = this.mapItemSelected;
            } else if (source === `trajectory`) {
                destination = this.endPoint;
            }

            this.selectFloorById(mapItem.floorId);

            this.setEndpoint(`start`, mapItem, { fromQRCode: true });
            this.startFromCurrentLocation = false;
            this.startFromQRCode = true;
            if (destination && !this.isSameMapItem(destination, mapItem)) {
                this.setEndpoint(`end`, destination);
                if (this.startPoint && this.endPoint && this.endPoint.cmsItem && this.endPoint.cmsItem.hasMultipleMapItems()) {
                    this.setEndpoint(`end`, this.findClosestItemByCmsItemId(this.startPoint, this.endPoint.cmsItem.id));
                }
            } else if (source === `location` || source === `trajectory`) {
                this.setEndpoint(`end`, null);
            }

            this.clearSelectedItem();
            this.navMode = true;
            this.navigationActive = false;
            this.pendingNavigationStart = false;
            this.navPointUpdated();
            this.zoomToMapItem(mapItem);

        },
        applyQrNavigationReposition(mapItem) {
            if (!mapItem) {
                return;
            }

            this.selectFloorById(mapItem.floorId);

            this.currentLocation = mapItem;
            this.currentLocationMarker = {
                x: mapItem.x,
                y: mapItem.y,
                floorId: mapItem.floorId,
                angle: this.currentLocationMarker ? this.currentLocationMarker.angle : 0,
            };
            this.currentLocationOnPlan = {
                x: mapItem.x,
                y: mapItem.y,
                floorId: mapItem.floorId,
                angle: this.currentLocationOnPlan ? this.currentLocationOnPlan.angle : 0,
            };
            this.syncStepModeMarkerState(this.currentLocationOnPlan);
            this.zoomToMapItem(mapItem);

            const rerouteApplied = this.rerouteFromGpsMarker({ ignoreCooldown: true });
            if (!rerouteApplied) {
                this.alignNavigationInstructionToMapItem(mapItem);
            }
        },
        clearSelectedItem() {
            this.mapItemSelected = null;
            this.selectedItemDistance = null;
            this.selectedItemDuration = null;
        },
        zoomToMapItem(item) {
            if (!this.camera || !item) return;
            this.zoomToPosition(item, { delay: 0, zoomFactor: 1.4, duration: 1000 });
        },
        cancelZoomToPositionAnimation() {
            if (this.zoomToPositionTimer) {
                clearTimeout(this.zoomToPositionTimer);
                this.zoomToPositionTimer = null;
            }
            if (this.zoomToPositionAnimationFrame) {
                cancelAnimationFrame(this.zoomToPositionAnimationFrame);
                this.zoomToPositionAnimationFrame = null;
            }
        },
        zoomToPosition(pos, options = {}) {
            if (!pos || !isFinite(pos.x) || !isFinite(pos.y) || !this.camera || !this.$refs.mapContainer) {
                return;
            }

            let delay = 500;
            let zoomFactor = 3;
            let duration = 1000;

            if (typeof options === `number`) {
                delay = options;
            } else {
                delay = Number.isFinite(options.delay) ? options.delay : delay;
                zoomFactor = Number.isFinite(options.zoomFactor) ? options.zoomFactor : zoomFactor;
                duration = Number.isFinite(options.duration) ? options.duration : duration;
            }

            const startZoomAnimation = () => {
                if (!this.camera || !this.$refs.mapContainer) {
                    return;
                }

                const targetZoom = Math.min(this.camera.maxZoom, Math.max(this.camera.minZoom, this.getDefaultZoom() * zoomFactor));
                const animationDuration = Math.max(1, duration);

                // Single camera animation for pan + zoom to avoid center-then-target feeling.
                this.camera.moveTo(pos.x, pos.y, targetZoom, animationDuration);
            };

            this.cancelZoomToPositionAnimation();
            if (delay > 0) {
                this.zoomToPositionTimer = setTimeout(() => {
                    this.zoomToPositionTimer = null;
                    startZoomAnimation();
                }, delay);
            } else {
                startZoomAnimation();
            }
        },
        updateSelectedItemPreview(item) {
            this.selectedItemDistance = null;
            this.selectedItemDuration = null;
            if (!item || !this.pathfinder) return;
            const start = this.startPointComputed;
            if (!start) return;
            const path = this.pathfinder.findClosestPath(start, item);
            if (!path) return;
            const preview = new NavPath(path);
            this.selectedItemDistance = preview.distance;
            this.selectedItemDuration = preview.duration;
        },
        navigationPreferenceChanged(mode) {
            this.applyNavigationPreference();
        },
        applyNavigationPreference() {
            if (!this.map?.items?.length) return;
            // if (!this.navRuleSnapshot) {
            //     this.navRuleSnapshot = new Map();
            //     this.map.items.forEach((item) => this.navRuleSnapshot.set(item.id, item.navRule));
            // }
            // const accessible = this.navPreference === `accessible`;
            // this.map.items.forEach((item) => {
            //     const baseRule = this.navRuleSnapshot.get(item.id) || item.navRule;
            //     if (accessible && (item.type === `stairs` || item.type === `escalator`)) {
            //         item.navRule = `unavailable`;
            //     } else {
            //         item.navRule = baseRule;
            //     }
            //     if (item.updateNavCost) {
            //         item.updateNavCost();
            //     }
            // });
            this.pathfinder = new Pathfinder(this.map, this.navSettings.preferences);
            if (this.startPointComputed && this.endPoint) {
                this.findClosestPath(this.startPointComputed, this.endPoint);
            }
        },
        async startMobileNavigation() {
            if (!this.navPath) {
                return;
            }

            // Request fullscreen for better navigation experience
            if (document.documentElement.requestFullscreen && this.isMobileDevice && this.isAndroidDevice && !this.isLocal()) {
                document.documentElement.requestFullscreen().catch(err => {
                    console.warn(`Could not enter fullscreen mode:`, err);
                });
            }

            if (this.isDesktopDevice) {
                this.isNavigationStatic = true;
                this.pendingNavigationStart = false;
                this.navigationActive = true;
                this.startNavigation();
                return;
            }

            this.pendingNavigationStart = true;

            const sensorsAlreadyActive = await this.requestNavigationSensorPermissions();
            if (sensorsAlreadyActive) {
                this.activateMobileNavigation();
            }
        },
        async requestNavigationSensorPermissions() {
            const needsMotion = this.mapIntegrations.isCompassEnabled() || this.mapIntegrations.isPedometerEnabled();
            const needsGeolocation = this.mapIntegrations.isGpsEnabled();

            // Check if required sensors are already actively running
            const motionRunning = !needsMotion || this.motion.isListening;
            const geoRunning = !needsGeolocation || this.sensors.geolocationWatchId != null;

            if (motionRunning && geoRunning) {
                // All required sensors are already active — signal the caller to proceed
                return true;
            }

            // Sensors not running — query browser permission state to decide approach
            const { allGranted, states } = await SensorPermissionHelper.checkNavigationPermissions({
                needsMotion,
                needsGeolocation,
                motionActive: this.motion.isListening,
            });

            this.sensorsInitalStates = {
                geolocation: states.geolocation,
                accelerometer: states.accelerometer,
            };

            if (allGranted) {
                // Permissions still granted but sensors not wired (e.g. after page reload)
                // Re-start silently with skipInitialPosition to avoid blocking GPS fix delay
                console.log(`[Navigation] Permissions already granted but sensors not active. Starting navigation sensors.`);
                await this.requestPermissionsForMobileNavigation({ skipInitialPosition: !this.startFromCurrentLocation });
                return false;
            }

            // Some permissions not yet granted or denied — show the modal
            await this.showPermissionModalRequest();
            return false;
        },
        async requestGeolocationPermission(setStartPoint = false) {
            this.startFromCurrentLocationLoading = true;
            this.startFromCurrentLocationDisabled = false;

            try {
                const { geolocation } = await SensorPermissionHelper.queryPermissionStates();
                if (geolocation !== `granted` && geolocation !== `prompt`) {
                    console.log(`[Navigation] Geolocation permission denied.`);
                    this.isNavigationStatic = true;
                    return;
                }

                await this.getCurrentGeolocationPosition();
                if (setStartPoint) {
                    await this.onTrajectoryEndpointSelected({
                        target: `start`,
                        type: `current-location`
                    });
                }
            } catch (error) {
                console.warn(`[Navigation] Failed to use current location as a start point`, error);
            } finally {
                this.startFromCurrentLocationLoading = false;
            }
        },
        onSpecialWalkthroughStep() {
            const mapMobileAssistant = this.$refs.mapMobileAssistant;
            if (!mapMobileAssistant) return;
            mapMobileAssistant.setIndex(mapMobileAssistant.index + 1);
        },
        /**
         * Select the map item and switch automatically to navMode if necessary
         * @param item {MapItem}
         * @param type {String} auto|start|end
         */
        selectMapItem(item, type = `auto`) {
            if (this.pendingMapSelectionTarget) {
                if (!item) {
                    return;
                }
                const selectionTarget = this.pendingMapSelectionTarget;
                this.pendingMapSelectionTarget = null;
                this.setEndpoint(selectionTarget, item);

                if (selectionTarget === `start` && this.startPoint && this.endPoint?.cmsItem?.hasMultipleMapItems()) {
                    this.setEndpoint(`end`, this.findClosestItemByCmsItemId(this.startPoint, this.endPoint.cmsItem.id));
                }
                if (selectionTarget === `end` && this.endPoint && this.startPoint?.cmsItem?.hasMultipleMapItems()) {
                    this.setEndpoint(`start`, this.findClosestItemByCmsItemId(this.endPoint, this.startPoint.cmsItem.id));
                }

                this.navMode = true;
                this.navPointUpdated();
                return;
            }

            // prevent map click selection when in nav path to avoid creating unwanted paths
            if (this.navPath && item) {
                // switch floor when clicking on relevant items
                if ((item.type === `elevator` || item.type === `stairs`) && this.navPath.startFloor.id !== this.navPath.endFloor.id) {
                    if (this.floorSelected.id === this.navPath.startFloor.id) {
                        this.selectFloor(this.navPath.endFloor);
                    } else {
                        this.selectFloor(this.navPath.startFloor);
                    }
                }

                return;
            }

            if (!item) {
                return this.unselectMapItem();
            }

            if (this.currentLocation && this.isKioskDevice) {
                // Kiosk: directly start navigation from current location to selected item
                this.setEndpoint(`end`, item);
                this.findClosestPath(this.currentLocation, this.endPoint);
                this.navMode = true;
                return;
            } else if (this.currentLocation && type === `auto` && !this.navMode) {
                this.mapItemSelected = item;
                if (this.isMobileLayoutOrPreview) {
                    this.updateSelectedItemPreview(item);
                    this.zoomToMapItem(item);
                }
            } else {
                if (this.navMode || type !== `auto`) {
                    if (!this.startPoint && type !== `end`) {
                        if (this.endPoint === item) {
                            swal({
                                title: this.$t(`map.areSureQuestion`),
                                text: this.$t(`map.differentDestination`),
                                icon: `warning`
                            });
                            return;
                        }
                        this.setEndpoint(`start`, item);

                        // check if there is a closest map item
                        if (this.startPoint && this.endPoint?.cmsItem?.hasMultipleMapItems()) {
                            this.setEndpoint(`end`, this.findClosestItemByCmsItemId(this.startPoint, this.endPoint.cmsItem.id));
                        }

                    } else {
                        if (this.startPoint === item) {
                            swal({
                                title: this.$t(`map.areSureQuestion`),
                                text: this.$t(`map.differentDestination`),
                                icon: `warning`
                            });
                            return;
                        }
                        this.setEndpoint(`end`, item);

                        // check if there is a closest map item
                        if (this.endPoint && this.startPoint?.cmsItem?.hasMultipleMapItems()) {
                            this.setEndpoint(`start`, this.findClosestItemByCmsItemId(this.endPoint, this.startPoint.cmsItem.id));
                        }
                    }
                    if (this.startPoint && this.endPoint) {
                        this.findClosestPath(this.startPoint, this.endPoint);
                    }
                    this.navMode = true;
                } else {
                    this.mapItemSelected = item;
                    if (this.isMobileLayoutOrPreview && !this.isKioskDevice) {
                        this.updateSelectedItemPreview(item);
                        this.zoomToMapItem(item);
                    }
                }
            }
        },
        // Pick the nearest shared-link item for a permalink.
        findClosestItemByPermalink(permalink) {
            let items = this.map.getItemsByPermalink(permalink);
            return this.pathfinder.findClosestItem(this.startPointComputed, items);
        },
        findClosestItemByCmsItemId(startPoint, cmsItemId) {
            if (!cmsItemId || !this.map || !this.pathfinder) {
                return null;
            }
            let items = this.map.getItemsByCmsItemId(cmsItemId);
            const strictCmsItems = items.filter((item) => item?.cmsItem?.id === cmsItemId);
            if (strictCmsItems.length > 0) {
                items = strictCmsItems;
            }
            return this.pathfinder.findClosestItem(startPoint, items);
        },
        findClosestItemByType(itemType) {
            if (!this.startPointComputed) {
                swal({
                    title: this.$t(`map.whereAreYouLocated`),
                    text: this.$t(`map.selectWhereToStartFrom`),
                    icon: `warning`
                })
                return;
            }
            const items = this.map.getItemsByType(itemType);
            let closestMapPoint = this.pathfinder.findClosestItem(this.startPointComputed, items);
            if (!closestMapPoint) {
                swal({
                    title: this.$t(`map.couldNotFindPath`),
                    icon: `warning`
                })
                this.navPath = null;
            } else {
                this.selectMapItem(closestMapPoint);
            }
        },
        findClosestPath(start, end) {
            let closestPath = this.pathfinder.findClosestPath(start, end);
            if (!closestPath) {
                swal({
                    title: this.$t(`map.couldNotFindPath`),
                    icon: `warning`
                })
                this.applyNavigationPath(null);
            } else {
                this.applyNavigationPath(closestPath, { historyStart: start, historyEnd: end });
            }
        },
        getNextFloorPath() {
            if (!this.navPath.floorPaths.length) {
                return null;
            }

            if (this.floorPath === null) {
                return this.navPath.floorPaths[0];
            } else {
                const index = this.navPath.floorPaths.indexOf(this.floorPath);
                if (this.navPath.floorPaths.length > index + 1) {
                    return this.navPath.floorPaths[index + 1];
                }
            }
            return null;
        },
        nextFloorPath(fitCameraToPath = true) {
            const floorPath = this.getNextFloorPath();
            if (!floorPath) {
                return;
            }

            this.selectFloor(floorPath.floor, fitCameraToPath);
            const nextFloorPath = this.getNextFloorPath();
            if (nextFloorPath) {
                if (this.isMobileLayoutOrPreview) {
                    return;
                }
                this.animationTimer = setTimeout(() => {
                    if ((this.currentInstruction && !this.isKioskDevice) ||
                        (this.currentInstruction?.index > 0 && this.isKioskDevice))
                        return;

                    this.floorModal.visible = true;
                    this.floorModal.direction = floorPath.floor.level < nextFloorPath.floor.level ? `up` : `down`;
                    this.floorModal.floorName = this.$tr(nextFloorPath.floor.getName());

                    setTimeout(() => {
                        this.floorModal.visible = false;
                        this.nextFloorPath();
                    }, 4000);
                }, (this.floorPath.duration + 2) * 1000);
            }
        },
        getCameraPosition() {
            if (!this.camera) {
                return null;
            }
            if (typeof this.camera.getPosition === `function`) {
                const point = this.camera.getPosition();
                if (point && isFinite(point.x) && isFinite(point.y)) {
                    return { x: point.x, y: point.y };
                }
            }

            const center = this.camera.center;
            if (center && isFinite(center.x) && isFinite(center.y)) {
                return { x: center.x, y: center.y };
            }

            const viewBox = this.camera.syncViewBox || this.camera.viewBox;
            if (viewBox && isFinite(viewBox.minX) && isFinite(viewBox.minY) && isFinite(viewBox.width) && isFinite(viewBox.height)) {
                return {
                    x: viewBox.minX + viewBox.width / 2,
                    y: viewBox.minY + viewBox.height / 2,
                };
            }

            return null;
        },
        fitCameraToNavPath() {
            if (this.floorPath && this.camera) {
                this.camera.smoothlyContainPoints(this.floorPath.items, 1500);
                setTimeout(() => {
                    const cameraPosition = this.getCameraPosition();
                    if (cameraPosition) {
                        this.lastCameraPosition = {
                            x: cameraPosition.x,
                            y: cameraPosition.y,
                        };
                    }
                }, 1600);
            }
        },
        resetAssistantArMode() {
            this.$refs.mapMobileAssistant.resetArMode();
        },
        onMapModeActivated() {
            this.resetAssistantArMode();
            this.splitViewActive = false;
            this.disableMap = false;
        },
        onSplitModeActivated() {
            this.resetAssistantArMode();
            this.splitViewActive = true;
            this.disableMap = false;
        },
        resetNavigationInstructionToStart() {
            if (!this.navPath?.instructions?.length) {
                return;
            }

            const firstInstruction = this.navPath.instructions[0];
            if (!firstInstruction) {
                return;
            }

            this.selectInstruction(firstInstruction, `auto-start`);
            this.syncAssistantInstructionIndex(firstInstruction);
        },
        startNavigation() {
            // disable merging instructions when GPS is enabled to get all 360 images
            if (this.navSettings.gps && this.mapIntegrations.isImages360Enabled()) {
                this.navPath = new NavPath(this.navPath.path, false);
            }

            this.persistLatestEndpoints();

            this.resetNavigationInstructionToStart();
            this.fitCameraToNavPath();
            NetworkV2.put(`/api/public/analytics/routes`, {
                step: `start`,
                id: this.analyticsRoute.id,
                navSettings: this.navSettings
            })
                .catch((e) => console.error(e));
        },
        endNavigation() {
            this.closeNavigation();
            // when pressing terminate trajectory
            NetworkV2.put(`/api/public/analytics/routes`, {
                step: `end`,
                id: this.analyticsRoute.id,
            }).catch((e) => console.error(e));
        },
        backToTrajectory() {
            // Go back to trajectory overlay with start/end points kept, but reset navigation state
            this.closeNavigation();
            this.navigationActive = false;
            this.pendingNavigationStart = false;

            // Clear navigation state but keep start/end points
            this.navPath = null;
            this.currentInstruction = null;

            // Clear current location/position tracking
            this.currentLocation = null;
            this.currentLocationOnPlan = null;
            this.currentLocationOnPath = null;

            // Recalculate the path with the existing start/end points
            this.$nextTick(() => {
                this.navPointUpdated();
            });
        },
        resetNavigation() {
            this.persistLatestEndpoints();
            this.closeNavigation();

            // after rating a trajectory
            localStorage.removeItem(`qrCode`);
            this.currentLocation = null;

            this.unselectMapItem();
        },
        closeNavigation() {
            this.disableMap = false;
            this.splitViewActive = false;
            this.hideRerouteToast();
            this.cleanupVirtualDeviceNodes();
            this.resetNavigationMode();
        },
        resetNavigationMode() {
            this.gpsNavMode = false;
            this.currentLocationOnPath = null;
            this.manualInstructionSelectionActive = false;
            this.markerEnterInRoute = false;
            // this.stepByStepMode = false;
        },
        unselectMapItem() {
            this.navPath = null;
            this.currentInstruction = null;
            this.startPoint = null;
            this.endPoint = null;
            this.startFromCurrentLocation = false;
            this.startFromQRCode = false;
            this.pendingMapSelectionTarget = null;
            this.$store.commit(`mapEditor/clearEndpoints`);
            this.navMode = false;
            this.navigationActive = false;
            this.pendingNavigationStart = false;
            this.permissionShowcaseModalVisible = false;
            this.selectedItemDistance = null;
            this.selectedItemDuration = null;
            this.hideRerouteToast();
            window.history.pushState({}, ``, `${this.$route.path}`);

            if (this.animationTimer) {
                clearTimeout(this.animationTimer);
            }
            this.cleanupVirtualDeviceNodes();
        },
        selectInstruction(mapInstruction, source, { skipRecenter = false } = {}) {
            if (!mapInstruction) {
                this.currentInstruction = null;
                return;
            }

            const instructionItem = mapInstruction.currentItem;
            const hasIntermediateInstruction = mapInstruction.index > 0;
            const isManualSelection = source === `manual`;
            const liveMarkerMatchesInstruction = this.currentLocationOnPlan
                && this.currentLocationOnPlan.x === instructionItem.x
                && this.currentLocationOnPlan.y === instructionItem.y
                && this.currentLocationOnPlan.floorId === instructionItem.floorId;
            const shouldPreviewGpsInstruction = isManualSelection
                && this.gpsNavMode
                && this.shouldShowLiveGpsMarkerGroup
                && this.hasPlanCoordinates(this.currentLocationOnPlan)
                && !liveMarkerMatchesInstruction;

            this.currentInstruction = mapInstruction;
            this.manualInstructionSelectionActive = shouldPreviewGpsInstruction;
            if (this.currentInstruction && this.currentInstruction.fromFloor !== this.floorSelected) {
                this.selectFloor(this.currentInstruction.fromFloor, !skipRecenter);
            }

            if ((skipRecenter || this.rerouteKeepMarker) && this.currentLocationOnPlan) {
                this.rerouteKeepMarker = false;
            } else if (this.gpsNavMode) {
                if (shouldPreviewGpsInstruction) {
                    this.currentLocationOnPath = {
                        x: instructionItem.x,
                        y: instructionItem.y,
                        floorId: instructionItem.floorId
                    };
                    this.moveCameraToMarker(this.currentLocationOnPath);
                } else if (this.currentLocationOnPath && this.manualInstructionSelectionActive === false) {
                    this.currentLocationOnPath = null;
                }
            } else if (hasIntermediateInstruction) {
                const shouldKeepStepMarkerFromPlan =
                    this.stepByStepMode
                    && this.hasPlanCoordinates(this.currentLocationOnPlan)
                    && !isManualSelection;
                if (shouldKeepStepMarkerFromPlan) {
                    this.currentLocationOnPath = {
                        x: this.currentLocationOnPlan.x,
                        y: this.currentLocationOnPlan.y,
                        floorId: this.currentLocationOnPlan.floorId || instructionItem.floorId
                    };
                } else {
                    this.currentLocationOnPath = {
                        x: instructionItem.x,
                        y: instructionItem.y,
                        floorId: instructionItem.floorId
                    };
                }

                if (isManualSelection) {
                    this.moveCameraToMarker(this.currentLocationOnPath);
                }
            } else {
                this.currentLocationOnPath = {
                    x: instructionItem.x,
                    y: instructionItem.y,
                    floorId: instructionItem.floorId
                };
                this.moveCameraToMarker(this.currentLocationOnPath);
            }

            this.lastDistanceBetweenUserPositionAndNextPoint = this.distanceBetweenUserPositionAndNextPoint;

            const lastInstruction = this.navPath.instructions.find((instr) => instr.nextInstruction === null);

            if ((mapInstruction.currentItem.id === lastInstruction.currentItem.id) && this.gpsNavMode) {
                const assistant = this.$refs.mapMobileAssistant;
                if (assistant && !assistant.arrivalModalVisible && !assistant.feedbackModalVisible && !assistant.arrivalSeen) {
                    assistant.showArrivalModal();
                }
            }
        },
        async resetCameraAndClearPreview() {
            this.cameraFollowMarker = true;
            this.automaticChangeFloor = true;

            const currentMarkerBeforeRefresh = this.currentLocationOnPlan || this.currentLocationOnPath;
            if (this.mapIntegrations.isGpsEnabled() && (this.isCurrentLocationOnSite || !currentMarkerBeforeRefresh)) {
                await this.getCurrentGeolocationPosition();
            }

            // If we are previewing the path, reset the camera to the current GPS position.
            if (this.manualInstructionSelectionActive) {
                this.manualInstructionSelectionActive = false;
            }

            // Get the current target
            const currentMarker = this.currentLocationOnPlan || this.currentLocationOnPath;
            const selectedPoint = this.startPoint || this.endPoint || this.mapItemSelected;

            const floorPoint = currentMarker || selectedPoint;
            const floorTarget = floorPoint?.floorId ? this.map.getFloorById(floorPoint.floorId) : null;
            const shouldChangeFloor = floorTarget && floorTarget.id !== this.floorSelected.id;

            if (currentMarker) {
                // If we have a current marker, move the camera to it
                if (shouldChangeFloor) {
                    this.selectFloor(floorTarget, false);
                }
                this.moveCameraToMarker(currentMarker);

            } else if (this.floorPath) {
                // If we have a navigation path but no marker, fit the camera to the path
                if (shouldChangeFloor) {
                    this.selectFloor(floorTarget);
                } else {
                    this.fitCameraToNavPath();
                }

            } else if (selectedPoint) {
                // If we have a selected map item, move the camera to it
                if (shouldChangeFloor) {
                    this.selectFloor(floorTarget);
                }
                this.moveCameraToMarker(selectedPoint);

            } else {
                // Default camera reset
                this.resetCamera();
            }
        },
        selectMapItemById(itemId) {
            this.selectMapItem(this.map.getItemById(itemId));
        },
        directorySwiped(direction) {
            if (!this.isWheelchairMode) return;

            if (direction === `top`) {
                this.wheelchairExtended = true;
            } else if (direction === `bottom`) {
                this.wheelchairExtended = false;
            }
        },
        selectService(type) {
            this.findClosestItemByType(type);
        },
        latLngToLocalXY(lat, lng, originLat, originLng) {
            const R = 6378137;
            const dLat = ((lat - originLat) * Math.PI) / 180;
            const dLng = ((lng - originLng) * Math.PI) / 180;
            const meanLat = (((lat + originLat) / 2) * Math.PI) / 180;

            const x = dLng * R * Math.cos(meanLat); // East-West
            const y = dLat * R; // North-South

            return { x, y };
        },
        localXYToLatLng(x, y, originLat, originLng) {
            const R = 6378137;

            // First recover latitude from North-South metric offset.
            const dLat = y / R;
            const lat = originLat + (dLat * 180) / Math.PI;

            // Then recover longitude using the same mean-lat approximation
            // used in latLngToLocalXY.
            const meanLat = (((lat + originLat) / 2) * Math.PI) / 180;
            const cosMeanLat = Math.cos(meanLat);
            const safeCos = Math.abs(cosMeanLat) < 1e-12 ? (cosMeanLat < 0 ? -1e-12 : 1e-12) : cosMeanLat;

            const dLng = x / (R * safeCos);
            const lng = originLng + (dLng * 180) / Math.PI;

            return { lat, lng };
        },
        createImageTransformConfig(meta, imageWidth, imageHeight) {
            const { topLeftCorner, topRightCorner, bottomLeftCorner } =
                meta.bounds;

            const originLat = meta.center.lat;
            const originLng = meta.center.lng;

            const TL = this.latLngToLocalXY(
                topLeftCorner.lat,
                topLeftCorner.lng,
                originLat,
                originLng
            );
            const TR = this.latLngToLocalXY(
                topRightCorner.lat,
                topRightCorner.lng,
                originLat,
                originLng
            );
            const BL = this.latLngToLocalXY(
                bottomLeftCorner.lat,
                bottomLeftCorner.lng,
                originLat,
                originLng
            );

            const xAxis = { x: TR.x - TL.x, y: TR.y - TL.y };
            const yAxis = { x: BL.x - TL.x, y: BL.y - TL.y };

            const det = xAxis.x * yAxis.y - xAxis.y * yAxis.x;

            if (Math.abs(det) < 1e-12) {
                throw new Error(`Les coins fournis sont dégénérés (det ~ 0)`);
            }

            return {
                originLat,
                originLng,
                TL,
                xAxis,
                yAxis,
                det,
                imageWidth,
                imageHeight,
            };
        },
        latLngToImageXY(lat, lng, config) {
            const {
                originLat,
                originLng,
                TL,
                xAxis,
                yAxis,
                det,
                imageWidth,
                imageHeight,
            } = config;

            const P = this.latLngToLocalXY(lat, lng, originLat, originLng);

            // vecteur TL -> P
            const v = { x: P.x - TL.x, y: P.y - TL.y };

            // v = a * xAxis + b * yAxis  (on résout le système)
            const a = (v.x * yAxis.y - v.y * yAxis.x) / det; // horizontal [0..1]
            const b = (xAxis.x * v.y - xAxis.y * v.x) / det; // vertical   [0..1]

            const x = a * imageWidth;
            const y = b * imageHeight;

            return { x, y, a, b };
        },
        imageXYToLatLng(x, y, config) {
            const {
                originLat,
                originLng,
                TL,
                xAxis,
                yAxis,
                imageWidth,
                imageHeight,
            } = config;

            const a = x / imageWidth;
            const b = y / imageHeight;

            // P = TL + a*xAxis + b*yAxis
            const localX = TL.x + a * xAxis.x + b * yAxis.x;
            const localY = TL.y + a * xAxis.y + b * yAxis.y;

            const gps = this.localXYToLatLng(localX, localY, originLat, originLng);
            return {
                lat: gps.lat,
                lng: gps.lng,
                a,
                b,
                localX,
                localY
            };
        },
        /*
        When we start from current position
         */
        createVirtualDeviceRoute(destMapItem, devicePos, reverse = false) {
            if (!this.map || !this.map.items || !devicePos || !destMapItem) return null;
            this.cleanupVirtualDeviceNodes();

            const tmpId = `device-${Date.now()}`;
            const tmpData = {
                id: tmpId,
                name: `Device position`,
                type: `nav-point`,
                x: devicePos.x,
                y: devicePos.y,
                floorId: devicePos.floorId,
            };

            const tmpItem = new MapItem(tmpData, this.map);
            tmpItem.index = `n${this.map.items.length}`;
            this.map.items.push(tmpItem);
            this.tempDeviceNode = tmpItem;

            const neighbors = this.map.items
                .filter((i) => i.id !== tmpItem.id && i.floorId === tmpItem.floorId && i.links && i.links.length > 0)
                .map((i) => ({ item: i, dist: Math.pow(i.x - tmpItem.x, 2) + Math.pow(i.y - tmpItem.y, 2) }))
                .sort((a, b) => a.dist - b.dist)
                .slice(0, 3)
                .map((n) => n.item);

            for (let neighbor of neighbors) {
                const link = new MapLink({}, this.map);
                this.map.links.push(link);
                link.linkItems(tmpItem, neighbor);
                this.tempDeviceLinks.push(link);
            }

            const pf = new Pathfinder(this.map, this.navSettings.preferences);
            const path = reverse ? pf.findClosestPath(destMapItem, tmpItem) : pf.findClosestPath(tmpItem, destMapItem);
            if (!path) {
                this.cleanupVirtualDeviceNodes();
                swal({ title: this.tr(`Could not find path|Chemin non trouvé`), icon: `warning` });
                return null;
            }

            this.navPath = new NavPath(path);
            this.navMode = true;

            if (!reverse) {
                this.startPoint = tmpItem;
                this.endPoint = destMapItem;
                this.startFromCurrentLocation = true;
                this.startFromQRCode = false;
            } else {
                this.startPoint = destMapItem;
                this.endPoint = tmpItem;
                this.startFromCurrentLocation = false;
                this.startFromQRCode = false;
            }
            this.syncEndpointIdsToStore();

            this.analyticsRoute = {
                sessionId: SessionTracker.session.id,
                websiteId: this.cmsWebsite.id,
                dealerid: this.dealerid,
                duration: this.navPath.duration,
                distance: this.navPath.distance,
                fromGps: true
            };
            if (!reverse) {
                this.analyticsRoute.start = {
                    id: tmpItem.id,
                    name: tmpItem.name || this.tr(`Your device|Votre appareil`),
                    type: tmpItem.type,
                    floor: { id: tmpItem.floorId, name: this.floorSelected ? this.floorSelected.getName() : null },
                };
                this.analyticsRoute.destination = {
                    id: destMapItem.id,
                    name: destMapItem.name || destMapItem.getName(),
                    type: destMapItem.type,
                    floor: { id: destMapItem.floorId, name: this.map.getFloorById(destMapItem.floorId).getName() },
                };
            } else {
                this.analyticsRoute.start = {
                    id: destMapItem.id,
                    name: destMapItem.name || destMapItem.getName(),
                    type: destMapItem.type,
                    floor: { id: destMapItem.floorId, name: this.map.getFloorById(destMapItem.floorId).getName() },
                };
                this.analyticsRoute.destination = {
                    id: tmpItem.id,
                    name: tmpItem.name || this.tr(`Your device|Votre appareil`),
                    type: tmpItem.type,
                    floor: { id: tmpItem.floorId, name: this.floorSelected ? this.floorSelected.getName() : null },
                };
            }
            this.analyticsRoute.navPoints = this.navPath.items.map((i) => ({ id: i.id, type: i.type }));

            NetworkV2.post(`/api/public/analytics/routes/add`, this.analyticsRoute)
                .then((resp) => { this.analyticsRoute.id = resp.data.id; });

            return this.navPath;
        },

        cleanupVirtualDeviceNodes() {
            if (this.tempDeviceLinks && this.tempDeviceLinks.length) {
                for (let link of this.tempDeviceLinks) {
                    try {
                        link.beforeDelete();
                    } catch (e) {
                        console.debug(`Error while deleting temporary link`, e);
                    }
                }
                this.map.links = this.map.links.filter((l) => !this.tempDeviceLinks.includes(l));
                this.tempDeviceLinks = [];
            }
            if (this.tempDeviceNode) {
                this.map.items = this.map.items.filter((i) => i.id !== this.tempDeviceNode.id);
                this.tempDeviceNode = null;
            }
        },

        // Find the nearest map item to a given coordinate on the plan
        findClosestMapItemToPlanCoord(x, y, floorId = null) {
            if (!this.map || !this.map.items) return null;
            let candidates = this.map.items;
            if (floorId) {
                candidates = candidates.filter((i) => i.isOnFloor(floorId));
            }
            // prefer items connected to the graph (with links)
            let connected = candidates.filter((i) => i.links && i.links.length > 0);
            if (connected.length) {
                candidates = connected;
            }
            if (!candidates || candidates.length === 0) return null;
            let nearest = null;
            let minDist = Number.POSITIVE_INFINITY;
            for (let item of candidates) {
                if (typeof item.x !== `number` || typeof item.y !== `number`) continue;
                const dx = item.x - x;
                const dy = item.y - y;
                const dist2 = dx * dx + dy * dy;
                if (dist2 < minDist) {
                    minDist = dist2;
                    nearest = item;
                }
            }
            return nearest;
        },
        shareLink() {
            const itinerary = `${this.navPath.start.getName()} ${this.$t(`map.assistant.to`)} ${this.navPath.end.getName()}`;
            const metaTitle = itinerary + ` - ` + this.$tr(this.cmsWebsite.meta.title);
            const metaDescription = this.$tr(this.cmsWebsite.meta.description);
            const shareUrl = `${this.baseSharingUrl}&medium=sharing&source=user`;

            if (navigator.clipboard && this.isDesktopDevice) {
                navigator.clipboard.writeText(shareUrl)
                    .then(() => {
                        this.$bvToast.toast(this.$t(`map.assistant.linkCopied`), {
                            title: this.$t(`map.assistant.linkCopiedTitle`),
                            variant: `info`,
                            solid: true,
                            toaster: `b-toaster-bottom-right`
                        });
                    });
            }

            if (navigator.share) {
                navigator.share({
                    title: metaTitle,
                    text: metaDescription,
                    url: shareUrl
                }).then(() => {
                    console.log(`Link shared`);
                }).catch(console.error);
            }

            NetworkV2.post(`/api/public/analytics/sharing/add`, {
                ...this.analyticsRoute,
                language: getLocale(),
                url: shareUrl
            });
        }
    },
};
</script>

<style lang="scss" scoped>

.cms-page-map {
    --map-assistant-width: 400px;
    --map-container-left-padding: 400px;
    --map-top-padding: 0px;

    --map-mobile-trajectory-drawer-snap-point: 110px;
    --map-mobile-navigation-drawer-snap-point: 120px;

    --map-mobile-trajectory-header-height: 106px;
    --map-mobile-navigation-header-height: 111px;

    --map-mobile-trajectory-offset: calc(var(--map-mobile-trajectory-drawer-snap-point) + var(--map-mobile-trajectory-header-height));
    --map-mobile-navigation-offset: calc(var(--map-mobile-navigation-drawer-snap-point) + var(--map-mobile-navigation-header-height));

    // Computed offsets based on the current state (trajectory vs navigation)
    --map-mobile-overlay-top-offset: 0px;
    --map-mobile-overlay-bottom-offset: 0px;

    &.navigation-active-split-view {
        --map-mobile-trajectory-header-height: 55px;
        --map-mobile-navigation-header-height: 42px;
    }
}

.compass-container {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: flex-start;

    .compass {
        margin-top: 20px;
        margin-left: 20px;
        transition: 0.2s;
        width: 50px;
        height: 50px;
    }

}

.warning-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;

    .alert {
        background-color: rgba(172, 44, 44, 0.4);
        backdrop-filter: blur(5px);
        border: 1px solid #fa4747;
        margin: 30px;
        color: white;
    }
}

.showcase-animation {
    position: relative;
    z-index: 0;
    transform: scaleX(-1);
    width: 100%;
    height: 190px;
    margin-bottom: -86px;

    display: flex;
    align-items: center;
    justify-content: center;

    // overflow: hidden;

    video {
        height: auto;
        width: 100%;
        max-width: 450px;
        object-fit: cover;
    }
}

.showcase-content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
    padding: 8px 0;

    &.showcase-content-animated {
        .showcase-title {
            padding-right: 64px;
        }
    }

    .showcase-body {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        text-align: left;

        .showcase-label {
            // position: absolute;
            // left: 12px;
            // top: 12px;
            font-size: 11pt;
            color: var(--color-accent);
            text-transform: uppercase;
        }

        .showcase-title {
            font-size: 16pt;
            font-weight: 600;
        }

        .showcase-description {
            font-size: 12pt;
            color: #768293;
        }

        .showcase-badge-wrapper {
            // position: absolute;
            // left: 12px;
            // top: 12px;

            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-block: 8px 16px;
        }

        .showcase-badge {
            width: fit-content;
            padding: 4px 12px;
            font-size: 9pt;
            border-radius: 999px;
            color: var(--color-accent);
            border: 1px solid var(--color-light);
        }

        .showcase-icon-text {
            display: flex;
            gap: 10px;
            align-items: center;


            .showcase-icon {
                background: rgba(0, 95, 229, 0.04);
                border: solid 1px rgba(0, 95, 229, 0.24);
                border-radius: 0.75rem;
                padding: 3px;
                margin:  auto;
                min-width: 30px;
                min-height: 30px;
                text-align: center;
                i {
                    font-size: 11pt;
                    color: var(--color-accent);
                }
            }

            .showcase-text {
                flex: 1;
                padding-top: 2px;

                p {
                    font-size: 10pt;
                    color: var(--color-dark);
                    line-height: 1.375;

                    span {
                        color:  var(--color-accent);
                        font-weight: 600;
                    }
                }

            }
        }
    }
}
.layout-mobile {
    .cms-page-map {
        --map-assistant-width: 100vw;
        --map-container-left-padding: 0px;
    }
}

.layout-kiosk {
    .cms-page-map {
        //--kiosk-map-height: 1600px;
        --kiosk-map-height: 850px;
        --map-container-left-padding: 0px;

        //&.assistant-visible {
        //    --kiosk-map-height: 750px;
        //}
    }
}

.accessibility-wheelchair {
    .cms-page-map {
        --wheelchair-reduced-height: 65px;
        --kiosk-map-height: calc(100vh - (var(--wheelchair-top-margin) + var(--wheelchair-reduced-height)));
    }
}

.assistant-placeholder {
    position: fixed;
    width: var(--map-assistant-width);
    top: var(--page-padding-top);
    height: calc(var(--app-height) - var(--menu-height));
    left: var(--page-padding-left);
    background: #fafafa;
    border-right: 1px solid #ccc;
    display: flex;
    align-items: center;
    justify-content: center;
}

.directory {
    background: #eee;
    color: #333;
    height: calc(100vh - var(--kiosk-map-height)) !important;
    display: flex;
    flex-direction: column;

    .this-floor {
        padding: 30px;
        flex: 2;
        overflow: hidden auto;

        .floor-items {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            grid-gap: 5px 30px;

            .floor-item {
                background: #fff;
                text-align: left;
                padding: 10px 20px;
                border-radius: var(--border-radius);
                //border-bottom: 1px solid #ccc;
            }
        }
    }

    .services-container {
        background: #eee;
        color: #333;
        flex: 1;
        min-height: 509px;
        max-height: 509px;

        .services {
            display: flex;
            justify-items: stretch;
        }

        .service {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 190px;

            .icon-container {
                width: 70px;
                height: 70px;
                border-radius: 10px;
                background: #095797;
                color: white;
                //border: 3px solid white;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                margin-bottom: 10px;
            }

            i {
                font-size: 40px;
            }

            img {
                width: 50px;
            }

            .name {
                text-transform: uppercase;
                letter-spacing: 1px;
            }
        }
    }
}

.accessibility-wheelchair {
    .map-overlay {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: calc(100% - var(--menu-width));
        background: rgba(black, 0.4);
    }

    .directory {
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
        height: var(--wheelchair-reduced-height) !important;
        overflow: hidden;
        bottom: 0;
        position: absolute;
        left: calc(var(--map-container-left-padding) + 10px);
        width: calc(100% - var(--menu-width) - 20px);

        .divider {
            border-radius: inherit;
            height: var(--wheelchair-reduced-height);
        }

        &.wheelchair-extended {
            top: 70px;
            height: auto !important;
            z-index: 10;
            box-shadow: 0 -5px 15px rgba(black, 0.3);
        }
    }

    .services-container {
        background: #eee;
        color: #333;
        flex: 1;
        min-height: auto;
        max-height: initial;
    }
}

.change-floor-content {
    --elevator-img-size: 600px;
    --elevator-icon-size: 256px;
    --stair-img-size: 250px;

    .elevator-image {
        width: var(--elevator-img-size);
        max-width: 100%;
    }

    .floor-direction {
        position: absolute;
        left: calc(50% - var(--elevator-icon-size) / 2);
        top: calc(var(--elevator-img-size) / 2 - var(--elevator-icon-size) / 2);
        background: var(--color-accent);
        border-radius: 50%;
        width: var(--elevator-icon-size);
        height: var(--elevator-icon-size);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: calc(var(--elevator-icon-size) / 2.1);
        color: white;

        &.direction-up {
            animation: floor-up 2s infinite;
        }

        &.direction-down {
            animation: floor-down 2s infinite;
        }
    }

    .title {
        font-size: 26pt;
        font-weight: 500;
    }

    @keyframes floor-up {
        0% {
            opacity: 0;
            transform: translatey(25px);
        }

        10% {
            opacity: 0.8;
            transform: translatey(0px);
        }

        50% {
            opacity: 0.8;
            transform: translatey(0px);
        }

        100% {
            opacity: 0;
            transform: translatey(-100px);
        }
    }

    @keyframes floor-down {
        0% {
            opacity: 0;
            transform: translatey(-25px);
        }

        25% {
            opacity: 0.8;
            transform: translatey(0px);
        }

        50% {
            opacity: 0.8;
            transform: translatey(0px);
        }

        100% {
            opacity: 0;
            transform: translatey(100px);
        }
    }
}

.layout-mobile {
    .cms-page-map {
        --map-top-padding: 80px;
    }

    .cms-page-map.nav-mode,
    .cms-page-map.assistant-visible:not(.navigation-active) {
        --map-mobile-overlay-top-offset: var(--map-mobile-trajectory-header-height);
        --map-mobile-overlay-bottom-offset: calc(var(--map-mobile-trajectory-offset) - var(--map-mobile-overlay-top-offset));
    }

    .cms-page-map.assistant-visible.navigation-active {
        --map-mobile-overlay-top-offset: var(--map-mobile-navigation-header-height);
        --map-mobile-overlay-bottom-offset: calc(var(--map-mobile-navigation-offset) - var(--map-mobile-overlay-top-offset));

        &.navigation-active-split-view .map-container {
            height: calc(52dvh - 100px) !important;
            margin-top: 100px !important;
            min-height: auto;
        }
    }

    .change-floor-content {
        --elevator-img-size: 300px;
        --elevator-icon-size: 150px;
    }

    .cms-page-map.nav-mode,
    .cms-page-map.assistant-visible {
        --map-top-padding: 22px;
    }
}

.destination-toast {
    position: absolute;
    top: 40px;
    left: 50%;
    transform: translateX(-50%);

    display: flex;
    gap: 0.5rem;
    justify-content: center;
    align-items: baseline;

    padding: 0.5rem 1rem;
    border-radius: 100px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);

    .destination-to {
        font-size: 10pt;
        color: #555;
        white-space: nowrap;
    }

    .destination-name {
        font-size: 12pt;
        font-weight: 500;
        white-space: nowrap;
    }
}

.debug-info {
    position: fixed;
    bottom: 100px;
    left: 0;
    right: 0;
    color: rgb(0, 0, 0);
    overflow: auto;
    font-size: 10pt;
    pointer-events: none;
    padding: 10px;
    z-index: 1000;

    pre {
        margin: 0;
        text-align: left;
    }
}

.split-view-map {
    height: 55dvh;
}

.map-copyright {
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 1;
    background-color: rgba(white, 0.9);
    font-size: 10px;
    padding: 1px 4px 1px;
    border-top-left-radius: 4px;
}
</style>








